import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import toast from "react-hot-toast";
import { FaEdit, FaPlus, FaThumbtack, FaRegCalendarAlt } from "react-icons/fa";
import { format } from "date-fns";
import { CUENTAS_ER } from "../../constants/clasificacionContable";
import { esGerente } from "../../constants/permisos";
import { useAuthContext } from "../../context/auth";
import { useRRHHCalculations } from "../../pages/ER/hooks/useRRHHCalculations";
import { getActivos, actualizarActivo } from "../../utils/activos";
import { cargarDepsComision } from "../../utils/autoMaterializarComisiones";
import { brutoCapex } from "../../utils/brutoCapex";
import { calcularImpuestos } from "../../utils/calcularImpuestos";
import { getFacturasConAsociacionCapex } from "../../utils/capex/asociarPagosCapex";
import { getSaldosCapexPorTarget } from "../../utils/capex/getSaldosCapexPorTarget";
import { resolverSaldo, buildContextoLibro } from "../../utils/cobro/resolverSaldo";
import { comisionPorGeneracion } from "../../utils/comisionPorGeneracion";
import { getCompromisos, eliminarCompromiso } from "../../utils/compromisosPago";
import { construirConceptoCapex, construirConceptoLote, TIPO_COMPONENTE_LABEL } from "../../utils/conceptoCapex";
import { getConciliacionesRecurrentes } from "../../utils/conciliacionCostoRecurrente";
import { getFacturasEmitidas, getFacturasRecibidas } from "../../utils/conciliacionSII";
import { getCuotasPendientesProyeccion } from "../../utils/creditos";
import { posicionesCuotas } from "../../utils/cuotasUtil";
import { calcularLiquidacionChile } from "../../utils/empleados";
import {
  FECHA_CORTE,
  construirLedgerProveedor,
  getSaldosPorFacturaProveedor,
  TOLERANCIA_REDONDEO_LOCAL,
} from "../../utils/estadoCuenta";
import { getDolarValue, getDolarFallback, getUFValue, getUTMValue, loadUTM } from "../../utils/exchangeRateService";
import { getFacturasMoraNeteada, getMorasPendientes, getOperacionesFactoring } from "../../utils/factoring";
import { facturaSiiPorPagar as facturaSiiPorPagarUtil } from "../../utils/facturasPorPagar";
import { fechaPagoEstimada } from "../../utils/fechaPagoSueldo";
import {
  hoyDelPais,
  toLocalYMD,
  parseLocalYMD,
  proximoDiaHabil,
  tituloVencida,
  esFechaModificada,
  tituloOverride,
} from "../../utils/fechasFuturosPanel";
import { getEdificiosSaldoPendiente } from "../../utils/getEdificiosSaldoPendiente";
import { detectarEstadoPago } from "../../utils/iva";
import {
  filtrarComponentesCapexProyectables,
  filtrarLotesCapexProyectables,
  getCostosLotesFuturos,
  getLotesFobSaldado,
} from "../../utils/loteCostos";
import { getLotes } from "../../utils/lotesCompra";
import {
  getMovimientosBancarios,
  getCuentasBancarias,
  getFacturasYaVinculadas,
  isMovPendiente,
  esMovIngreso,
  esMovEgreso,
} from "../../utils/movimientosBancarios";
import { construirExclusionNcAnulaError } from "../../utils/ncAnulaError";
import {
  getPagosPorMovimientos,
  getHaberesPais,
  getFechasPagoPais,
  saldoLiquidoPorPeriodo,
  sumarPagadoComision,
  getPagosEmpleados,
  TOLERANCIA_SALDO_LIQUIDO,
} from "../../utils/pagosEmpleados";
import { matchEsperadoCumplido, patchReagendado, slicesSinSetter } from "../../utils/patchReagendado";
import { getPipelineCobro } from "../../utils/pipelineCobroSII";
import { getCuotasPagosFuturas, getCuotasCobrosFuturas } from "../../utils/planCobros";
import { getCuotasActivosFuturas, getActivosConPlan } from "../../utils/planPagosActivos";
import { getCuotasCompromisoFuturas, getCompromisosConPlan } from "../../utils/planPagosCompromiso";
import { getPlanPagosEdificios } from "../../utils/planPagosEdificios";
import { getPlazosCobroParaSII } from "../../utils/plazosCobroSII";
import { procesarVentaDivisas } from "../../utils/procesarVentaDivisas";
import { getNombreProveedor } from "../../utils/proveedores";
import { getProveedoresRecurrentesEntries } from "../../utils/proveedoresRecurrentes";
import { getSaldosIniciales } from "../../utils/saldosIniciales";
import {
  puedeGestionarSticky,
  getStickyFuturos,
  fijarEntrySticky,
  soltarEntrySticky,
  soltarTodasSticky,
} from "../../utils/stickyFuturos";
import { client } from "../../utils/supabase";
import { useExchangeRates } from "../../utils/useExchangeRates";
import AsignarCajaChicaModal from "../AsignarCajaChicaModal";
import AsociarPagosCapexModal from "../AsociarPagosCapexModal";
import CompromisoPagoModal from "../CompromisoPagoModal";
import { Header } from "../Header";
import KebabMenu from "../KebabMenu";
import PlanCuotasModal from "../PlanCuotasModal";
import PlanPagoEdificioModal from "../PlanPagoEdificioModal";
import VincularMovimientoModal from "../VincularMovimientoModal";
import CeldaFechaReagendable from "./CeldaFechaReagendable";
import ConfirmarCapexPagadoModal from "./ConfirmarCapexPagadoModal";
import EditarFechaCobroModal from "./EditarFechaCobroModal";
import EditarFechaRecurrenteModal from "./EditarFechaRecurrenteModal";
import EditarFechaSueldoModal from "./EditarFechaSueldoModal";
import ModificarFechaLoteCapexModal from "./ModificarFechaLoteCapexModal";
import ModificarFechaProyeccionCapexModal from "./ModificarFechaProyeccionCapexModal";
import ModificarFechaProyeccionCompromisoModal from "./ModificarFechaProyeccionCompromisoModal";
import OverrideF29Modal from "./OverrideF29Modal";
import PlanPagosActivoModal from "./PlanPagosActivoModal";
import styles from "./FlujoCajaV2.module.scss";

// Una fila proyectada está VENCIDA si su fecha (plazo/estimada/vencimiento/etc.) existe y ya pasó.
// `hoy` se pasa como parámetro porque las distintas ramas de proyección lo tienen en su propio
// scope (string YYYY-MM-DD); la comparación es la misma en todas (caso E5/B5 de la auditoría 10.1).
const esVencida = (fecha, hoy) => !!fecha && fecha < hoy;

const FILTROS_PROYECCION = [
  { key: "todo", label: "Todo" },
  { key: "cobros", label: "Por cobrar" },
  { key: "backlog", label: "Backlog" },
  { key: "proveedores_recurrentes", label: "Recurrentes" },
  { key: "proveedores_facturados", label: "Por pagar" },
  { key: "edificios", label: "Edificios" },
  { key: "rrhh", label: "RRHH" },
  { key: "impuestos", label: "Impuestos" },
  { key: "capex", label: "CapEx" },
  { key: "financiamiento", label: "Créditos" },
];

const FILTROS_HISTORICO = [
  { key: "todo", label: "Todo" },
  { key: "ingresos", label: "Ingresos" },
  { key: "egresos", label: "Egresos" },
];

// Cinco slots de corte: "Semana" (viernes de cierre), "Mes" (fin del mes actual),
// "+1"/"+2" (fin del mes siguiente / de dos meses después, con label dinámico "Hasta el DD/MM")
// y "Año" (31/12 del año en curso). Todas las fechas son dinámicas; los labels de +1/+2 también
// se derivan de la fecha real del sistema (10.110), nunca hardcodeados.
const calcularFiltrosTemporales = () => {
  const hoy = new Date();
  const yyyy = hoy.getFullYear();
  const m0 = hoy.getMonth();
  const finDeMes = (y, m) => new Date(y, m + 1, 0);
  // Label "Hasta el DD/MM" desde un Date (mismo formato DD/MM que el resto del panel, ver formatFecha).
  const labelHasta = (fecha) => {
    const d = String(fecha.getDate()).padStart(2, "0");
    const m = String(fecha.getMonth() + 1).padStart(2, "0");
    return `Hasta el ${d}/${m}`;
  };
  const finDeMesMas1 = finDeMes(yyyy, m0 + 1);
  const finDeMesMas2 = finDeMes(yyyy, m0 + 2);
  // "Semana" (label del botón; concepto = semana que cierra el viernes). La semana va de sábado
  // a viernes; el viernes es el cierre. Corte = próximo viernes de cierre (incluido el propio
  // viernes → offset 0). offset en días desde hoy: (5 - dow + 7) % 7, dow = getDay() (dom=0…sáb=6).
  // Se deriva del MISMO `hoy` que los demás slots (no un new Date() aparte) para no desfasar en
  // el borde de medianoche. Techo-only como el resto: sin piso, los vencidos viejos siguen dentro.
  const dow = hoy.getDay();
  const offsetViernes = (5 - dow + 7) % 7;
  const proximoViernes = new Date(yyyy, m0, hoy.getDate() + offsetViernes);
  const toKey = (fecha) => {
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, "0");
    const d = String(fecha.getDate()).padStart(2, "0");
    return `corte_${y}-${m}-${d}`;
  };
  const slots = [
    { label: "Semana", fechaCorte: proximoViernes },
    { label: "Mes", fechaCorte: finDeMes(yyyy, m0) },
    { label: labelHasta(finDeMesMas1), fechaCorte: finDeMesMas1 },
    { label: labelHasta(finDeMesMas2), fechaCorte: finDeMesMas2 },
    { label: "Año", fechaCorte: new Date(yyyy, 11, 31) },
  ];
  return slots.map((s) => ({ ...s, key: toKey(s.fechaCorte) }));
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  let f = fechaStr;
  if (f.includes("T")) f = f.substring(0, 10);
  const [, month, day] = f.split("-");
  return `${day}/${month}`;
};

// ─── Badge de fecha del panel de Futuros (Fase 2, solo visual) ───────────────────────────────
// Tres estados, donde el COLOR codifica el estado (no la dirección — esa la da el color del monto):
// vencido = rojo clarito, override futuro = azul clarito, normal = sin pill. Precedencia estricta
// vencido > override > normal (un override ya vencido va rojo, no azul).
const PILL_FECHA_BASE = { borderRadius: "4px", padding: "2px 6px" };
const PILL_FECHA_VENCIDO = {
  ...PILL_FECHA_BASE,
  background: "#fdecea",
  border: "1px solid #e74c3c",
  color: "#c0392b",
};
const PILL_FECHA_OVERRIDE = {
  ...PILL_FECHA_BASE,
  background: "#eaf2fd",
  border: "1px solid #5b9bd5",
  color: "#2f6db3",
};

// ─── Badge de estado del movimiento bancario ────────────────────────────
// Sistema consistente: cada movimiento muestra un badge según su tipo de vinculación.
// P=pendiente, E=empleado, V=venta (SII emitida), C=compra SII (recibida — incluye CapEx ya vinculado),
// X=compra extranjera (factura externa, rombo), A=CapEx sin factura (compra_activo huérfano), I=ignorado.
const BADGE_BASE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.65rem",
  fontWeight: 700,
  width: 22,
  height: 22,
  borderRadius: 4,
  verticalAlign: "middle",
};

// Tooltip estructurado: tipo como encabezado, detalles abajo
// Formato de monto compacto para tooltips (renderBadgeMovimiento es módulo, sin acceso al hook useCurrency).
const fmtMontoTt = (n, moneda) => {
  const pref = moneda === "USD" ? "US$ " : moneda === "PEN" ? "S/ " : "$";
  return pref + Number(n || 0).toLocaleString("es-CL", { maximumFractionDigits: moneda === "USD" ? 2 : 0 });
};

// Meses para el tooltip del badge "E" (período de cada concepto de pago a empleado).
const MESES_TT = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

const buildTooltip = ({ tipo, folioLabel, folio, entidad, minWidth = 140, maxWidth = 260 }) => (
  <div style={{ textAlign: "left", padding: "2px 4px", minWidth, maxWidth }}>
    <div
      style={{
        fontSize: "0.65rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        opacity: 0.75,
        marginBottom: 3,
      }}
    >
      {tipo}
    </div>
    {folio && (
      <div
        style={{ fontSize: "0.78rem", fontWeight: 600, fontFamily: "var(--font-mono)", marginBottom: entidad ? 2 : 0 }}
      >
        {folioLabel} {folio}
      </div>
    )}
    {entidad && <div style={{ fontSize: "0.72rem", opacity: 0.9, lineHeight: 1.25 }}>{entidad}</div>}
  </div>
);

const BadgeConTooltip = ({ tooltip, tooltipId, tooltipClassName, children }) => (
  <OverlayTrigger
    placement="top"
    delay={{ show: 100, hide: 100 }}
    overlay={
      <Tooltip id={tooltipId} className={tooltipClassName}>
        {tooltip}
      </Tooltip>
    }
  >
    {children}
  </OverlayTrigger>
);

const renderBadgeMovimiento = (
  m,
  pagosMovIds,
  enteFiscal = "SII",
  factoringMovIds = new Set(),
  factoringMap = new Map(),
  facturaInfoMap = new Map(),
  facturaRecibidaInfoMap = new Map(),
  boletaHonorarioInfoMap = new Map(),
  capexResumenMap = new Map(),
  pagosEmpleadosPorMov = new Map(),
  recurrenteResumenMap = new Map(),
  movsCobroSIILibro = new Set(),
  movsPagoRecibidaLibro = new Set()
) => {
  // Vinculado a operación de factoring (adelanto o excedente) → F violeta. Prioritario
  // sobre cualquier otro check para que se vea aunque también caiga en algún match secundario.
  if (factoringMovIds.has(m.id)) {
    const entries = factoringMap.get(m.id) || [];
    const provNombre = entries[0]?.op?.proveedor?.nombre || "Factoring";
    const tiposUnicos = [...new Set(entries.map((e) => e.tipoVinc))];
    const tipoLabel = tiposUnicos.length === 1 ? tiposUnicos[0] : "Múltiple";
    const fmtOpts = { style: "currency", currency: m.moneda || "CLP", maximumFractionDigits: 0 };
    const fmtMonto = (n) => Number(n || 0).toLocaleString("es-CL", fmtOpts);

    // 1 op → tooltip clásico vía buildTooltip. N ops → JSX inline con lista por folio.
    let tooltipContent;
    if (entries.length === 1) {
      const e = entries[0];
      const montoVinc = e.tipoVinc === "Giro" ? Number(e.op.giro_a_recibir || 0) : Number(e.op.monto_remanente || 0);
      tooltipContent = buildTooltip({
        tipo: `Factoring · ${e.tipoVinc}`,
        entidad: provNombre,
        folio: montoVinc > 0 ? fmtMonto(montoVinc) : null,
        folioLabel: "Monto",
      });
    } else {
      tooltipContent = (
        <div style={{ textAlign: "left", padding: "2px 4px", minWidth: 180, maxWidth: 280 }}>
          <div
            style={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              opacity: 0.75,
              marginBottom: 3,
            }}
          >
            {`Factoring · ${tipoLabel} (${entries.length} ops)`}
          </div>
          <div style={{ fontSize: "0.72rem", opacity: 0.9, lineHeight: 1.25, marginBottom: 4 }}>{provNombre}</div>
          {entries.map((e, i) => {
            const monto = e.tipoVinc === "Giro" ? Number(e.op.giro_a_recibir || 0) : Number(e.op.monto_remanente || 0);
            return (
              <div key={i} style={{ fontSize: "0.72rem", fontFamily: "var(--font-mono)", lineHeight: 1.3 }}>
                Folio {e.op.factura?.folio || "—"}: {fmtMonto(monto)}
              </div>
            );
          })}
        </div>
      );
    }

    return (
      <BadgeConTooltip tooltip={tooltipContent} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#922b21" }}>F</span>
      </BadgeConTooltip>
    );
  }
  // Pago a empleado → E (chequeo externo, depende del state pagosEmpleadosRegistrados)
  if (pagosMovIds.has(m.id)) {
    // Desglose real del pago (filas de pagos_empleados que comparten movimiento_bancario_id): nombre
    // del empleado + una línea por concepto (tipo · período · monto) + total. Normalmente un solo
    // empleado, pero NO se asume (10.7): se agrupa por empleado_id y se lista cada grupo.
    const filas = pagosEmpleadosPorMov.get(m.id) || [];
    const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
    const periodoLabel = (f) =>
      f.periodo_mes && f.periodo_año ? `${MESES_TT[f.periodo_mes - 1]} ${f.periodo_año}` : "";
    const porEmpleado = new Map();
    for (const f of filas) {
      if (!porEmpleado.has(f.empleado_id)) {
        const nombre = `${f.empleados?.nombre || ""} ${f.empleados?.apellido || ""}`.trim() || "Empleado";
        porEmpleado.set(f.empleado_id, { nombre, filas: [] });
      }
      porEmpleado.get(f.empleado_id).filas.push(f);
    }
    const total = filas.reduce((s, f) => s + Number(f.monto || 0), 0);
    const grupos = [...porEmpleado.values()];
    const rowStyle = {
      fontSize: "0.72rem",
      fontFamily: "var(--font-mono)",
      lineHeight: 1.3,
      display: "flex",
      justifyContent: "space-between",
      gap: 10,
    };
    // Sin filas cargadas (state aún sin poblar / desvinculación en curso) → tooltip genérico de fallback.
    const tooltipContent =
      filas.length === 0 ? (
        buildTooltip({ tipo: "Pago a empleado" })
      ) : (
        <div style={{ textAlign: "left", padding: "2px 4px", minWidth: 180, maxWidth: 300 }}>
          <div
            style={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              opacity: 0.75,
              marginBottom: 3,
            }}
          >
            Pago a empleado
          </div>
          {grupos.map((grp, gi) => (
            <div key={gi} style={{ marginBottom: gi < grupos.length - 1 ? 5 : 0 }}>
              <div style={{ fontSize: "0.75rem", fontWeight: 600, marginBottom: 2 }}>{grp.nombre}</div>
              {grp.filas.map((f, i) => (
                <div key={i} style={rowStyle}>
                  <span>{[cap(f.tipo), periodoLabel(f)].filter(Boolean).join(" ")}</span>
                  <span>{fmtMontoTt(f.monto, m.moneda)}</span>
                </div>
              ))}
            </div>
          ))}
          <div
            style={{
              ...rowStyle,
              fontWeight: 700,
              borderTop: "1px solid rgba(255,255,255,0.25)",
              marginTop: 4,
              paddingTop: 3,
            }}
          >
            <span>Total</span>
            <span>{fmtMontoTt(total, m.moneda)}</span>
          </div>
        </div>
      );
    return (
      <BadgeConTooltip tooltip={tooltipContent} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#27ae60" }}>E</span>
      </BadgeConTooltip>
    );
  }
  // Ignorado → I gris (estado, prioridad sobre cualquier categoría)
  if (m.estado === "ignorado") {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Ignorado" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#707b7c" }}>I</span>
      </BadgeConTooltip>
    );
  }
  // Costo recurrente conciliado → R marrón. La conciliación de una ocurrencia de costo recurrente
  // (conciliarOcurrenciasRecurrentes) escribe la caja como `pago_proveedor` SIN fila en
  // movimiento_bancario_facturas (no hay factura), así que sin esta rama caería en C mintiendo
  // "Compra SII" con tooltip vacío. El predicado sale del HILO ESTABLE conciliacion_costo_recurrente
  // por movimiento_bancario_id (recurrenteResumenMap, NUNCA de categoria_pago — mismo criterio que
  // esCapexACuenta, 10.240). Se evalúa después del guard de ignorado y antes de V/X/H/C.
  const lineasRecurrente = recurrenteResumenMap.get(m.id) || [];
  if (lineasRecurrente.length > 0) {
    const provNombreR = lineasRecurrente.find((l) => l.proveedor_nombre)?.proveedor_nombre || "Proveedor";
    const sumaLineasR = lineasRecurrente.reduce((s, l) => s + (Number(l.monto) || 0), 0);
    const sinAtribuirR = Math.abs(Number(m.monto || 0)) - sumaLineasR;
    const rowStyleR = { fontSize: "0.72rem", fontFamily: "var(--font-mono)", lineHeight: 1.3 };
    const tooltipR = (
      <div style={{ textAlign: "left", padding: "2px 4px", minWidth: 180, maxWidth: 300 }}>
        <div
          style={{
            fontSize: "0.65rem",
            fontWeight: 700,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            opacity: 0.75,
            marginBottom: 3,
          }}
        >
          Costo recurrente
        </div>
        <div style={{ fontSize: "0.75rem", fontWeight: 600, marginBottom: 2 }}>{provNombreR}</div>
        {lineasRecurrente.map((l, i) => {
          // Nunca muda (10.177): se muestra con los datos que haya; el monto solo si está presente.
          const periodo =
            l.periodo_mes && l.periodo_año ? `${String(l.periodo_mes).padStart(2, "0")}/${l.periodo_año}` : "";
          const partes = [l.concepto, periodo].filter(Boolean);
          if (l.monto != null) partes.push(fmtMontoTt(l.monto, m.moneda));
          return (
            <div key={i} style={rowStyleR}>
              {partes.join(" · ")}
            </div>
          );
        })}
        {sinAtribuirR > 1 && (
          <div style={{ ...rowStyleR, opacity: 0.9, marginTop: 3 }}>
            Sin atribuir: {fmtMontoTt(sinAtribuirR, m.moneda)}
          </div>
        )}
      </div>
    );
    return (
      <BadgeConTooltip tooltip={tooltipR} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#6d4c41" }}>R</span>
      </BadgeConTooltip>
    );
  }
  // FRONTERA Fase 3.1 (10.5 / 10.190): los badges V (cobro SII) y C (compra SII) describen el ESTADO
  // de una factura ("este mov cobró/pagó tal factura") → se deciden desde el LIBRO por origen
  // (movsCobroSIILibro / movsPagoRecibidaLibro), NO desde categoria_pago. TODOS los demás badges
  // (A, H, X, P, D, G, PA, CA, CC, B, impuestos, capex) describen el TIPO del movimiento de CAJA y
  // siguen leyendo categoria_pago — eso es caja pura (10.5), legítimo, no es estado de factura.
  // Esto cubre tanto single (FK directa poblada) como múltiple (FK directa NULL pero
  // movimiento_bancario_facturas tiene N filas) — el badge depende del tipo contable, no del
  // shape de la vinculación.
  // movimientos_caja.movimiento_bancario_id tiene UNIQUE → PostgREST devuelve el embed como objeto.
  const categoriaCaja = m.movimiento_caja?.categoria_pago;
  // Un pago CapEx está VINCULADO a su factura cuando su movimiento bancario tiene una fila MBF a una
  // recibida (lo que crea la asociación CapEx, Fase 2). La caja puede haber nacido 'compra_activo'
  // (fallback) o 'pago_a_cuenta' (proveedor único, regla 10.215) — AMBAS cuentan: una vez vinculada a
  // una recibida es, contablemente, una compra SII pagada (badge C con folio + emisor), no un anticipo
  // sin atribuir (PA). Sin incluir 'pago_a_cuenta', el pago que nace así (caso Barbato $208.250) caía a
  // la rama PA y mostraba "Pago a cuenta · {proveedor}" sin folio, como si siguiera sin resolver. Se lee
  // del embed ya cargado en bulk `facturas_vinculadas` (mismo dato que el badge C), por ID (10.1/10.7).
  const capexPagoVinculado =
    (categoriaCaja === "compra_activo" || categoriaCaja === "pago_a_cuenta") &&
    (m.facturas_vinculadas || []).some((v) => v.factura_tipo === "sii_recibida");
  // Pago de CapEx "a cuenta" (badge A). Se detecta por el HILO ESTABLE del vínculo —
  // plan_pagos_activos / lote_costos → movimiento_bancario_id, materializado en capexResumenMap— y NO
  // por categoria_pago: desde la regla 10.215 (commit 53cf775d) la caja de un CapEx saldado nace como
  // 'pago_a_cuenta' (cuando el activo resuelve a un proveedor único) o 'compra_activo' (fallback), y
  // antes SIEMPRE era 'compra_activo'. Si dependiéramos de categoria_pago, las filas nuevas caerían en
  // la rama PA ("Pago a cuenta") y perderían el badge/tooltip de CapEx. Se excluye el caso ya vinculado
  // a una factura recibida (ese es el badge C). Este flag se evalúa ANTES de la rama PA (ver guard).
  const tieneFacturaRecibida = (m.facturas_vinculadas || []).some((v) => v.factura_tipo === "sii_recibida");
  const esCapexACuenta =
    !tieneFacturaRecibida && (categoriaCaja === "compra_activo" || (capexResumenMap.get(m.id) || []).length > 0);

  // Cobro SII (single o múltiple) → V. DECISIÓN desde el libro por origen (Fase 3.1): el mov cobró
  // ≥1 emitida (aplicaciones_saldo origen_tipo='movimiento_bancario', documento_tipo='sii_emitida').
  // Ya NO se decide por categoria_pago='cobro_factura' — esto suma los movs con MBF sin caja (badge
  // que antes faltaba) y saca la dependencia de la fila de caja. El tooltip (folios/razón) se sigue
  // resolviendo del puente `facturas_vinculadas` vía facturaInfoMap (detalle, no decisión).
  if (movsCobroSIILibro.has(m.id)) {
    // B3 Fase B paso 2: la info (tipo_dte, folio, razon_social_receptor) se resuelve SIEMPRE por el
    // puente `facturas_vinculadas` vía facturaInfoMap (construido en cliente desde el puente, ya que
    // movimiento_bancario_facturas no tiene FK formal a facturas_sii_emitidas). Antes el caso single
    // salía del embed FK directa `factura_emitida` (en retiro) — ahora single y multi son la misma vía.
    const vincsEmitidas = (m.facturas_vinculadas || [])
      .filter((v) => v.factura_tipo === "sii_emitida")
      .map((v) => facturaInfoMap.get(v.factura_id))
      .filter(Boolean);
    const esCobroExportacion = vincsEmitidas.some((info) => info.tipo_dte === 110);
    const foliosMulti = vincsEmitidas
      .map((i) => i.folio)
      .filter(Boolean)
      .sort((a, b) => a - b); // orden ascendente determinístico ("35, 36"), no orden de inserción MBF
    const tooltipFolio = foliosMulti.length > 0 ? foliosMulti.join(", ") : null;
    const tooltipFolioLabel = tooltipFolio ? (foliosMulti.length > 1 ? "Folios" : "Folio") : null;
    const tooltipEntidad = vincsEmitidas[0]?.razon_social_receptor;
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: esCobroExportacion ? "Cobro Exportación" : `Cobro ${enteFiscal}`,
          folioLabel: tooltipFolioLabel,
          folio: tooltipFolio,
          entidad: tooltipEntidad,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#2980b9" }}>V</span>
      </BadgeConTooltip>
    );
  }
  // Compra extranjera (factura externa) → X. Cae antes que C porque comparte
  // categoria_pago='pago_proveedor' pero se distingue por match_factura_tipo.
  if (m.match_factura_tipo === "factura_externa") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: "Compra extranjera",
          folioLabel: "Doc",
          folio: m.factura_externa?.[0]?.numero_documento,
          entidad: m.match_entidad_nombre,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#117a8b" }}>X</span>
      </BadgeConTooltip>
    );
  }
  // Pago de boleta de honorarios (BHE) → H. Comparte categoria_pago='pago_proveedor' con la compra
  // SII (C), pero se distingue por el factura_tipo del vínculo MBF: una BHE no es una compra SII.
  // Cae ANTES que C para que el honorario no se etiquete como "Compra SII".
  const vincsHonorario = (m.facturas_vinculadas || []).filter((v) => v.factura_tipo === "boleta_honorario");
  if (vincsHonorario.length > 0) {
    const infoH = boletaHonorarioInfoMap.get(vincsHonorario[0].factura_id);
    // Fallo NO silencioso (10.177): si falta la info de la boleta, el emisor cae al nombre del asiento
    // de caja (entidad_nombre) y, en última instancia, a un texto explícito — nunca queda mudo.
    const tooltipH = buildTooltip({
      tipo: "Honorario",
      folioLabel: infoH?.folio ? "Boleta N°" : null,
      folio: infoH?.folio || null,
      entidad: infoH?.emisor_nombre || m.movimiento_caja?.entidad_nombre || "(sin dato de la boleta)",
    });
    return (
      <BadgeConTooltip tooltip={tooltipH} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#ad1457" }}>H</span>
      </BadgeConTooltip>
    );
  }
  // Compra SII (single o múltiple) → C. DECISIÓN desde el libro por origen (Fase 3.1): el mov pagó
  // ≥1 recibida (aplicaciones_saldo origen_tipo='movimiento_bancario', documento_tipo='sii_recibida').
  // Ya NO se decide por categoria_pago='pago_proveedor' + MBF. Se conserva `|| capexPagoVinculado`
  // (pago CapEx ya vinculado a su recibida: contablemente compra SII pagada, mismo badge) — su RPC
  // también escribe el libro (10.239), así que en general ya cae en el Set, pero el flag preserva el
  // tooltip/caso CapEx explícitamente. X (externa) y H (boleta) caen ANTES: su evento de libro es de
  // otro documento_tipo, no entra a este Set. El tooltip (folios/razón) sale del puente (detalle).
  // EXCEPCIÓN de frontera (10.5): un pago de COMISIÓN DE EDIFICIO (`comision_edificio`) escribe una MBF
  // de soporte a la recibida del edificio + su evento de libro, pero su semántica primaria es el badge B
  // (edificio), que se chequea más abajo. Sin este guard, C (arriba de B) le robaría el badge (11 movs).
  // El edificio queda por categoria_pago (frontera declarada); C no pisa a los badges de caja pura.
  if ((movsPagoRecibidaLibro.has(m.id) && categoriaCaja !== "comision_edificio") || capexPagoVinculado) {
    // B3 Fase B paso 2: folio + emisor SIEMPRE desde el puente `facturas_vinculadas` vía
    // facturaRecibidaInfoMap (antes el single salía del embed FK directa `factura_recibida`, en
    // retiro). Con 1 factura se conserva el tooltip clásico de una línea; con N, la lista multi.
    const vincsRecibidas = (m.facturas_vinculadas || [])
      .filter((v) => v.factura_tipo === "sii_recibida")
      .map((v) => facturaRecibidaInfoMap.get(v.factura_id))
      .filter(Boolean)
      .sort((a, b) => Number(a.folio) - Number(b.folio));
    let tooltipC;
    if (vincsRecibidas.length <= 1) {
      // Single (o sin info): tooltip clásico de una línea, folio + emisor del puente.
      const uno = vincsRecibidas[0];
      tooltipC = buildTooltip({
        tipo: `Compra ${enteFiscal}`,
        folioLabel: "Folio",
        folio: uno?.folio,
        entidad: uno?.razon_social_emisor,
      });
    } else {
      // Multi: lista UNA factura por línea (folio + razón social) + la propina si el débito supera
      // la Σ de facturas (excedente sin DTE, 10.108). Orden asc determinístico de folio.
      const sumFacturas = vincsRecibidas.reduce((s, i) => s + Number(i.monto_total || 0), 0);
      const propinaC = Math.abs(Number(m.monto || 0)) - sumFacturas;
      const tienePropina = vincsRecibidas.length > 0 && propinaC > 0.5;
      tooltipC = (
        <div style={{ textAlign: "left", padding: "2px 4px", minWidth: 160, maxWidth: 300 }}>
          <div
            style={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              opacity: 0.75,
              marginBottom: 3,
            }}
          >
            {`Compra ${enteFiscal}${vincsRecibidas.length > 1 ? ` (${vincsRecibidas.length} facturas)` : ""}`}
          </div>
          {vincsRecibidas.map((i, idx) => (
            <div key={idx} style={{ fontSize: "0.72rem", lineHeight: 1.3, marginBottom: 1 }}>
              <span style={{ fontFamily: "var(--font-mono)", fontWeight: 600 }}>Folio {i.folio}</span>
              {i.razon_social_emisor ? ` · ${i.razon_social_emisor}` : ""}
            </div>
          ))}
          {tienePropina && (
            <div style={{ fontSize: "0.72rem", lineHeight: 1.3, opacity: 0.9, marginTop: 3 }}>
              Propina (sin DTE): {fmtMontoTt(propinaC, m.moneda)}
            </div>
          )}
        </div>
      );
    }
    return (
      <BadgeConTooltip tooltip={tooltipC} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#7d3c98" }}>C</span>
      </BadgeConTooltip>
    );
  }
  // Pago a proveedor SIN vínculo a sii_recibida ni conciliación recurrente → PP gris pizarra. Es el
  // pago_proveedor que la rama C ya no atrapa (sin factura recibida) y que R ya descartó (sin
  // conciliación): mayormente proveedores extranjeros y honorarios sin vínculo MBF. Tooltip "Pago a
  // proveedor" con la entidad y, si existe y no es el texto crudo del banco, el concepto de la caja
  // (mismo criterio que la rama G, para no mostrar el descripcion_raw como si fuera un concepto real).
  if (categoriaCaja === "pago_proveedor") {
    const mcPP = m.movimiento_caja;
    const entidadPP = mcPP?.entidad_nombre || m.match_entidad_nombre || "";
    const descCajaPP = (mcPP?.descripcion || "").trim();
    const conceptoPP = descCajaPP && descCajaPP !== (m.descripcion_raw || "").trim() ? descCajaPP : "";
    const detallePP = entidadPP && conceptoPP ? `${entidadPP} // ${conceptoPP}` : entidadPP || conceptoPP;
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({ tipo: "Pago a proveedor", entidad: detallePP })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#566573" }}>PP</span>
      </BadgeConTooltip>
    );
  }
  // Pago institucional → I azul oscuro. PREVIRED, SII (declaración), banco, etc.
  if (
    categoriaCaja === "cotizaciones_previsionales" ||
    categoriaCaja === "pago_impuesto" ||
    categoriaCaja === "pago_banco"
  ) {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Pago institucional" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#1a3a5c" }}>P</span>
      </BadgeConTooltip>
    );
  }
  // Cambio de moneda (Venta/Compra Divisas) → D azul. categoria_pago='costo_financiero'.
  if (categoriaCaja === "costo_financiero") {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Cambio de Moneda" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#1a5276" }}>D</span>
      </BadgeConTooltip>
    );
  }
  // Gasto sin factura → G. Detalle = "{cuenta ER} // {concepto}". El concepto tipeado a mano vive en
  // `descripcion`. En los gastos viejos (pre-fix de persistencia) `descripcion` quedó con el texto crudo
  // del banco (== descripcion_raw): eso NO es un concepto real, así que no se muestra (no inventamos).
  if (categoriaCaja === "gasto_sin_factura") {
    const mc = m.movimiento_caja;
    const cuentaVal = mc?.cuenta_er;
    const cuentaLabel = cuentaVal ? CUENTAS_ER.find((c) => c.value === cuentaVal)?.label || cuentaVal : "";
    const descCaja = (mc?.descripcion || "").trim();
    const concepto = descCaja && descCaja !== (m.descripcion_raw || "").trim() ? descCaja : "";
    const detalle = cuentaLabel && concepto ? `${cuentaLabel} // ${concepto}` : cuentaLabel || concepto;
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Gasto sin factura", entidad: detalle })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#1e8449" }}>G</span>
      </BadgeConTooltip>
    );
  }
  // Pago a cuenta proveedor → PA. Movimiento de cuenta corriente sin factura asociada
  // (anticipos, retiros, devoluciones). entidad_tipo='proveedor', cuenta_er=null.
  // Incluye los buckets nuevos pago_factura/pago_credito del modal (mismo badge visual).
  // `!esCapexACuenta`: un pago de CapEx saldado ahora nace como 'pago_a_cuenta' (10.215) pero debe
  // mostrar el badge A (CapEx), no PA — se detecta por el vínculo estable, no por categoria_pago.
  if (
    !esCapexACuenta &&
    (categoriaCaja === "pago_a_cuenta" || categoriaCaja === "pago_factura" || categoriaCaja === "pago_credito")
  ) {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: "Pago a cuenta",
          entidad: m.movimiento_caja?.entidad_nombre || m.match_entidad_nombre,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#d35400" }}>PA</span>
      </BadgeConTooltip>
    );
  }
  // Cobro a cuenta proveedor → CA. Ingreso a cuenta corriente del proveedor (devolución,
  // anticipo retornado, refacturación). entidad_tipo='proveedor', cuenta_er=null.
  // Incluye el bucket nuevo cobro_credito del modal (mismo badge visual).
  if (categoriaCaja === "cobro_a_cuenta" || categoriaCaja === "cobro_credito") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: "Cobro a cuenta",
          entidad: m.movimiento_caja?.entidad_nombre || m.match_entidad_nombre,
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#d35400" }}>CA</span>
      </BadgeConTooltip>
    );
  }
  // Caja chica → CC ámbar/oro. Entrega de fondos a un empleado (categoria_pago='caja_chica').
  if (categoriaCaja === "caja_chica") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({ tipo: "Caja Chica", entidad: m.movimiento_caja?.entidad_nombre })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#b7950b" }}>CC</span>
      </BadgeConTooltip>
    );
  }
  // Pago a edificio → B turquesa. categoria_pago='comision_edificio'. entidad_tipo='edificio'.
  if (categoriaCaja === "comision_edificio") {
    return (
      <BadgeConTooltip
        tooltip={buildTooltip({
          tipo: "Pago a edificio",
          entidad: m.movimiento_caja?.entidad_nombre || "",
        })}
        tooltipId={`tt-${m.id}`}
      >
        <span style={{ ...BADGE_BASE, background: "#1abc9c" }}>B</span>
      </BadgeConTooltip>
    );
  }
  // Compra de activo (CapEx) "a cuenta" → A índigo. Es un pago de CapEx (modal → tab CapEx → "Saldar
  // proyecciones") que amortiza cuotas de activo / componentes de lote SIN factura todavía: hay
  // vinculación (a la proyección), solo que aún no capitaliza contra un DTE. NUNCA "Gasto sin factura"
  // (era el falso fallback G). El caso vinculado a factura ya salió arriba como C. La condición es
  // `esCapexACuenta` (hilo estable plan_pagos_activos/lote_costos → capexResumenMap, NO categoria_pago:
  // cubre tanto 'pago_a_cuenta' de 10.215 como el fallback 'compra_activo'). El detalle (proveedor ·
  // edificio/lote · Cuota N/M) también sale de capexResumenMap (lookup inverso por movimiento_bancario_id).
  if (esCapexACuenta) {
    const lineasCapex = capexResumenMap.get(m.id) || [];
    let tooltipA;
    if (lineasCapex.length <= 1) {
      // 0 líneas → fallback al header renombrado + entidad de la caja si la hubiera (huérfano/legacy).
      // 1 línea → tooltip clásico con el detalle en la línea de entidad.
      const detalleCapex = lineasCapex[0] || m.movimiento_caja?.entidad_nombre || null;
      // maxWidth ensanchado (el concepto CapEx es largo: "Edificio | Tipo | Proveedor · Cuota N/M");
      // el tope real del globo lo sube la clase fcv2CapexTt (--bs-tooltip-max-width) — ver scss.
      tooltipA = buildTooltip({ tipo: "CapEx a cuenta", entidad: detalleCapex, minWidth: 200, maxWidth: 380 });
    } else {
      // N líneas: el pago saldó varias cuotas/componentes (el modal permite tildar varios) → una por línea.
      tooltipA = (
        <div style={{ textAlign: "left", padding: "2px 4px", minWidth: 200, maxWidth: 380 }}>
          <div
            style={{
              fontSize: "0.65rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              textTransform: "uppercase",
              opacity: 0.75,
              marginBottom: 3,
            }}
          >
            {`CapEx a cuenta (${lineasCapex.length} ítems)`}
          </div>
          {lineasCapex.map((linea, idx) => (
            <div key={idx} style={{ fontSize: "0.72rem", lineHeight: 1.3, marginBottom: 1, opacity: 0.9 }}>
              {linea}
            </div>
          ))}
        </div>
      );
    }
    return (
      <BadgeConTooltip tooltip={tooltipA} tooltipId={`tt-${m.id}`} tooltipClassName="fcv2CapexTt">
        <span style={{ ...BADGE_BASE, background: "#3949ab" }}>A</span>
      </BadgeConTooltip>
    );
  }
  // Fallback: tiene mov_caja_id pero categoria_pago no matcheó (raro). Default G.
  if (m.movimiento_caja_id) {
    return (
      <BadgeConTooltip tooltip={buildTooltip({ tipo: "Gasto sin factura" })} tooltipId={`tt-${m.id}`}>
        <span style={{ ...BADGE_BASE, background: "#1e8449" }}>G</span>
      </BadgeConTooltip>
    );
  }
  // Pendiente → guion (la fila se tinta naranja sutil vía isMovPendiente)
  return (
    <BadgeConTooltip tooltip={buildTooltip({ tipo: "Pendiente de vincular" })} tooltipId={`tt-${m.id}`}>
      <span
        style={{ color: "#999", fontWeight: 400, display: "inline-block", width: 22, height: 22, textAlign: "center" }}
      >
        —
      </span>
    </BadgeConTooltip>
  );
};

// Marca "forzado/pendiente" (10.108 invertida): si el movimiento tiene una vinculación forzada por
// faltante con estado='pendiente' (tabla vinculaciones_forzadas), devuelve un ⚠ naranja con su
// tooltip para anteponer AL CONCEPTO en el histórico. estado='resuelto' (llegó la NC real) → null.
// Va en la celda de descripción, NO en la del badge: así el badge (la letra) queda en su posición y
// tamaño de siempre, esté o no la marca (la columna del badge se ve idéntica a las filas sin forzar).
// Presentación de texto forzada ("⚠︎") para que el color naranja del CSS se respete (con la
// presentación emoji por defecto el glyph ignoraba el color y quedaba invisible).
const renderMarcaForzado = (m, forzadosMap = new Map()) => {
  const forzado = forzadosMap.get(m.id);
  if (!forzado || forzado.estado !== "pendiente") return null;
  const tooltipForzado = (
    <div style={{ textAlign: "left", padding: "2px 4px", minWidth: 160, maxWidth: 300 }}>
      <div
        style={{
          fontSize: "0.65rem",
          fontWeight: 700,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          opacity: 0.75,
          marginBottom: 3,
        }}
      >
        Vinculación forzada
      </div>
      <div style={{ fontSize: "0.72rem", lineHeight: 1.3, opacity: 0.9 }}>
        Faltante forzado (pendiente): {fmtMontoTt(forzado.faltante, m.moneda)}
      </div>
    </div>
  );
  // Posición ABSOLUTA (fuera de flujo) → no aporta altura: la fila mide exactamente igual con o sin
  // marca, por grande que sea el glyph (además el td tiene overflow:hidden). Anclada al extremo
  // derecho de la celda de descripción (la celda es position:relative), centrada verticalmente, con
  // aire respecto a la columna de MONTO (right:10). Presentación de texto (⚠︎ U+FE0E) + naranja + bold.
  return (
    <BadgeConTooltip tooltip={tooltipForzado} tooltipId={`tt-forz-${m.id}`}>
      <span
        style={{
          position: "absolute",
          right: 10,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#e67e22",
          fontWeight: 700,
          fontSize: 17,
          lineHeight: 1,
          cursor: "default",
        }}
      >
        {"⚠︎"}
      </span>
    </BadgeConTooltip>
  );
};

// Detalle del CapEx "a cuenta" impreso como 2ª línea en la celda "Movimiento" del histórico, con el
// MISMO string que muestra el tooltip del badge A (capexResumenMap: "proveedor | tipo | edificio/lote
// · Cuota N/M"). Se renderiza SOLO cuando el panel histórico está maximizado (panelMaximizado ===
// "historico"): ahí hay ancho de sobra y no molesta; en la vista dividida 50/50 el detalle sigue
// viviendo solo en el tooltip (no hay espacio). Fuera de ese modo, o si el movimiento no es CapEx / no
// tiene líneas, devuelve null → cero cambio en el layout de la vista dividida.
const renderCapexDetalleInline = (m, capexResumenMap = new Map(), panelMaximizado = null) => {
  if (panelMaximizado !== "historico") return null;
  // Gate por el HILO ESTABLE del vínculo (capexResumenMap, de plan_pagos_activos/lote_costos), NO por
  // categoria_pago: desde 10.215 un CapEx saldado puede venir como 'pago_a_cuenta', no solo
  // 'compra_activo'. Tener líneas en el map ya significa "este movimiento saldó una cuota/componente".
  const lineas = capexResumenMap.get(m.id) || [];
  if (lineas.length === 0) return null;
  return (
    <div className={styles.capexDetalle}>
      {lineas.map((linea, idx) => (
        <div key={idx}>{linea}</div>
      ))}
    </div>
  );
};

// isMovPendiente: extraído a src/utils/movimientosBancarios.js (regla 10.1) — mismo predicado
// que consume el badge del tile "Caja" del Home (utils/badges.js). Se importa arriba.

// La columna EDIT del panel futuros es editable solo para entries que resuelven a una fila real
// (factura SII, externa, edificio, pipeline). Las sintéticas recurrente/residuo NO son editables.
// `es_lote` queda fuera: una línea de LOTE se ejecuta desde la pantalla Activos (genera
// las N unidades), no con el lápiz de "marcar pagado" — ese handler espera un activo_id
// y con un lote haría un return silencioso. Se habilita en la sub-fase 5 del bloque Stock.
const esEntryEditable = (f) =>
  ["facturado", "proveedor", "edificio", "pipeline", "capex"].includes(f.tipo) &&
  !f.es_recurrente &&
  !f.es_residuo &&
  // Fase 5b: la boleta de honorarios YA cuotea (cuelga de documento_id) → editable como cualquier
  // proveedor. (Antes se excluía con !f.es_boleta.)
  !f.es_lote &&
  // Cartelería (Backlog): solo los TRAMOS DE HARDWARE (los que crea la Fase 2A, con `entry_index`)
  // son editables — EditarFechaCobroModal escribe su override en detalle_pagos_hardware (Fase 2B).
  // La cuota de GESTIÓN de cartelería (origen "carteleria" SIN entry_index) sigue sin lápiz.
  (f.origen !== "carteleria" || f.entry_index != null);

// Entry de "Por pagar" de PROVEEDOR que va al kebab: SII recibida, externa (Fase 5a) O boleta
// (Fase 5b) — las tres cuotean. Necesita proveedor_id (para el modal) y un id (numérico en SII/externa;
// string "boleta-<id>" en boleta, cuyo id numérico real viaja en f.boleta_id). El ítem "Asociar pagos
// de CapEx" del kebab es SII-recibida-only → se guarda aparte con !f.es_externa && !f.es_boleta; y el
// ítem principal es "Plan de cuotas" para boleta (no tiene "Ajustar fecha"). Combina con esEntryEditable.
const esFacturaSiiRecibidaPorPagar = (f) => f.tipo === "proveedor" && f.id != null && f.proveedor_id != null;

// Ocurrencia recurrente (línea principal de un proveedor recurrente) con lápiz de override de fecha
// (Fase 3). NO reusa esEntryEditable (que sigue excluyendo es_recurrente para no routear estas filas
// a setPlanCuotasModal): es un branch dedicado. Requiere los campos que emite proveedoresRecurrentes.js
// (proveedor_id + período); los costos asociados no los traen, así que quedan fuera (correcto: la clave
// del override es una por proveedor+mes). Residuo/boleta/lote no son recurrentes → tampoco entran.
const esRecurrenteEditable = (f) =>
  !!f.es_recurrente &&
  f.tipo === "proveedor" &&
  f.proveedor_id != null &&
  f.periodo_año != null &&
  f.periodo_mes != null;

// Período tributario de una fila de impuesto, derivado de fecha_display (día 12 del mes SIGUIENTE
// al período → mes − 1), para no depender del texto del label. Lo usa el override de PPM/IUT.
const periodoDeImpuesto = (f) => {
  if (!f || f.tipo !== "impuesto" || !f.fecha_display) return null;
  const [yy, mm] = f.fecha_display.split("-").map(Number);
  if (!yy || !mm) return null;
  const py = mm === 1 ? yy - 1 : yy;
  const pm = mm === 1 ? 12 : mm - 1;
  return `${py}-${String(pm).padStart(2, "0")}`;
};

// RUT de la empresa por país (single-tenant). Solo Chile tiene bloque F29 / override. Coincide
// con RUT_BASE de los edge functions (Merinell 76857932). Comparación/escritura por ID.
const RUT_EMPRESA_POR_PAIS = { Chile: "76857932" };
// PPM/IUT de un mes CERRADO (id `ppm-YYYY-MM` / `iut-YYYY-MM`, NO los estimados `*-est-*`) →
// se pueden overridear a mano con el valor del contador.
const esFilaOverrideF29 = (f) => f && f.tipo === "impuesto" && /^(ppm|iut)-\d{4}-\d{2}$/.test(f.id || "");

// Invoca un edge function de sync (POST, sin body) con el anon key. Tira si devuelve {error}.
// Lo usan f29-sync / honorarios-sync / rcv-sync, enganchados al botón "Actualizar SII".
const invocarEdgeSync = async (nombre) => {
  const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/${nombre}`;
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
    },
  });
  const data = await resp.json();
  if (data.error) throw new Error(data.error);
  return data;
};

// Ejecutivo comercial Chile con renglón de comisión SEPARADO del sueldo base (Fase 1). Se identifica
// por usuario_id estable (regla 10.7 / 10.219), NO por rol. Perú (Karin 306 / Priscilla 7) fuera (P3.62).
const ID_USUARIO_COMERCIAL_CL = "904"; // Nicolás Soto

const FlujoCajaV2 = () => {
  const { pais, id: userId } = useAuthContext();
  const { loaded: ratesLoaded } = useExchangeRates(pais);
  const monedaLocal = pais === "Chile" ? "CLP" : "PEN";
  const simboloLocal = pais === "Chile" ? "$" : "S/";
  const enteFiscal = pais === "Chile" ? "SII" : "SUNAT";
  const [monedaVista, setMonedaVista] = useState(monedaLocal);
  const [consolidado, setConsolidado] = useState(false);
  const [convertirAUSD, setConvertirAUSD] = useState(false);
  const [filtrosActivos, setFiltrosActivos] = useState(["todo"]);
  // Sin filtro temporal por defecto (null) → la vista muestra hasta el 31/12 del año
  // siguiente (techo por defecto en filtrarPorCorte). Los botones Mes/+1/+2/Año son
  // cortes opcionales que se activan y se pueden desactivar (toggle).
  const [filtroTemporalKey, setFiltroTemporalKey] = useState(null);
  // Rango personalizado (desde/hasta) — alternativo y mutuamente excluyente con
  // filtroTemporalKey. { desde: "YYYY-MM-DD", hasta: "YYYY-MM-DD" } | null.
  const [rangoPersonalizado, setRangoPersonalizado] = useState(null);
  // UI del selector de período (ícono calendario + menú desplegable en la tarjeta de futuros).
  const [menuPeriodoAbierto, setMenuPeriodoAbierto] = useState(false);
  const [mostrarRangoForm, setMostrarRangoForm] = useState(false);
  const [rangoDesdeInput, setRangoDesdeInput] = useState("");
  const [rangoHastaInput, setRangoHastaInput] = useState("");
  const periodoMenuRef = useRef(null);
  const [filtroHistorico, setFiltroHistorico] = useState("todo");
  // Toggle independiente y combinable: filtra a solo movs pendientes de vincular
  // (predicado isMovPendiente, fuente única). Se suma a filtroHistorico + filtroTiempo.
  const [soloPendientes, setSoloPendientes] = useState(false);
  // Toggle independiente (⚠): filtra a solo movs con forzado pendiente (forzadosMap). Se apila
  // sobre fecha + tipo + búsqueda. Enabled solo si hay ⚠ en la vista base (hayAtencionEnVista).
  const [soloAtencion, setSoloAtencion] = useState(false);
  const [filtroTiempo, setFiltroTiempo] = useState("ytd");
  const [busquedaHistorico, setBusquedaHistorico] = useState("");
  const [busquedaFuturos, setBusquedaFuturos] = useState("");
  // Sticky en Futuros (docs/modulos/flujo-caja-v2.md § Sticky en Futuros): filas de
  // `flujo_caja_sticky` del país activo, en orden de fijado. `stickyLoaded` gatea el primer
  // pintado sticky para no mostrar "sin fijadas" mientras carga (10.273).
  const [stickyRows, setStickyRows] = useState([]);
  const [stickyLoaded, setStickyLoaded] = useState(false);
  // Carga acotada a esta tabla (10.234): una query al montar y al cambiar de país, filtrada por
  // país. Independiente de cargarDatosFuturos (no bloquea futurosListos).
  useEffect(() => {
    let vivo = true;
    setStickyLoaded(false);
    getStickyFuturos(pais)
      .then((rows) => {
        if (!vivo) return;
        setStickyRows(rows);
      })
      .catch((err) => {
        if (!vivo) return;
        // eslint-disable-next-line no-console
        console.error("stickyFuturos.getStickyFuturos", err);
        toast.error("No se pudieron cargar las filas fijadas");
        setStickyRows([]);
      })
      .finally(() => {
        if (vivo) setStickyLoaded(true);
      });
    return () => {
      vivo = false;
    };
  }, [pais]);
  // Modo del número del header de futuros: "neto" (netoVisibleFuturos, default) o
  // "saldo" (saldo final proyectado del conjunto filtrado). Solo UI, no persiste.
  const [modoHeaderFuturos, setModoHeaderFuturos] = useState("neto");
  // Panel maximizado (pin): null (vista 50/50) | "historico" | "futuros". Solo layout/UI.
  // Efímero a propósito (sin localStorage): al recargar vuelve a la vista 50/50. El panel
  // no elegido NO se desmonta (colapsa por CSS) → los datos ya cargados no se re-fetchean.
  const [panelMaximizado, setPanelMaximizado] = useState(null);

  // Cierra el menú de período al hacer click fuera del control.
  useEffect(() => {
    if (!menuPeriodoAbierto) return undefined;
    const onDocMouseDown = (ev) => {
      if (periodoMenuRef.current && !periodoMenuRef.current.contains(ev.target)) {
        setMenuPeriodoAbierto(false);
        setMostrarRangoForm(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [menuPeriodoAbierto]);

  // Selección de un corte fijo (Semana/Mes/+1/+2/Año). Toggle: re-elegir el activo lo desactiva.
  // Excluyente con el rango personalizado.
  const seleccionarPeriodo = (key) => {
    setFiltroTemporalKey((prev) => (prev === key ? null : key));
    setRangoPersonalizado(null);
    setMenuPeriodoAbierto(false);
    setMostrarRangoForm(false);
  };

  // Abre el sub-formulario de rango, precargando el rango vigente si lo hay.
  const abrirRangoForm = () => {
    setRangoDesdeInput(rangoPersonalizado?.desde || "");
    setRangoHastaInput(rangoPersonalizado?.hasta || "");
    setMostrarRangoForm(true);
  };

  // Aplica el rango desde/hasta como filtro (excluyente con el corte fijo). Requiere ambas
  // fechas; si vienen invertidas las ordena.
  const aplicarRangoPersonalizado = () => {
    if (!rangoDesdeInput || !rangoHastaInput) return;
    const desde = rangoDesdeInput <= rangoHastaInput ? rangoDesdeInput : rangoHastaInput;
    const hasta = rangoDesdeInput <= rangoHastaInput ? rangoHastaInput : rangoDesdeInput;
    setRangoPersonalizado({ desde, hasta });
    setFiltroTemporalKey(null);
    setMenuPeriodoAbierto(false);
    setMostrarRangoForm(false);
  };

  const toggleFiltro = (filtro) => {
    if (filtro === "todo") {
      setFiltrosActivos(["todo"]);
      return;
    }
    setFiltrosActivos((prev) => {
      const sinTodo = prev.filter((f) => f !== "todo");
      if (sinTodo.includes(filtro)) {
        const nuevo = sinTodo.filter((f) => f !== filtro);
        return nuevo.length === 0 ? ["todo"] : nuevo;
      }
      return [...sinTodo, filtro];
    });
  };

  const [movBancarios, setMovBancarios] = useState([]);
  const [cuentas, setCuentas] = useState([]);
  const [saldosIniciales, setSaldosIniciales] = useState([]);
  const [pendientesCobro, setPendientesCobro] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [proveedoresPendientes, setProveedoresPendientes] = useState([]);
  const [proveedoresRecurrentes, setProveedoresRecurrentes] = useState([]);
  const [impuestosEntries, setImpuestosEntries] = useState([]);
  const [capexEntries, setCapexEntries] = useState([]);
  // Lotes de compra planificados (bloque Stock) — segunda fuente del tab CapEx.
  const [capexLotes, setCapexLotes] = useState([]);
  // Componentes de costo (landed cost) pendientes — tercera fuente del tab CapEx. SUMAN a la línea
  // FOB del lote (no la reemplazan): el FOB es la base, estos son extras que capitalizan aparte (9.36).
  const [componentesLotes, setComponentesLotes] = useState([]);
  // Set de lote_id cuyo FOB ya está saldado (materializado como lote_costos tipo='fob') → mapCapexLote
  // oculta su línea FOB. Fuente única compartida con el modal de vinculación (CapEx-IVA Fase 5, 10.1).
  const [lotesFobSaldado, setLotesFobSaldado] = useState(new Map());
  const [edificiosMap, setEdificiosMap] = useState({});
  const [edificiosSaldo, setEdificiosSaldo] = useState([]);
  const [planPagosEdificios, setPlanPagosEdificios] = useState([]);
  const [pagoEdificioModal, setPagoEdificioModal] = useState(null);
  const [morasPendientes, setMorasPendientes] = useState([]);
  const [cuotasCreditos, setCuotasCreditos] = useState([]);
  const [operacionesFactoring, setOperacionesFactoring] = useState([]);
  // IDs de movimientos bancarios que ya están vinculados como adelanto o remanente de
  // alguna operación de factoring. Se calcula a partir de operacionesFactoring para no
  // hacer un fetch separado.
  const [movimientosBancarioFactoring, setMovimientosBancarioFactoring] = useState(() => new Set());
  const [mapaFactoring, setMapaFactoring] = useState(() => new Map());
  const [facturaInfoMap, setFacturaInfoMap] = useState(() => new Map());
  // Espejo del anterior para facturas RECIBIDAS: el badge C resuelve folio + razón social del emisor
  // desde acá (poblado desde el puente `facturas_vinculadas`) — única fuente tras B3 Fase B paso 2.
  const [facturaRecibidaInfoMap, setFacturaRecibidaInfoMap] = useState(() => new Map());
  // Espejo para BOLETAS de honorarios: el badge H resuelve folio + emisor_nombre desde acá (poblado
  // desde el puente `facturas_vinculadas`, factura_tipo='boleta_honorario'). Una BHE no es compra SII.
  const [boletaHonorarioInfoMap, setBoletaHonorarioInfoMap] = useState(() => new Map());
  // Detalle del badge A "CapEx a cuenta": movimiento_bancario_id → [líneas de display]. El vínculo es
  // INVERSO (plan_pagos_activos / lote_costos apuntan al movimiento con movimiento_bancario_id), no vive
  // en `m` — se resuelve por lookup en cargarDatosHistoricos (mismo patrón que facturaRecibidaInfoMap).
  const [capexResumenMap, setCapexResumenMap] = useState(() => new Map());
  // Detalle del badge R "Costo recurrente": movimiento_bancario_id → [{ concepto, periodo_mes,
  // periodo_año, monto, proveedor_nombre }]. El vínculo es el HILO ESTABLE conciliacion_costo_recurrente
  // por movimiento_bancario_id (NO categoria_pago, 10.240) — se resuelve por lookup en
  // cargarDatosHistoricos (mismo patrón que capexResumenMap).
  const [recurrenteResumenMap, setRecurrenteResumenMap] = useState(() => new Map());
  // Fase 3.1: badge V/C DERIVADO DEL LIBRO por origen (NO de categoria_pago). Sets de movimiento_bancario_id
  // que tienen un evento en `aplicaciones_saldo` con origen_tipo='movimiento_bancario':
  //   movsCobroSIILibro   = origen de una emitida  (documento_tipo='sii_emitida', eje=cobro) → badge V.
  //   movsPagoRecibidaLibro = origen de una recibida (documento_tipo='sii_recibida', eje=pago) → badge C.
  // Se pueblan en cargarDatosHistoricos (mismo loader que movBancarios, para quedar en lockstep con lo que
  // itera el badge — 10.229; el fetch del libro de Futuros lo saltea el patch scoped). El tooltip (folios)
  // sigue saliendo del puente `facturas_vinculadas` vía facturaInfoMap/facturaRecibidaInfoMap.
  const [movsCobroSIILibro, setMovsCobroSIILibro] = useState(() => new Set());
  const [movsPagoRecibidaLibro, setMovsPagoRecibidaLibro] = useState(() => new Set());
  // R3.4: resto del eje pago que YA está en el libro desde 1.2a/1.2b (externas/boletas) pero que NO
  // alimenta el badge C (ese es "Compra SII" específicamente — X/H se deciden por match_factura_tipo/
  // facturas_vinculadas, no por este set). Existe SOLO para que `movsVinculadosLibro` (→ isMovPendiente)
  // cubra TODO el eje pago del libro, no solo sii_recibida. No se pasa a renderBadgeMovimiento.
  const [movsPagoOtroLibro, setMovsPagoOtroLibro] = useState(() => new Set());
  // Vinculaciones forzadas por faltante (10.108 invertida): movimiento_bancario_id → { estado, faltante }.
  // Alimenta la marca "forzado/pendiente" del histórico. Arranca vacío → sin cambio visual hasta Fase 3.
  const [forzadosMap, setForzadosMap] = useState(() => new Map());
  const [planCuotasModal, setPlanCuotasModal] = useState(null);
  // "Pagada por caja chica" (kebab de "Por pagar", solo SII recibida): la entry de la factura a asignar.
  const [cajaChicaModal, setCajaChicaModal] = useState(null);
  // Asociar pagos de CapEx ya hechos (adelantos) a una factura recibida de "Por pagar" (modal compartido con Compras).
  const [asociarCapexModal, setAsociarCapexModal] = useState(null);
  // Set de factura_id (sii_recibida) con asociación CapEx ACTIVA → el kebab de "Por pagar" ofrece
  // "Desasociar" en vez de "Asociar" (mismo patrón y MISMA fuente que ComprasTab, regla 10.1 —
  // getFacturasConAsociacionCapex sobre asociaciones_capex_factura; sin reimplementar "está asociada").
  const [asociacionesCapex, setAsociacionesCapex] = useState(() => new Set());
  const [vincularModal, setVincularModal] = useState(null);
  const [entryEditarFecha, setEntryEditarFecha] = useState(null);
  const [entrySueldoFecha, setEntrySueldoFecha] = useState(null);
  const [entryRecurrenteFecha, setEntryRecurrenteFecha] = useState(null);
  const [capexPagadoModal, setCapexPagadoModal] = useState(null);
  const [capexFechaModal, setCapexFechaModal] = useState(null);
  const [compromisoFechaModal, setCompromisoFechaModal] = useState(null);
  // Alta suelta de un compromiso de pago desde el header de "Movimientos futuros" (proveedor opcional).
  const [mostrarCompromisoAltaModal, setMostrarCompromisoAltaModal] = useState(false);
  const [loteFechaModal, setLoteFechaModal] = useState(null);
  const [capexPlanModal, setCapexPlanModal] = useState(null);
  // { entry, campo: 'ppm'|'iut', periodo } — carga manual del PPM/IUT del contador (bloque F29).
  const [overrideF29Modal, setOverrideF29Modal] = useState(null);
  const [cuotasPagosPendientes, setCuotasPagosPendientes] = useState([]);
  const [cuotasCobrosPendientes, setCuotasCobrosPendientes] = useState([]);
  // Plan de pagos de activos CapEx: cuotas pendientes (proyección por cuota) + set de activos que
  // tienen plan (para no volver a la línea única cuando el plan está completo). `null` inicial
  // (Fase 1c, Commit 2a) distingue "todavía no cargó" de "cargó y no hay ningún activo con plan" — un
  // Set vacío por defecto hacía indistinguible una carga FALLIDA de "sin planes", lo que dejaba
  // `esFechaReagendable` tratar como reagendable (sin plan) un activo que en realidad sí tenía uno.
  // Solo se setea un Set real cuando `getActivosConPlan` resuelve con éxito (ver cargarDatosFuturos).
  const [cuotasActivosPendientes, setCuotasActivosPendientes] = useState([]);
  const [activosConPlan, setActivosConPlan] = useState(null);
  // Saldo del libro CapEx (`pagos_capex`) por target — Fase 3b (M1, 2026-09-25). `null` inicial, mismo
  // motivo que `activosConPlan`: distingue "todavía no cargó"/"la carga falló" de "cargó, libro vacío"
  // (un Map vacío por fallo dejaba TODAS las cuotas, incluidas las ya pagadas, con
  // `_tiene_evento_vigente:false` → clickeables como si estuvieran pendientes). `ctxReagendarFuturo`
  // exige `saldosCapex != null` para abrir el reagendado de TODO el tipo "capex". Solo se setea un Map
  // real cuando `getSaldosCapexPorTarget` resuelve con éxito (ver cargarDatosFuturos).
  const [saldosCapex, setSaldosCapex] = useState(null);
  // Compromisos de pago planificados (egreso futuro a proveedor sin factura). Espeja CapEx:
  // driver = compromisos planificado+sin-factura; bifurcación con-plan (cuotas) / sin-plan (línea única).
  const [compromisosEntries, setCompromisosEntries] = useState([]);
  const [cuotasCompromisoPendientes, setCuotasCompromisoPendientes] = useState([]);
  // `null` inicial — mismo motivo que `activosConPlan` arriba (espejo exacto).
  const [compromisosConPlan, setCompromisosConPlan] = useState(null);
  // IDs de facturas SII recibidas vinculadas a caja chica de algún empleado — se filtran de
  // "Por pagar" (su pago se rinde contra el fondo del empleado, no es deuda a proveedor).
  const [cajaChicaFacturaIds, setCajaChicaFacturaIds] = useState(() => new Set());
  // IDs de facturas SII recibidas de comisión NETEADA de un ingreso — unión de dos fuentes:
  // comisión de factoring (operaciones_factoring_comisiones) y comisión de recepción de
  // transferencia internacional (comisiones_ingreso_neteadas). Ninguna genera débito → se
  // filtran de "Por pagar" y del ledger.
  const [facturasComisionFactoringSet, setFacturasComisionFactoringSet] = useState(() => new Set());
  // IDs de facturas SII recibidas registradas como mora saldada por NETEO (operaciones_factoring_mora,
  // estado='saldada_neteo'). El DTE de mora se descontó del giro de una op posterior, sin débito
  // bancario → el FIFO las deja vivas; se excluyen de "Por pagar" con un gate propio en el predicado.
  const [facturasMoraNeteadaSet, setFacturasMoraNeteadaSet] = useState(() => new Set());
  const [yaVinculadasExternas, setYaVinculadasExternas] = useState(() => new Set());
  // Set de emitidas ya cobradas por CUALQUIER vía bancaria (FK directa + cuotas completas +
  // MBF con Σ ≥ total). Lo construye getFacturasYaVinculadas; "Por cobrar" lo usa para excluir.
  const [yaVinculadasEmitidas, setYaVinculadasEmitidas] = useState(() => new Set());
  const [facturasExternasPendientes, setFacturasExternasPendientes] = useState([]);
  // Saldo neto por proveedor (SII + externas + movs caja). El panel "Por pagar"
  // muestra detalle por folio/cuota, no el saldo consolidado — el agregado vive arriba
  // (ResumenSaldo). State preservado intencionalmente para consumidores futuros / ledger.
  // eslint-disable-next-line no-unused-vars
  const [saldosPendientesProveedores, setSaldosPendientesProveedores] = useState([]);
  // Map<"origen:id", saldoRestanteNativo> — saldo restante por factura calculado vía FIFO
  // aplicando pagos cronológicamente sobre facturas pendientes del mismo proveedor. Cubre
  // el caso donde los pagos no están vinculados a una factura específica vía FK. "origen"
  // ∈ {SII, EXT, LEG}. Una factura ausente del map = totalmente pagada (saldo ≤ 0.01).
  const [saldosPorFacturaMap, setSaldosPorFacturaMap] = useState(() => new Map());
  // Moneda en que está expresado cada saldo del ledger por proveedor (provId → provMoneda,
  // = moneda_default || monedaLocal). El consumidor en proyeccionesFuturas la usa para
  // re-expresar el saldo a la moneda de display de forma consistente, en vez de asumir
  // que coincide con la moneda de la factura (que puede diferir si el dato es inconsistente).
  const [provMonedaMap, setProvMonedaMap] = useState(() => new Map());
  // Nombre a MOSTRAR por proveedor (id → alias || razón social, getNombreProveedor). Se puebla en
  // cargarDatosFuturos y lo consume el mapper de "Por pagar" para mostrar el alias en vez de la
  // razón social del DTE (regla 10.1). Objeto plano, mismo patrón que provMonedaMap (state).
  const [nombreProvMap, setNombreProvMap] = useState(() => ({}));
  // Residuos por proveedor: deuda global del ledger que NO se atribuye a una factura
  // específica (cobros a cuenta, saldo inicial). Aparece como fila "Saldo a cuenta"
  // en "Por pagar" para que la suma del panel coincida con el saldo del estado de
  // cuenta. Shape: [{ provId, nombre, saldoDisplay, moneda }].
  const [residuosProveedoresPendientes, setResiduosProveedoresPendientes] = useState([]);
  // Boletas de honorarios (Bloque A): fuente por-documento de "Por pagar", espejo de las SII
  // recibidas. Se persisten las computa del país; el saldo por boleta sale del ledger
  // (saldosPorFacturaMap, clave "BHE:<id>"). Shape: filas crudas de boletas_honorarios.
  const [boletasHonorariosPendientes, setBoletasHonorariosPendientes] = useState([]);
  // IDs de proveedores-edificio (edificios.proveedor_id): su deuda se mide por la comisión del
  // edificio (getEdificiosSaldoPendiente), NO como obligación de proveedor → cargarDatosFuturos los
  // excluye del ledger vía `excluirProvIds`. Persistido para que el patch scoped de vinculación
  // (patchFuturosProveedor) pueda GUARDAR contra ellos: getSaldosPorFacturaProveedor NO recibe
  // excluirProvIds, así que para un proveedor-edificio divergiría → se cae a recarga completa.
  const [provIdsEdificio, setProvIdsEdificio] = useState(() => new Set());
  const [pagosEmpleadosRegistrados, setPagosEmpleadosRegistrados] = useState([]);
  // Haberes imponibles por-mes (bono one-off / comisión) para la liquidación Chile. Batch por país
  // (sin N+1), indexado en haberesPorEmpMes; se pasa a calcularLiquidacionChile por empleado/mes.
  const [haberesRRHH, setHaberesRRHH] = useState([]);
  // Flag de "listo" del efecto getHaberesPais (Fase 1.5). haberesRRHH alimenta rrhhEntries →
  // proyeccionesFuturas por AFUERA de cargarDatosFuturos, así que el gate futurosListos no lo espera.
  // El render del panel de futuros combina esta señal con futurosListos para no repintar cuando este
  // feed llega tarde. Arranca false; se baja en cada corrida del efecto (cambio de pais) y sube tras
  // el setState de haberesRRHH.
  const [haberesListos, setHaberesListos] = useState(false);
  // Override de fecha de pago del sueldo líquido por empleado/mes (pago_sueldo_fecha). Batch por
  // país; indexado en fechasPagoPorEmpMes. Sin override → último día del mes (comportamiento actual).
  const [fechasPagoRRHH, setFechasPagoRRHH] = useState([]);
  // Flag de "listo" del efecto getFechasPagoPais (Fase 1.5). Mismo rol que haberesListos para el otro
  // feed externo de rrhhEntries.
  const [fechasPagoListos, setFechasPagoListos] = useState(false);
  // Pagos de PREVIRED (movimientos_caja categoria='cotizaciones_previsionales') para conciliar el
  // PREVIRED del tab RRHH a nivel mes (R2). Se puebla en cargarDatosFuturos desde movimientosCajaRes.
  const [movimientosCajaCotizaciones, setMovimientosCajaCotizaciones] = useState([]);
  // Comisión comercial de Nicolás (usuario 904) re-agrupada por mes de GENERACIÓN con saldo acumulado
  // (comisionPorGeneracion). Se muestra como renglón "(comisión)" SEPARADO del sueldo base en la
  // proyección Chile. null hasta computar / si no aplica. Solo Chile (Perú fuera, ver P3.62).
  const [comisionGen, setComisionGen] = useState(null); // { empleadoId, totalPendiente, porBucket, ... } | null
  const [loading, setLoading] = useState(false);
  // Gate visual del panel de FUTUROS (espejo de `loading` para el histórico). cargarDatosFuturos
  // puebla su estado en 4 tandas separadas por `await`; sin este flag el panel se re-renderiza en
  // cada tanda intermedia y se ven aparecer/desaparecer filas (costos de ene/feb). Arranca false, se
  // baja a false al inicio de CADA corrida (carga inicial y refetch de modales) y sube a true recién
  // tras el último setState (setPendientesCobro). No toca datos ni memos.
  const [futurosListos, setFuturosListos] = useState(false);
  const [sincronizandoBanco, setSincronizandoBanco] = useState(false);
  const [sincronizandoSII, setSincronizandoSII] = useState(false);
  const [ultimaConsulta, setUltimaConsulta] = useState(null);

  const currentYear = new Date().getFullYear();
  const { empleadosRRHH, isEmpleadosRRHHLoading, getSueldoBaseVigenteParaAno } = useRRHHCalculations(
    pais,
    currentYear,
    12,
    [],
    []
  );

  const pagosMovIds = useMemo(
    () => new Set(pagosEmpleadosRegistrados.map((p) => p.movimiento_bancario_id).filter(Boolean)),
    [pagosEmpleadosRegistrados]
  );
  // Fase 3.1 + R3.4: unión de los TRES Sets del libro por origen → un movimiento con CUALQUIER evento
  // en el libro (cobró una emitida, pagó una recibida, o pagó una externa/boleta) NO está pendiente.
  // movsCobroSIILibro/movsPagoRecibidaLibro son también la contraparte de predicado del badge V/C
  // (10.229); movsPagoOtroLibro solo alimenta isMovPendiente (badges X/H se deciden aparte).
  const movsVinculadosLibro = useMemo(
    () => new Set([...movsCobroSIILibro, ...movsPagoRecibidaLibro, ...movsPagoOtroLibro]),
    [movsCobroSIILibro, movsPagoRecibidaLibro, movsPagoOtroLibro]
  );

  useEffect(() => {
    let activo = true;
    setHaberesListos(false); // reset al re-disparar (cambio de pais); el panel de futuros vuelve a esperar
    getHaberesPais(pais)
      .then((h) => {
        if (!activo) return;
        setHaberesRRHH(h || []);
        setHaberesListos(true);
      })
      .catch(() => {
        if (!activo) return;
        setHaberesRRHH([]);
        setHaberesListos(true);
      });
    return () => {
      activo = false;
    };
  }, [pais]);

  useEffect(() => {
    let activo = true;
    setFechasPagoListos(false); // reset al re-disparar (cambio de pais); el panel de futuros vuelve a esperar
    getFechasPagoPais(pais)
      .then((f) => {
        if (!activo) return;
        setFechasPagoRRHH(f || []);
        setFechasPagoListos(true);
      })
      .catch(() => {
        if (!activo) return;
        setFechasPagoRRHH([]);
        setFechasPagoListos(true);
      });
    return () => {
      activo = false;
    };
  }, [pais]);

  // Comisión de Nicolás (usuario 904) por mes de GENERACIÓN con saldo acumulado (comisionPorGeneracion).
  // Reusa cargarDepsComision — los MISMOS deps que la card de mis-facturas / la materialización (regla
  // 10.1, sin duplicar el motor). Pendiente = Cobrado (getCobroReal SIN cambios: factoring solo
  // liquidado — 10.194) − Pagado (haberes ya disbursados). Se renderiza como renglón "(comisión)"
  // separado del sueldo base. Solo Chile; Perú fuera (P3.62).
  useEffect(() => {
    if (pais !== "Chile" || !ratesLoaded || isEmpleadosRRHHLoading || !empleadosRRHH?.length) {
      setComisionGen(null);
      return undefined;
    }
    const nicolas = empleadosRRHH.find((e) => String(e.usuario_id) === ID_USUARIO_COMERCIAL_CL);
    if (!nicolas) {
      setComisionGen(null);
      return undefined;
    }
    let activo = true;
    (async () => {
      try {
        const deps = await cargarDepsComision("Chile");
        // Pagado = Σ comisión YA DESEMBOLSADA a Nicolás, desde pagos_empleados (FUENTE ÚNICA, 10.1 —
        // mismo criterio que Sueldo). Toda fila de pagos_empleados es un pago real: se suman TODAS las
        // tipo='comision' sin filtrar por período (un pago cuenta aunque su período sea el mes en curso,
        // ej. el pago de agosto). Antes se sumaban los haberes con período pasado (proxy), que no veía
        // los pagos registrados por el modal. Se cargan los pagos del empleado directo (los del backfill
        // no tienen movimiento_bancario_id, así que no vendrían por pagosEmpleadosRegistrados).
        const pagosNicolas = await getPagosEmpleados({ empleadoId: nicolas.id });
        const pagadoTotal = sumarPagadoComision(pagosNicolas, nicolas.id);
        const res = comisionPorGeneracion(ID_USUARIO_COMERCIAL_CL, { ...deps, pagadoTotal });
        // Vencido = meses de pago anteriores al mes en curso todavía pendientes → se agrupan y muestran
        // en el mes en curso (no en un mes pasado). El resto va en su propio mes de pago.
        const hoyYm = new Date().toISOString().slice(0, 7);
        let overdue = 0;
        for (const [ym, monto] of res.pendientePorMesPago) if (ym < hoyYm) overdue += monto;
        if (activo) setComisionGen({ empleadoId: nicolas.id, hoyYm, overdue, ...res });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[RRHH] comisión por generación (Nicolás):", e);
        if (activo) setComisionGen(null);
      }
    })();
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pais, ratesLoaded, isEmpleadosRRHHLoading, empleadosRRHH, haberesRRHH]);

  // Haberes indexados por empleado/período: `${empleado_id}-${año}-${mes}` → array {monto, imponible}.
  const haberesPorEmpMes = useMemo(() => {
    const map = {};
    for (const h of haberesRRHH) {
      if (!h.empleado_id || !h.periodo_año || !h.periodo_mes) continue;
      const key = `${h.empleado_id}-${h.periodo_año}-${h.periodo_mes}`;
      (map[key] || (map[key] = [])).push({ monto: h.monto, imponible: h.imponible, tipo: h.tipo });
    }
    return map;
  }, [haberesRRHH]);

  // Override de fecha de pago indexado: `${empleado_id}-${año}-${mes}` → fecha_pago (YYYY-MM-DD).
  const fechasPagoPorEmpMes = useMemo(() => {
    const map = {};
    for (const f of fechasPagoRRHH) {
      if (!f.empleado_id || !f.periodo_año || !f.periodo_mes || !f.fecha_pago) continue;
      map[`${f.empleado_id}-${f.periodo_año}-${f.periodo_mes}`] = f.fecha_pago;
    }
    return map;
  }, [fechasPagoRRHH]);

  const pagadoLiquidoPorEmpMes = useMemo(() => {
    const map = {};
    for (const p of pagosEmpleadosRegistrados) {
      if (p.tipo !== "sueldo" && p.tipo !== "adelanto") continue;
      if (!p.empleado_id || !p.periodo_año || !p.periodo_mes) continue;
      const key = `${p.empleado_id}-${p.periodo_año}-${p.periodo_mes}`;
      map[key] = (map[key] || 0) + Number(p.monto || 0);
    }
    return map;
  }, [pagosEmpleadosRegistrados]);

  // Pagos de cada empleado agrupados por empleado_id (bank-backed). Alimentan la cuenta corriente
  // por período (saldoLiquidoPorPeriodo): cada sueldo/adelanto neta el mes que declara.
  const pagosPorEmpleado = useMemo(() => {
    const map = new Map();
    for (const p of pagosEmpleadosRegistrados) {
      if (!p.empleado_id) continue;
      if (!map.has(p.empleado_id)) map.set(p.empleado_id, []);
      map.get(p.empleado_id).push(p);
    }
    return map;
  }, [pagosEmpleadosRegistrados]);

  // Filas de pagos_empleados agrupadas por movimiento_bancario_id → alimenta el desglose del badge "E"
  // (un pago combinado sueldo+bono son N filas con el mismo movimiento). Mismo patrón que pagosPorEmpleado.
  const pagosEmpleadosPorMov = useMemo(() => {
    const map = new Map();
    for (const p of pagosEmpleadosRegistrados) {
      if (!p.movimiento_bancario_id) continue;
      if (!map.has(p.movimiento_bancario_id)) map.set(p.movimiento_bancario_id, []);
      map.get(p.movimiento_bancario_id).push(p);
    }
    return map;
  }, [pagosEmpleadosRegistrados]);

  const rrhhEntries = useMemo(() => {
    if (isEmpleadosRRHHLoading || !empleadosRRHH?.length) return [];
    const hoy = new Date().toISOString().split("T")[0];
    const añoBase = new Date().getFullYear();

    const DESCUENTO_EMPLEADO_PE = 0.13;
    const CARGA_EMPLEADOR_PE = 0.09;

    const entries = [];

    // ── Cuenta corriente RRHH Chile (por período) — saldo de líquido y PREVIRED por empleado/mes ──
    // Fuente ÚNICA del saldo (saldoLiquidoPorPeriodo en pagosEmpleados.js, regla 10.1). Imputación
    // ESTRICTA por período (2026-08-20): saldo_mes = devengado_mes − pagado_mes, donde pagado_mes es
    // Σ de lo tageado a ESE mes — un pago neta el mes que declara, nunca otro (reemplaza el FIFO
    // viejo→nuevo de netearEmpleado, que ignoraba el período y arrastraba al mes más viejo). Mes
    // cerrado (con recibo) anclado al líquido transferido, mes abierto al motor; allowlist intacta.
    // El motor (calcularLiquidacionChile) se corre UNA vez por empleado/mes acá y de paso alimenta
    // el PREVIRED del empleado (no se recalcula en el loop de emisión de abajo).
    const saldoLiquidoChile = new Map(); // `${emp.id}-${año}-${mes}` -> saldo del líquido (FULL, con bono)
    const previredChilePorEmpMes = new Map(); // `${emp.id}-${año}-${mes}` -> PREVIRED del empleado
    // Contribución NETA del bono al líquido del mes (líquido CON bono − líquido SIN bono), por
    // empleado/mes. Solo PRESENTACIÓN: en la emisión se parte la fila del líquido en "base" + "(bono)"
    // que SUMAN el mismo saldo de hoy (no cambia cuánto ni cuándo). "Bono" = `bonos_fijo`/`bonos_variable`
    // de la vigencia (Franco) + haberes tipo bono/otro (Jorge). La comisión de Nicolás va por su propio
    // camino (comisionGen), no por acá.
    const bonoNetoChilePorEmpMes = new Map(); // `${emp.id}-${año}-${mes}` -> contribución neta del bono
    if (pais === "Chile") {
      for (const emp of empleadosRRHH) {
        const devengados = [];
        for (const añoDev of [añoBase, añoBase + 1]) {
          for (let m = 0; m < 12; m++) {
            const periodoDev = `${añoDev}-${String(m + 1).padStart(2, "0")}`;
            if (periodoDev < "2026-01") continue;
            const mesStrDev = `${añoDev}-${String(m + 1).padStart(2, "0")}-01`;
            if (emp.egreso_fecha && emp.egreso_fecha < mesStrDev) continue;
            const ultimoDiaDev = new Date(añoDev, m + 1, 0).toISOString().split("T")[0];
            // Remuneración vigente del mes (misma lógica de vigencia que el ER). Sin vigente → no
            // devenga ese mes (no se hardcodea año alguno; sale de las vigencias, regla 10.110).
            const vigentes = (emp.remuneraciones || []).filter(
              (r) => r.inicio_vigencia <= ultimoDiaDev && (!r.fin_vigencia || r.fin_vigencia >= mesStrDev)
            );
            const remVigente = vigentes.sort((a, b) => (a.inicio_vigencia < b.inicio_vigencia ? 1 : -1))[0];
            if (!remVigente || Number(remVigente.sueldo_base) <= 0) continue;
            // El sueldo BASE del comercial (Nicolás) NO incluye comisión: la comisión va en su propio
            // renglón "(comisión)" vía comisionPorGeneracion (no mezclar en un solo número). Para el
            // resto, los haberes (bono, etc.) siguen entrando al devengado como hoy.
            const esComercialSplit = String(emp.usuario_id) === ID_USUARIO_COMERCIAL_CL;
            const haberesMesRaw = haberesPorEmpMes[`${emp.id}-${añoDev}-${m + 1}`] || [];
            const haberesMes = esComercialSplit ? haberesMesRaw.filter((h) => h.tipo !== "comision") : haberesMesRaw;
            const liq = calcularLiquidacionChile(
              remVigente,
              getUFValue(ultimoDiaDev),
              0,
              getUTMValue(ultimoDiaDev),
              haberesMes
            );
            // PREVIRED del empleado = AFP + salud + cesantía + carga patronal (sin impuesto único:
            // ese va por la línea IUT del F29, no a PreviRed). Sale del líquido FULL (con bono), como hoy.
            previredChilePorEmpMes.set(
              `${emp.id}-${añoDev}-${m + 1}`,
              liq.totalAfp + liq.totalSalud + liq.segCesantia + liq.cargaPatronal
            );
            // Contribución neta del bono = líquido FULL − líquido SIN bono (mismo motor, dos corridas —
            // no lineal por grati/impuesto, por eso se resta, no se usa el bruto). Se guarda para partir
            // la fila en la emisión (base + "(bono)"). Si no hay bono → 0 → no se emite fila de bono.
            const remBaseSinBono = { ...remVigente, bonos_fijo: 0, bonos_variable: 0 };
            const haberesBase = haberesMes.filter((h) => h.tipo !== "bono" && h.tipo !== "otro");
            const liqBase = calcularLiquidacionChile(
              remBaseSinBono,
              getUFValue(ultimoDiaDev),
              0,
              getUTMValue(ultimoDiaDev),
              haberesBase
            );
            bonoNetoChilePorEmpMes.set(`${emp.id}-${añoDev}-${m + 1}`, Math.max(0, liq.liquido - liqBase.liquido));
            devengados.push({ año: añoDev, mes: m + 1, devengadoMotor: liq.liquido });
          }
        }
        const { subledger } = saldoLiquidoPorPeriodo({
          devengados,
          pagos: pagosPorEmpleado.get(emp.id) || [],
          empleadoId: emp.id, // scope de pagos + allowlist P3.17 (neteo por motor directo, Ignacio)
        });
        for (const row of subledger) saldoLiquidoChile.set(`${emp.id}-${row.año}-${row.mes}`, row.saldo);
      }
    }

    // Proyectar sueldos/cargas desde enero del año actual hasta diciembre del año
    // siguiente, para cubrir la vista completa (sin corte temporal) del panel futuro.
    for (const año of [añoBase, añoBase + 1]) {
      for (let mesIdx = 0; mesIdx < 12; mesIdx++) {
        const ultimoDiaMes = new Date(año, mesIdx + 1, 0).toISOString().split("T")[0];
        const mesSiguiente = mesIdx === 11 ? 1 : mesIdx + 2;
        const añoSiguiente = mesIdx === 11 ? año + 1 : año;
        const dia12SiguienteMes = `${añoSiguiente}-${String(mesSiguiente).padStart(2, "0")}-12`;
        const periodoMes = `${año}-${String(mesIdx + 1).padStart(2, "0")}`;

        // Corte: no proyectar períodos < 2026-01 (saldados por saldo inicial). Reemplaza el viejo
        // guard que salteaba TODO mes cerrado por fecha (R1, regla 10.163): ahora los meses cerrados
        // pasan y el líquido impago se genera; PREVIRED y Perú mantienen su propio guard de fecha.
        if (periodoMes < "2026-01") continue;

        // R2: PREVIRED consolidado por mes (el pago real es UNO por mes, todos los empleados juntos).
        // Se acumula durante el loop de empleados y se emite UNA fila después.
        let previredMesChile = 0;

        for (const emp of empleadosRRHH) {
          const mesStr = `${año}-${String(mesIdx + 1).padStart(2, "0")}-01`;
          if (emp.egreso_fecha && emp.egreso_fecha < mesStr) continue;

          const bruto = getSueldoBaseVigenteParaAno(emp, mesIdx, año) || 0;
          if (bruto <= 0) continue;

          const nombreRaw = `${emp.nombre || ""} ${emp.apellido || ""}`.trim();
          const nombre = nombreRaw
            .toLowerCase()
            .split(" ")
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");

          const yaPagado = pagadoLiquidoPorEmpMes[`${emp.id}-${año}-${mesIdx + 1}`] || 0;
          // Sufijo de la fila base: "Sueldo" (mes completo por pagar) o "Saldo MM/AA" cuando hay pago
          // parcial → el MM/AA es el PERÍODO que se está neteando (año, mesIdx+1), no la fecha de pago.
          // Solo texto/label. El mes se muestra solo en "Saldo" (para Sueldo ya está la columna Fecha).
          const sufijoBase =
            yaPagado > 0 ? `Saldo ${String(mesIdx + 1).padStart(2, "0")}/${String(año).slice(-2)}` : "Sueldo";

          // Fecha del sueldo líquido: override (pago_sueldo_fecha) si existe, si no la fecha estimada
          // de pago (día hábil cerca de fin de mes, Prod-1a). Solo mueve fecha_display de la fila de
          // sueldo; NO afecta monto/saldo/neteo ni la fecha de PREVIRED/EsSalud (día 12). Chile y Perú.
          const overrideFechaSueldo = fechasPagoPorEmpMes[`${emp.id}-${año}-${mesIdx + 1}`] || null;
          const fechaEstimadaSueldo = fechaPagoEstimada(año, mesIdx + 1);
          const fechaPagoLiquido = overrideFechaSueldo || fechaEstimadaSueldo;
          // Campos para el lápiz/modal de override de fecha de sueldo (FASE 8.2). No afectan monto.
          const camposFechaSueldo = {
            es_sueldo_liquido: true,
            empleado_id: emp.id,
            periodo_año: año,
            periodo_mes: mesIdx + 1,
            fecha_default: fechaEstimadaSueldo,
            tiene_override: !!overrideFechaSueldo,
          };

          if (pais === "Chile") {
            // Saldo del líquido y PREVIRED salen de la cuenta corriente por período ya computada arriba
            // (saldoLiquidoPorPeriodo). Sin entrada en el mapa → el mes no devenga (sin rem. vigente).
            const saldoLiquido = saldoLiquidoChile.get(`${emp.id}-${año}-${mesIdx + 1}`) || 0;
            const previred = previredChilePorEmpMes.get(`${emp.id}-${año}-${mesIdx + 1}`) || 0;

            // R1 (regla 10.163): el líquido sale de la proyección SOLO cuando está pagado de verdad
            // (sueldos+adelantos bank-backed netados contra el mes que declaran), NO por fecha. Vencido
            // impago queda visible (fecha pasada → rojo por la regla universal del render). La fila se
            // emite solo si el saldo PENDIENTE supera la tolerancia de redondeo. NOTA: este gate exige
            // saldo > 0, así que un sobrepago (saldo negativo) hoy NO se emite — no hay casos reales
            // (auditoría 2026-08-20) y surfacearlo como "a favor" en la proyección es decisión de Fase 3.
            if (saldoLiquido > TOLERANCIA_SALDO_LIQUIDO) {
              // Partición base + "(bono)" (SOLO presentación, no cambia el total): el bono (bonos_fijo
              // de Franco / haber tipo bono/otro de Jorge) va en su propia fila. base + bono =
              // saldoLiquido (idéntico a la fila única de hoy). El adelanto se imputa primero a la base
              // (el bono se capa al saldo disponible → la base nunca queda negativa). Comisión de Nicolás
              // NO pasa por acá (va por comisionGen). bonoNeto=0 → solo la fila base, como antes.
              const bonoNeto = Math.min(
                bonoNetoChilePorEmpMes.get(`${emp.id}-${año}-${mesIdx + 1}`) || 0,
                saldoLiquido
              );
              const baseSaldo = saldoLiquido - bonoNeto;
              if (baseSaldo > TOLERANCIA_SALDO_LIQUIDO) {
                entries.push({
                  id: `rrhh-liquido-${emp.id}-${año}-${mesIdx}`,
                  fecha_display: fechaPagoLiquido,
                  importeEnMoneda: -baseSaldo,
                  razon_social_receptor: `RRHH | ${nombre} | ${sufijoBase}`,
                  tipo: "rrhh",
                  ...camposFechaSueldo,
                });
              }
              if (bonoNeto > TOLERANCIA_SALDO_LIQUIDO) {
                entries.push({
                  id: `rrhh-bono-${emp.id}-${año}-${mesIdx}`,
                  fecha_display: fechaPagoLiquido,
                  importeEnMoneda: -bonoNeto,
                  razon_social_receptor: `RRHH | ${nombre} | Bono`,
                  tipo: "rrhh",
                  ...camposFechaSueldo, // lápiz de fecha (override por empleado/período, igual que el sueldo)
                });
              }
            }

            // Renglón SEPARADO de comisión del comercial (Nicolás): la comisión pendiente se muestra en
            // CADA mes de PAGO (= cobro real + 1, comisionPorGeneracion.pendientePorMesPago). Ej.:
            // factoring liquidado en julio → pago agosto; cobro de agosto (INTEREXPO/ABINBEV) → pago
            // septiembre. Lo VENCIDO (meses de pago ya pasados y aún impagos) se agrupa en el mes en
            // curso. NO se mezcla con el sueldo base (que ya excluye la comisión del devengado, arriba).
            if (comisionGen && comisionGen.empleadoId === emp.id) {
              const mesPagoYm = `${año}-${String(mesIdx + 1).padStart(2, "0")}`;
              const esMesEnCurso = mesPagoYm === comisionGen.hoyYm;
              // Meses pasados: nada (su pendiente ya se sumó en `overdue` → mes en curso).
              if (mesPagoYm >= comisionGen.hoyYm) {
                const propio = comisionGen.pendientePorMesPago.get(mesPagoYm) || 0;
                const montoComision = esMesEnCurso ? propio + (comisionGen.overdue || 0) : propio;
                if (montoComision > TOLERANCIA_SALDO_LIQUIDO) {
                  entries.push({
                    id: `rrhh-comision-${emp.id}-${año}-${mesIdx}`,
                    fecha_display: fechaPagoLiquido,
                    importeEnMoneda: -Math.round(montoComision),
                    razon_social_receptor: `RRHH | ${nombre} | Comisión`,
                    tipo: "rrhh",
                    ...camposFechaSueldo, // lápiz de fecha (override por empleado/período de pago)
                  });
                }
              }
            }

            // PREVIRED consolidado: acumular (la fila única del mes se emite tras el loop de empleados).
            previredMesChile += previred;
          } else {
            const liquidoEsperado = Math.round(bruto * (1 - DESCUENTO_EMPLEADO_PE));
            const saldoLiquido = Math.max(0, liquidoEsperado - yaPagado);
            const essalud = Math.round(bruto * CARGA_EMPLEADOR_PE);

            if (ultimoDiaMes > hoy && saldoLiquido > 0) {
              entries.push({
                id: `rrhh-liquido-${emp.id}-${año}-${mesIdx}`,
                fecha_display: fechaPagoLiquido,
                importeEnMoneda: -saldoLiquido,
                razon_social_receptor: `RRHH | ${nombre} | ${sufijoBase}`,
                tipo: "rrhh",
                ...camposFechaSueldo,
              });
            }
            if (ultimoDiaMes > hoy) {
              entries.push({
                id: `rrhh-essalud-${emp.id}-${año}-${mesIdx}`,
                fecha_display: dia12SiguienteMes,
                importeEnMoneda: -essalud,
                razon_social_receptor: `EsSalud | ${nombre}`,
                tipo: "rrhh",
              });
            }
          }
        }

        // R2 (regla 10.163): PREVIRED consolidado sale de la proyección SOLO si hay pago real
        // conciliado a nivel mes (movimientos_caja categoria='cotizaciones_previsionales', match por
        // período con el mismo patrón de detectarEstadoPago). NO por fecha: vencido impago queda
        // (fecha pasada → rojo por la regla universal). Solo Chile.
        if (pais === "Chile" && previredMesChile > 0) {
          const estadoPrevired = detectarEstadoPago(
            periodoMes,
            dia12SiguienteMes,
            movimientosCajaCotizaciones,
            "cotizaciones_previsionales"
          ).estado;
          if (estadoPrevired !== "pagado") {
            entries.push({
              id: `rrhh-previred-${año}-${mesIdx}`,
              fecha_display: dia12SiguienteMes,
              importeEnMoneda: -previredMesChile,
              razon_social_receptor: `PREVIRED ${periodoMes}`,
              tipo: "rrhh",
            });
          }
        }
      }
    }

    return entries.sort((a, b) => a.fecha_display.localeCompare(b.fecha_display));
  }, [
    empleadosRRHH,
    isEmpleadosRRHHLoading,
    pais,
    getSueldoBaseVigenteParaAno,
    pagadoLiquidoPorEmpMes,
    pagosPorEmpleado,
    haberesPorEmpMes,
    fechasPagoPorEmpMes,
    movimientosCajaCotizaciones,
    comisionGen,
  ]);

  const cargarDatosHistoricos = useCallback(async () => {
    if (!pais || pais === "Region" || !ratesLoaded) return;
    const [movs, ctas, saldosInicialesData, libroOrigenRes] = await Promise.all([
      getMovimientosBancarios(pais, { fechaDesde: "2026-01-01" }),
      getCuentasBancarias(pais),
      client.from("saldos_iniciales_cuentas").select("*"),
      // Fase 3.1: eventos del libro por ORIGEN bancario → decisión V/C del badge (NO categoria_pago).
      // Fetch focalizado (solo lo que el badge necesita) en ESTE loader para quedar en lockstep con
      // movBancarios: el fetch del libro de cargarDatosFuturos lo saltea el patch scoped en cada vincular,
      // pero cargarDatosHistoricos SIEMPRE recarga (10.229 — badge y predicado del mismo predicado/fuente).
      // aplicaciones_saldo NO es país-scoped (sin columna pais); origen_id es el movimiento_bancario.id.
      client
        .from("aplicaciones_saldo")
        .select("origen_id, documento_tipo")
        .eq("origen_tipo", "movimiento_bancario")
        .range(0, 49999),
    ]);
    // Sets por eje (el CHECK chk_aplicaciones_saldo_eje_tipo garantiza eje=cobro⟺sii_emitida,
    // eje=pago⟺recibida/externa/boleta → filtrar por documento_tipo equivale a filtrar por eje).
    const cobroSIISet = new Set();
    const pagoRecibidaSet = new Set();
    const pagoOtroSet = new Set(); // R3.4: factura_externa / boleta_honorario (eje pago, fuera del badge C)
    for (const a of libroOrigenRes.data || []) {
      if (a.origen_id == null) continue;
      if (a.documento_tipo === "sii_emitida") cobroSIISet.add(a.origen_id);
      else if (a.documento_tipo === "sii_recibida") pagoRecibidaSet.add(a.origen_id);
      else if (a.documento_tipo === "factura_externa" || a.documento_tipo === "boleta_honorario") {
        pagoOtroSet.add(a.origen_id);
      }
    }
    setMovsCobroSIILibro(cobroSIISet);
    setMovsPagoRecibidaLibro(pagoRecibidaSet);
    setMovsPagoOtroLibro(pagoOtroSet);

    // Procesar ventas de divisas antes de setear el state
    await procesarVentaDivisas({
      movimientos: movs,
      cuentas: ctas,
      pais,
    });

    // Segundo fetch para traer estados actualizados post-proceso
    const movsActualizados = await getMovimientosBancarios(pais, { fechaDesde: "2026-01-01" });
    const ordenados = [...movsActualizados].sort((a, b) => b.fecha.localeCompare(a.fecha) || b.id - a.id);

    setMovBancarios(ordenados);
    setCuentas(ctas);
    setSaldosIniciales(saldosInicialesData?.data || []);

    // Resolver info (tipo_dte, folio, razon_social_receptor) de facturas emitidas
    // vinculadas vía multi: la tabla puente movimiento_bancario_facturas no tiene FK
    // formal a facturas_sii_emitidas, así que PostgREST no embebe el anidado. Fetch
    // separado y mapa en cliente.
    const idsEmitidasMulti = new Set();
    for (const m of ordenados) {
      for (const v of m.facturas_vinculadas || []) {
        if (v.factura_tipo === "sii_emitida" && v.factura_id != null) {
          idsEmitidasMulti.add(v.factura_id);
        }
      }
    }
    if (idsEmitidasMulti.size > 0) {
      const { data: facsInfo } = await client
        .from("facturas_sii_emitidas")
        .select("id, tipo_dte, folio, razon_social_receptor")
        .in("id", [...idsEmitidasMulti]);
      setFacturaInfoMap(
        new Map(
          (facsInfo || []).map((f) => [
            f.id,
            { tipo_dte: f.tipo_dte, folio: f.folio, razon_social_receptor: f.razon_social_receptor },
          ])
        )
      );
    } else {
      setFacturaInfoMap(new Map());
    }

    // Espejo para RECIBIDAS: mismo problema (MBF no tiene FK formal a facturas_sii_recibidas →
    // PostgREST no embebe el anidado en el caso múltiple). Fetch separado para que el badge C
    // muestre folio + razón social cuando la FK directa factura_sii_recibida_id quedó null.
    const idsRecibidasMulti = new Set();
    for (const m of ordenados) {
      for (const v of m.facturas_vinculadas || []) {
        if (v.factura_tipo === "sii_recibida" && v.factura_id != null) {
          idsRecibidasMulti.add(v.factura_id);
        }
      }
    }
    if (idsRecibidasMulti.size > 0) {
      const { data: recsInfo } = await client
        .from("facturas_sii_recibidas")
        .select("id, folio, razon_social_emisor, monto_total")
        .in("id", [...idsRecibidasMulti]);
      setFacturaRecibidaInfoMap(
        new Map(
          (recsInfo || []).map((f) => [
            f.id,
            { folio: f.folio, razon_social_emisor: f.razon_social_emisor, monto_total: f.monto_total },
          ])
        )
      );
    } else {
      setFacturaRecibidaInfoMap(new Map());
    }

    // Espejo para BOLETAS de honorarios (badge H): el vínculo MBF factura_tipo='boleta_honorario' no
    // tiene FK formal a boletas_honorarios → fetch separado de folio + emisor_nombre para el tooltip.
    const idsBoletasMulti = new Set();
    for (const m of ordenados) {
      for (const v of m.facturas_vinculadas || []) {
        if (v.factura_tipo === "boleta_honorario" && v.factura_id != null) {
          idsBoletasMulti.add(v.factura_id);
        }
      }
    }
    if (idsBoletasMulti.size > 0) {
      const { data: bhsInfo } = await client
        .from("boletas_honorarios")
        .select("id, folio, emisor_nombre")
        .in("id", [...idsBoletasMulti]);
      setBoletaHonorarioInfoMap(
        new Map((bhsInfo || []).map((b) => [b.id, { folio: b.folio, emisor_nombre: b.emisor_nombre }]))
      );
    } else {
      setBoletaHonorarioInfoMap(new Map());
    }

    // Badge A "CapEx a cuenta": detalle de las cuotas de activo / componentes de lote que ESTE
    // movimiento saldó (modal → tab CapEx → "Saldar proyecciones", handleConfirmarCapexMultiple). El
    // vínculo es INVERSO: cada cuota/componente guarda su movimiento_bancario_id (10.190), y la
    // movimientos_caja consolidada queda anónima (entidad_nombre=null) — el "qué se pagó" NO está en
    // `m`. Se resuelve desde el subledger pagos_capex (eventos vigentes → target → concepto), MISMA
    // fuente que Futuros (10.229/10.240). Concepto reusa los helpers únicos (10.1): construirConceptoCapex (activo) y
    // construirConceptoLote (lote). Patrón silencioso-degradado: si algo falla, el badge cae al header
    // renombrado sin detalle (nunca rompe el histórico). Ver ARQUITECTURA 4.2.oo.
    try {
      const capexResumen = new Map();
      const pushCapex = (movId, linea) => {
        if (movId == null || !linea) return;
        const arr = capexResumen.get(movId) || [];
        arr.push(linea);
        capexResumen.set(movId, arr);
      };
      // CUTOVER Fase 2b: el detalle del badge se deriva del subledger `pagos_capex` (MISMA fuente que
      // Futuros → reconciliable 1:1, 10.229/10.240), no del flag `estado`. Eventos VIGENTES del país
      // (reversado_at IS NULL) dan el movimiento_bancario_id + el target; el concepto sale del target
      // (activo / lote), buscado por id. Así el badge y la proyección nunca divergen (incl. parciales F3).
      const { data: eventosCapex } = await client
        .from("pagos_capex")
        .select("movimiento_bancario_id, target_tipo, target_id")
        .eq("pais", pais)
        .is("reversado_at", null);
      const cuotaTargetIds = [
        ...new Set((eventosCapex || []).filter((e) => e.target_tipo === "cuota_activo").map((e) => e.target_id)),
      ];
      const loteTargetIds = [
        ...new Set((eventosCapex || []).filter((e) => e.target_tipo === "lote_costo").map((e) => e.target_id)),
      ];
      let cuotasRows = [];
      let lotesRows = [];
      if (cuotaTargetIds.length > 0) {
        const { data } = await client
          .from("plan_pagos_activos")
          .select(
            `id, activo_id, numero_cuota,
             activo:activos(id, tipo_activo, descripcion, destino_id, destino_tipo, proveedor:proveedores(id, nombre, alias))`
          )
          .in("id", cuotaTargetIds);
        cuotasRows = data || [];
      }
      if (loteTargetIds.length > 0) {
        const { data } = await client
          .from("lote_costos")
          .select(
            `id, tipo,
             lote:lotes_compra(cantidad_total, tipo_activo, proveedor, proveedor_rel:proveedores(id, nombre))`
          )
          .in("id", loteTargetIds);
        lotesRows = data || [];
      }
      const cuotaById = new Map(cuotasRows.map((c) => [c.id, c]));
      const loteCostoById = new Map(lotesRows.map((l) => [l.id, l]));
      // Total de cuotas por activo (cualquier estado) → "Cuota N/M" (mismo criterio que getCuotasActivosFuturas).
      // Se trae el plan COMPLETO (id + numero_cuota + fecha_vencimiento) para derivar la POSICIÓN ordinal
      // por (fecha_vencimiento, numero_cuota) — el número que se muestra, decoupled del numero_cuota crudo
      // append-only (10.1, posicionesCuotas). Nunca se muestra numero_cuota crudo.
      const activoIds = [...new Set((cuotasRows || []).map((c) => c.activo_id).filter((id) => id != null))];
      const totalPorActivo = {};
      const posPorCuotaId = new Map();
      if (activoIds.length > 0) {
        const { data: todasCuotas } = await client
          .from("plan_pagos_activos")
          .select("id, activo_id, numero_cuota, fecha_vencimiento")
          .in("activo_id", activoIds);
        const cuotasPorActivo = {};
        for (const r of todasCuotas || []) {
          totalPorActivo[r.activo_id] = (totalPorActivo[r.activo_id] || 0) + 1;
          (cuotasPorActivo[r.activo_id] = cuotasPorActivo[r.activo_id] || []).push(r);
        }
        for (const filas of Object.values(cuotasPorActivo)) {
          for (const [id, pos] of posicionesCuotas(filas)) posPorCuotaId.set(id, pos);
        }
      }
      // Nombre del edificio de destino (activos.destino_id es polimórfico, sin FK): se resuelve solo para
      // destino_tipo='edificio'. No depende de edificiosMap (que puebla cargarDatosFuturos, después).
      const destinoEdificioIds = [
        ...new Set(
          (cuotasRows || [])
            .map((c) => (c.activo?.destino_tipo === "edificio" ? c.activo?.destino_id : null))
            .filter((id) => id != null)
        ),
      ];
      const edifNombrePorId = {};
      if (destinoEdificioIds.length > 0) {
        const { data: edifs } = await client.from("edificios").select("id, edificio").in("id", destinoEdificioIds);
        for (const e of edifs || []) edifNombrePorId[e.id] = e.edificio;
      }
      for (const ev of eventosCapex || []) {
        if (ev.target_tipo === "cuota_activo") {
          const c = cuotaById.get(ev.target_id);
          const a = c?.activo;
          if (!a) continue;
          const edifNombre =
            a.destino_tipo === "edificio" && a.destino_id != null
              ? edifNombrePorId[a.destino_id] || `Edificio #${a.destino_id}`
              : null;
          const concepto = construirConceptoCapex(a, edifNombre);
          const total = totalPorActivo[c.activo_id] || 0;
          // Posición ordinal (no numero_cuota crudo). Fallback al total si faltara (defensivo, no debería).
          const posicion = posPorCuotaId.get(c.id) || null;
          const sufijo = `Cuota ${posicion || "?"}${total > 0 ? `/${total}` : ""}`;
          pushCapex(ev.movimiento_bancario_id, [concepto, sufijo].filter(Boolean).join(" · "));
        } else if (ev.target_tipo === "lote_costo") {
          const lc = loteCostoById.get(ev.target_id);
          if (!lc) continue;
          const concepto = construirConceptoLote(lc.lote);
          const sufijo = TIPO_COMPONENTE_LABEL[lc.tipo] || lc.tipo;
          pushCapex(ev.movimiento_bancario_id, [concepto, sufijo].filter(Boolean).join(" · "));
        }
      }
      setCapexResumenMap(capexResumen);
    } catch {
      setCapexResumenMap(new Map());
    }

    // Badge R "Costo recurrente": detalle de las ocurrencias de costo recurrente que ESTE movimiento
    // saldó (conciliarOcurrenciasRecurrentes → conciliacion_costo_recurrente). El vínculo es el HILO
    // ESTABLE por movimiento_bancario_id (10.240), no `m`. Una sola query por eje (10.234): las
    // conciliaciones del país acotadas a los movimientos ya cargados + resolución de nombre/concepto/
    // monto por ID (10.7), nunca del texto libre de la descripción. Patrón silencioso-degradado como
    // capex: si algo falla, el badge cae a "Compra SII"/PP sin romper el histórico.
    try {
      const recurrenteResumen = new Map();
      const concils = await getConciliacionesRecurrentes(pais);
      const movIdsSet = new Set(ordenados.map((m) => m.id));
      const concilsRel = (concils || []).filter(
        (c) => c.movimiento_bancario_id != null && movIdsSet.has(c.movimiento_bancario_id)
      );
      if (concilsRel.length > 0) {
        const provIds = [...new Set(concilsRel.map((c) => c.proveedor_id).filter((id) => id != null))];
        const costoIds = [...new Set(concilsRel.map((c) => c.costo_asociado_id).filter((id) => id != null))];
        const cajaIds = [...new Set(concilsRel.map((c) => c.movimiento_caja_id).filter((id) => id != null))];
        const [provsRes, costosRes, cajasRes] = await Promise.all([
          provIds.length > 0
            ? client.from("proveedores").select("id, nombre, alias, concepto_flujo").in("id", provIds)
            : Promise.resolve({ data: [] }),
          costoIds.length > 0
            ? client.from("proveedor_costos_asociados").select("id, concepto").in("id", costoIds)
            : Promise.resolve({ data: [] }),
          cajaIds.length > 0
            ? client.from("movimientos_caja").select("id, monto").in("id", cajaIds)
            : Promise.resolve({ data: [] }),
        ]);
        const provById = new Map((provsRes.data || []).map((p) => [p.id, p]));
        const costoById = new Map((costosRes.data || []).map((c) => [c.id, c]));
        const montoByCaja = new Map((cajasRes.data || []).map((c) => [c.id, Number(c.monto)]));
        for (const c of concilsRel) {
          const prov = provById.get(c.proveedor_id);
          const provNombre = prov ? getNombreProveedor(prov) : `Proveedor #${c.proveedor_id}`;
          // Concepto por ID (10.7), nunca del texto libre de movimientos_caja.descripcion: costo asociado
          // → proveedor_costos_asociados.concepto; línea principal (costo_asociado_id null) →
          // proveedores.concepto_flujo, con fallback a getNombreProveedor (nunca mudo, 10.177).
          const concepto =
            c.costo_asociado_id != null
              ? costoById.get(c.costo_asociado_id)?.concepto || "Costo"
              : prov?.concepto_flujo?.trim() || provNombre || "Recurrente";
          const monto =
            c.movimiento_caja_id != null && montoByCaja.has(c.movimiento_caja_id)
              ? montoByCaja.get(c.movimiento_caja_id)
              : null;
          const arr = recurrenteResumen.get(c.movimiento_bancario_id) || [];
          arr.push({
            concepto,
            periodo_mes: c.periodo_mes,
            periodo_año: c.periodo_año,
            monto,
            proveedor_nombre: provNombre,
          });
          recurrenteResumen.set(c.movimiento_bancario_id, arr);
        }
      }
      setRecurrenteResumenMap(recurrenteResumen);
    } catch {
      setRecurrenteResumenMap(new Map());
    }

    try {
      const pagosEmp = await getPagosPorMovimientos(ordenados.map((m) => m.id));
      setPagosEmpleadosRegistrados(pagosEmp);
    } catch {
      setPagosEmpleadosRegistrados([]);
    }

    // Vinculaciones forzadas por faltante (10.108 invertida): marca "forzado/pendiente" en el
    // histórico. Patrón silencioso: si la tabla no existe o falla, degrada a Map vacío (no rompe el
    // histórico). Filtrado por país (regla 8: la fila guarda el país del movimiento). Arranca vacía.
    const forzadosRows = await client
      .from("vinculaciones_forzadas")
      .select("movimiento_bancario_id, estado, faltante")
      .eq("pais", pais)
      .then((r) => r.data || [])
      .catch(() => []);
    setForzadosMap(
      new Map(forzadosRows.map((f) => [f.movimiento_bancario_id, { estado: f.estado, faltante: Number(f.faltante) }]))
    );

    setUltimaConsulta(new Date());
  }, [pais, ratesLoaded]);

  const cargarDatosFuturos = useCallback(async () => {
    // Baja el gate al ARRANQUE de cada corrida (carga inicial y todo refetch de los modales): mientras
    // esté abajo, el panel de futuros muestra "Cargando…" en vez de pintar tandas parciales. Sube recién
    // tras el último setState de la función (ver setFuturosListos(true) al final).
    setFuturosListos(false);
    if (!pais || pais === "Region" || !ratesLoaded) return;
    // UTM para la liquidación RRHH Chile (la UF ya la carga useExchangeRates). Idempotente;
    // se resuelve antes de los setState de abajo → rrhhEntries recalcula con UTM real.
    await loadUTM();
    // CUTOVER Fase 2b: saldo del subledger CapEx por target, UNA sola vez, compartido por los 3 loaders
    // (getCuotasActivosFuturas / getCostosLotesFuturos / getLotesFobSaldado) → derivan "pendiente" del
    // ledger sin N+1 (10.234).
    // Fase 3b (auditoría independiente, M1, 2026-09-25): YA NO cae a `new Map()` en el catch — un Map
    // VACÍO es indistinguible de "el libro cargó y no hay ningún evento vigente", así que TODAS las
    // cuotas (incluidas las YA PAGADAS) se veían con `_tiene_evento_vigente:false`/`_saldada:false` →
    // quedaban clickeables como si estuvieran pendientes (el bug real que motivó esta fase). `null`
    // propaga la falla explícita: `getCuotasActivosFuturas(pais,{saldosCapex})` de abajo, al recibir
    // `saldosCapex:null` (falsy), reintenta ELLA MISMA `getSaldosCapexPorTarget(pais)` una vez (mismo
    // criterio `saldosCapex || (await getSaldosCapexPorTarget(pais))` que ya trae ese archivo) — si
    // esa segunda vuelta también falla, propaga la excepción, que el catch de la línea de abajo
    // convierte en `cuotasActivos:null` (nunca `[]`) + su propio toast. `ctxReagendarFuturo.planesCargados`
    // exige `saldosCapex != null` (ver más abajo) — mientras el libro no cargó, TODO el tipo "capex"
    // (activo simple/lote/cuota) queda cerrado al reagendado por click, no solo la cuota (10.177).
    let saldosCapex = null;
    try {
      saldosCapex = await getSaldosCapexPorTarget(pais);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[FlujoCajaV2] getSaldosCapexPorTarget:", err);
      toast.error(
        "No se pudo cargar el libro de CapEx — el reagendado por click de cuotas CapEx queda deshabilitado hasta recargar."
      );
    }
    const [
      emitidas,
      pipelineData,
      recibidas,
      edificiosData,
      cuotasPagos,
      cuotasCobros,
      yaVinculadas,
      edificiosSaldoData,
      planPagosData,
      morasData,
      operacionesFactoringActivas,
      externasRes,
      proveedoresRes,
      movimientosCajaRes,
      saldosInicialesEntidades,
      cajaChicaGastosRes,
      mbfRes,
      comisionesFactoringRes,
      comisionesIngresoRes,
      cuotasCreditosData,
      moraNeteadaRes,
      f29Res,
      honorariosRes,
      rcvRes,
      overrideRes,
      cuotasActivos,
      activosConPlanData,
      boletasHonorariosRes,
      cuotasCompromiso,
      compromisosConPlanData,
      libroEmitidasRes,
      libroPagoRes,
    ] = await Promise.all([
      getFacturasEmitidas(pais, { estado: "vinculada" }),
      getPipelineCobro(pais, monedaLocal),
      getFacturasRecibidas(pais, { estado_not: "ignorada" }),
      client.from("edificios").select("id, edificio, proveedor_id").eq("pais", pais),
      getCuotasPagosFuturas(pais),
      getCuotasCobrosFuturas(pais),
      getFacturasYaVinculadas(pais),
      getEdificiosSaldoPendiente({ pais, fechaInicio: "2026-01-01", monedaLocal }),
      getPlanPagosEdificios(pais).catch(() => []),
      getMorasPendientes(pais).catch(() => []),
      // Sin filtro de estado — necesitamos también liquidadas para que mapaFactoring marque
      // con badge F los movs históricos vinculados a ops ya cerradas.
      getOperacionesFactoring(pais).catch(() => []),
      // Fuente extra de "Por pagar". El dedup contra movs bancarios pagados lo hace
      // yaVinculadas.externas abajo — acá traemos sin pagada.
      client
        .from("facturas_externas")
        .select(
          "id, proveedor_id, proveedor_nombre, concepto, fecha_emision, periodo_mes, monto_total, moneda, numero_documento, estado, plazopago"
        )
        .eq("pais", pais)
        .neq("estado", "anulada"),
      // `alias` es imprescindible: nombreProvMap resuelve el concepto con getNombreProveedor
      // (alias‖razón social). Sin traerlo, el fallback caía SIEMPRE al nombre formal (regla 10.1).
      client.from("proveedores").select("id, nombre, alias, moneda_default").eq("pais", pais),
      // Movs caja y saldos iniciales: input del ledger para calcular saldo neto por proveedor.
      // Filtro estado != "anulado" + fecha desde FECHA_CORTE replican lo que hace EstadoCuenta.
      client
        .from("movimientos_caja")
        .select(
          "id, fecha, monto, moneda, tipo, entidad_tipo, entidad_id, descripcion, numero_documento, categoria_pago, estado, movimiento_bancario_id"
        )
        .eq("pais", pais)
        .neq("estado", "anulado")
        .gte("fecha", FECHA_CORTE),
      getSaldosIniciales(pais).catch(() => []),
      // Facturas SII recibidas ya vinculadas a caja chica → se excluyen de "Por pagar".
      client.from("caja_chica_gastos").select("factura_tipo, factura_id").eq("pais", pais),
      // Vinculaciones directas pago↔factura (MBF). El ledger las usa para saldar al 100% las
      // facturas vinculadas y excluir sus pagos del FIFO (evita residuos fantasma por TC).
      client.from("movimiento_bancario_facturas").select("movimiento_bancario_id, factura_tipo, factura_id, monto"),
      // Facturas SII recibidas de comisión de factoring (vinculadas a una operación): se
      // descuentan del giro, no son deuda real → se excluyen de "Por pagar" y del ledger.
      client.from("operaciones_factoring_comisiones").select("factura_sii_recibida_id"),
      // Comisiones neteadas de un ingreso (transferencia internacional recibida): mismo tratamiento.
      client.from("comisiones_ingreso_neteadas").select("factura_sii_recibida_id"),
      // Cuotas pendientes de créditos activos → proyección en el tab "Créditos".
      getCuotasPendientesProyeccion(pais).catch(() => []),
      // Facturas SII recibidas de mora saldada por neteo → se excluyen de "Por pagar" (gate propio).
      getFacturasMoraNeteada(pais).catch(() => []),
      // Fase B2 — fuentes reales para afinar el bloque F29 del tab Impuestos (solo Chile las
      // pueblan; en Perú vienen vacías → fallback a estimación en calcularImpuestos).
      client.from("f29_declaraciones").select("*").eq("pais", pais),
      client.from("honorarios_retencion").select("*").eq("pais", pais),
      client.from("rcv_periodo").select("*").eq("pais", pais),
      client.from("impuestos_f29_override").select("*").eq("pais", pais),
      // Plan de pagos de activos CapEx: cuotas pendientes (proyección por cuota) + activos con plan.
      // Fase 3b (M1): `null` en el catch (nunca `[]`) — mismo motivo que `activosConPlanData` un poco
      // más abajo: un array vacío por fallo es indistinguible de "cargó bien, cero cuotas pendientes" y
      // el mapper (`mapCuotaActivo`) dejaría de emitir filas para TODOS los activos con plan sin avisar
      // (10.177). `setCuotasActivosPendientes` más abajo deja el estado en `null` tal cual, sin
      // fallback `|| []` que lo disfrace.
      getCuotasActivosFuturas(pais, { saldosCapex }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[FlujoCajaV2] getCuotasActivosFuturas:", err);
        toast.error(
          "No se pudieron cargar las cuotas de plan CapEx — el reagendado por click queda deshabilitado hasta recargar."
        );
        return null;
      }),
      // Fase 1c, Commit 2a: `null` en el catch (nunca un Set vacío) — un Set vacío por fallo es
      // indistinguible de "cargó bien, cero activos con plan" para `esFechaReagendable`. El toast
      // avisa explícito (10.177); `setActivosConPlan` más abajo deja el estado en `null` tal cual.
      getActivosConPlan(pais).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[FlujoCajaV2] getActivosConPlan:", err);
        toast.error(
          "No se pudo cargar el estado de planes de CapEx — el reagendado por click queda deshabilitado para esas filas."
        );
        return null;
      }),
      // Boletas de honorarios (Fase 1): deuda del proveedor en el ledger FIFO (monto_bruto). Solo
      // las que computan alimentan la deuda; el filtro `computa` vive en construirLedgerProveedor.
      // Fase 4 de "Futuros: reagendar" (2026-09-25): `plazopago` agregada al select explícito — esta
      // query NO usa `select("*")` (a diferencia de los loaders de cuotas), así que la columna nueva
      // (migración `20260925c_boletas_plazopago.sql`, APLICADA) no llega sola, hay que pedirla.
      client
        .from("boletas_honorarios")
        .select("id, proveedor_id, folio, emisor_nombre, fecha_emision, plazopago, monto_bruto, computa, pais")
        .eq("pais", pais),
      // Compromisos de pago: cuotas pendientes (proyección por cuota) + compromisos con plan.
      // Espeja getCuotasActivosFuturas / getActivosConPlan de CapEx.
      getCuotasCompromisoFuturas(pais).catch(() => []),
      // Fase 1c, Commit 2a: espejo exacto del catch de getActivosConPlan de arriba.
      getCompromisosConPlan(pais).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[FlujoCajaV2] getCompromisosConPlan:", err);
        toast.error(
          "No se pudo cargar el estado de planes de Compromisos — el reagendado por click queda deshabilitado para esas filas."
        );
        return null;
      }),
      // R2.2: libro de saldo del eje cobro (emitidas) para resolverSaldo en "Por cobrar".
      // aplicaciones_saldo NO es país-scoped (sin columna pais); se filtra por documento_tipo.
      client
        .from("aplicaciones_saldo")
        .select("documento_tipo, documento_id, via, monto_documento, moneda_origen, fecha_evento, monto, cuota_id")
        .eq("documento_tipo", "sii_emitida"),
      // R3.2: libro del eje PAGO (recibidas/externas/boletas) → el ledger de proveedor deriva su
      // saldada/saldo de resolverSaldo (regla 10.190).
      client
        .from("aplicaciones_saldo")
        .select("documento_tipo, documento_id, via, monto_documento, moneda_origen, fecha_evento")
        .in("documento_tipo", ["sii_recibida", "factura_externa", "boleta_honorario"]),
    ]);
    setPipeline(pipelineData);
    setProveedoresPendientes(recibidas);
    const ccGastos = cajaChicaGastosRes?.data || [];
    const ccSiiIds = ccGastos.filter((g) => g.factura_tipo === "sii_recibida").map((g) => g.factura_id);
    setCajaChicaFacturaIds(new Set(ccSiiIds));
    // Set de facturas de comisión de factoring (local para el ledger de abajo + state para el filtro
    // de render en proyeccionesFuturas / countPorPagarPendientes).
    // Unión de facturas neteadas (comisión de factoring + comisión de ingreso/transferencia
    // recibida): ambas se excluyen de "Por pagar" y del ledger con el mismo criterio.
    const comisionIngresoIds = (comisionesIngresoRes?.data || []).map((r) => r.factura_sii_recibida_id);
    const comisionFactoringIds = new Set([
      ...(comisionesFactoringRes?.data || []).map((r) => r.factura_sii_recibida_id),
      ...comisionIngresoIds,
    ]);
    setFacturasComisionFactoringSet(comisionFactoringIds);
    // Set de facturas de mora saldada por neteo (estado='saldada_neteo'). Se excluyen SOLO de
    // "Por pagar" vía el predicado (no del ledger/FIFO — el saldo del proveedor no se toca acá).
    const moraNeteadaIds = new Set((moraNeteadaRes || []).map((r) => r.factura_sii_recibida_id));
    setFacturasMoraNeteadaSet(moraNeteadaIds);
    // Recurrentes se calcula después del Promise.all para que pueda deduplicar contra ambas
    // fuentes: SII recibidas (proveedores chilenos) y facturas externas (extranjeros como
    // OnSign, que no tienen DTE). Sin esta dedup unificada los meses con factura externa
    // cargada duplican la proyección recurrente en "Por pagar".
    const recurrentes = await getProveedoresRecurrentesEntries(
      pais,
      monedaLocal,
      recibidas,
      externasRes.data || [],
      movimientosCajaRes.data || [],
      boletasHonorariosRes.data || [],
      // Fase 3.2 "recurrentes meses pagados": libro eje pago (ya fetcheado para el ledger de
      // proveedor, R3.2) — mismo dato, sin query nueva.
      libroPagoRes.data || []
    );
    setProveedoresRecurrentes(recurrentes);
    setImpuestosEntries(
      calcularImpuestos(
        emitidas,
        recibidas,
        pais,
        movimientosCajaRes.data || [],
        f29Res.data || [],
        honorariosRes.data || [],
        rcvRes.data || [],
        overrideRes.data || []
      )
    );
    // PREVIRED del tab RRHH (R2): subconjunto de movimientos_caja para conciliar a nivel mes.
    setMovimientosCajaCotizaciones(
      (movimientosCajaRes.data || []).filter((m) => m.categoria_pago === "cotizaciones_previsionales")
    );
    setEdificiosMap(Object.fromEntries((edificiosData.data || []).map((e) => [e.id, e.edificio])));
    setCuotasPagosPendientes(cuotasPagos || []);
    setCuotasCobrosPendientes(cuotasCobros || []);
    // Fase 3b (M1): SIN fallback a `[]` — mismo motivo que `activosConPlanData` de abajo, si la carga
    // falló `cuotasActivos` es `null` (ver el `.catch` de arriba) y así queda el estado.
    setCuotasActivosPendientes(cuotasActivos);
    // Fase 1c, Commit 2a: SIN fallback a `new Set()` — si la carga falló, `activosConPlanData` es
    // `null` (ver el `.catch` de arriba) y así queda el estado, nunca disfrazado de "cargó vacío".
    setActivosConPlan(activosConPlanData);
    // Fase 3b (M1): sincroniza el Map local (`let saldosCapex` de arriba, `null` si la carga falló) al
    // estado — lo lee `ctxReagendarFuturo.planesCargados` (abajo) y el refetch dirigido de
    // `aplicarPatchReagendado` tras reagendar una cuota (Commit 2 de esta fase).
    setSaldosCapex(saldosCapex);
    setCuotasCompromisoPendientes(cuotasCompromiso || []);
    setCompromisosConPlan(compromisosConPlanData);
    setYaVinculadasExternas(yaVinculadas.externas || new Set());
    setYaVinculadasEmitidas(yaVinculadas.emitidas || new Set());
    setEdificiosSaldo(edificiosSaldoData || []);
    setPlanPagosEdificios(planPagosData || []);
    setMorasPendientes(morasData || []);
    setCuotasCreditos(cuotasCreditosData || []);
    setOperacionesFactoring(operacionesFactoringActivas || []);
    const idsFactoring = new Set();
    // Map<movId, Array<{op, tipoVinc}>>. Un mismo movimiento bancario puede ser adelanto/excedente
    // de N operaciones (caso típico: Chita agrupa giros de varias facturas en un único depósito).
    // Lee de dos fuentes: las FKs legacy (_adelanto_id / _remanente_id) y la nueva tabla puente
    // operaciones_factoring_movimientos. Se deduplica por (movId, opId) para no contar dos veces
    // cuando una op pueblan ambos.
    const nuevoMapaFactoring = new Map();
    const pushEntry = (key, entry) => {
      idsFactoring.add(key);
      const existing = nuevoMapaFactoring.get(key) || [];
      if (existing.some((e) => e.op.id === entry.op.id && e.tipoVinc === entry.tipoVinc)) return;
      nuevoMapaFactoring.set(key, [...existing, entry]);
    };
    for (const op of operacionesFactoringActivas || []) {
      if (op.movimiento_bancario_adelanto_id) pushEntry(op.movimiento_bancario_adelanto_id, { op, tipoVinc: "Giro" });
      if (op.movimiento_bancario_remanente_id) {
        pushEntry(op.movimiento_bancario_remanente_id, { op, tipoVinc: "Excedente" });
      }
      for (const mv of op.movimientos_vinculados || []) {
        if (!mv.movimiento?.id) continue;
        const tipoVinc = mv.tipo === "adelanto" ? "Giro" : "Excedente";
        pushEntry(mv.movimiento.id, { op, tipoVinc });
      }
    }
    setMovimientosBancarioFactoring(idsFactoring);
    setMapaFactoring(nuevoMapaFactoring);

    // ─── Saldo neto pendiente por proveedor ───
    // Corre construirLedgerProveedor para cada proveedor con actividad en cualquier
    // fuente (SII recibidas + externas + movs caja). Convención del ledger:
    // saldo > 0 = el proveedor tiene saldo a su favor = le debemos → "Por pagar".
    // Epsilon 0.01 descarta saldos cuasi-cero (facturas + pagos calzando exacto).
    // USD se convierte a moneda local por fecha del item. UF rara en proveedores → identity.
    // Solo facturas desde FECHA_CORTE alimentan el ledger: las pre-corte (históricas 2025)
    // ya están incorporadas en el saldo inicial por entidad. Incluirlas las doble-contaría y
    // mis-atribuiría pagos 2026 vía FIFO a facturas viejas. Las nulas se conservan (defensivo).
    // Pieza A (regla 10.29): EXCEPCIÓN — las pre-corte con residual_corte > 0 SÍ se "despiertan" (su
    // saldo al corte, descompuesto del saldo inicial). El ledger las mete con débito = residual_corte y
    // reduce el INI en Σ(residual_corte) → total idéntico. Las demás pre-corte siguen dormidas.
    const recibidasArr = (recibidas || []).filter(
      (f) => !f.fecha_emision || f.fecha_emision >= FECHA_CORTE || Number(f.residual_corte) > 0
    );
    const externasArr = externasRes.data || [];
    // BHE (Fase 1): deuda del proveedor en el ledger. Mismo corte FECHA_CORTE que las recibidas.
    const honorariosArr = (boletasHonorariosRes.data || []).filter(
      (b) => !b.fecha_emision || b.fecha_emision >= FECHA_CORTE
    );
    // Persistir a state para la Fuente 4 de proyeccionesFuturas (el saldo lo pone el ledger vía
    // saldosPorFacturaMap). Mismo patrón raw→state que las externas / proveedoresPendientes.
    setBoletasHonorariosPendientes(honorariosArr);
    const movsCajaArr = movimientosCajaRes.data || [];
    const saldosInicArr = saldosInicialesEntidades || [];
    const proveedoresArr = proveedoresRes.data || [];
    const vinculacionesMbfArr = mbfRes.data || [];
    // Raw arrays a state — el filtrado por ya-vinculadas / estado se hace en el useMemo
    // (mismo patrón que proveedoresPendientes + yaVinculadasExternas). La query ya
    // descarta estado='anulada'.
    setFacturasExternasPendientes(externasArr);
    // Proveedores-edificio: su id está en edificios.proveedor_id. Su deuda se mide solo por la
    // comisión de sus edificios (getEdificiosSaldoPendiente), NO como obligación de proveedor.
    // Se excluyen del universo del ledger de proveedor → no aparecen en "Por pagar" ni generan
    // residuo "Sin folio". Comparación por ID (regla 8). Mientras edificios.proveedor_id esté
    // vacío el Set es vacío y nada cambia.
    const provIdsEdificio = new Set((edificiosData.data || []).map((e) => e.proveedor_id).filter((id) => id != null));
    setProvIdsEdificio(provIdsEdificio); // persist para la guarda del patch scoped (patchFuturosProveedor)
    const provIdsConActividad = new Set();
    for (const f of recibidasArr)
      if (f.proveedor_id && !provIdsEdificio.has(f.proveedor_id)) provIdsConActividad.add(f.proveedor_id);
    for (const f of externasArr)
      if (f.proveedor_id && !provIdsEdificio.has(f.proveedor_id)) provIdsConActividad.add(f.proveedor_id);
    // BHE: un proveedor con SOLO honorarios (sin SII/EXT/caja) igual debe entrar al ledger.
    for (const b of honorariosArr)
      if (b.proveedor_id && !provIdsEdificio.has(b.proveedor_id)) provIdsConActividad.add(b.proveedor_id);
    for (const m of movsCajaArr) {
      if (m.entidad_tipo === "proveedor" && m.entidad_id && !provIdsEdificio.has(m.entidad_id))
        provIdsConActividad.add(m.entidad_id);
    }
    const nombreProvMapObj = Object.fromEntries(proveedoresArr.map((p) => [p.id, getNombreProveedor(p)]));
    const tdcLocal = (monto, monedaOrigen, fecha) => {
      if (!monto) return 0;
      const origen = monedaOrigen || monedaLocal;
      if (origen === monedaLocal) return monto;
      if (origen === "USD") {
        const rate = getDolarValue(fecha, pais) || getDolarFallback(pais) || 1;
        return Math.round(monto * rate);
      }
      return monto;
    };
    // tdcUSD: simétrico — convierte local → USD dividiendo por dólar del día. Para
    // proveedores predominantemente USD genera una segunda entry en USD (sin convertir).
    const tdcUSD = (monto, monedaOrigen, fecha) => {
      if (!monto) return 0;
      const origen = monedaOrigen || monedaLocal;
      if (origen === "USD") return monto;
      if (origen === monedaLocal) {
        const rate = getDolarValue(fecha, pais) || getDolarFallback(pais) || 1;
        return rate > 0 ? Math.round(monto / rate) : 0;
      }
      return monto;
    };
    const saldosProvs = [];
    const nuevoSaldosMap = new Map();
    const nuevoProvMonedaMap = new Map();
    const residuosArr = [];
    // R3.2: contexto del libro del eje pago (una sola vez) → el ledger deriva saldada/saldo de resolverSaldo.
    const ctxLibroPago = buildContextoLibro(libroPagoRes.data || []);
    for (const provId of provIdsConActividad) {
      const saldoInicial = saldosInicArr.find((s) => s.entidad_tipo === "proveedor" && s.entidad_id === provId) || null;
      const ledgerArgs = {
        provId,
        siiRecibidas: recibidasArr,
        facturasExternas: externasArr,
        honorarios: honorariosArr,
        movimientosCaja: movsCajaArr,
        saldoInicial,
        monedaPrincipal: monedaLocal,
        vinculacionesDirectas: vinculacionesMbfArr,
        excluirProvIds: provIdsEdificio,
        facturasComisionFactoring: comisionFactoringIds,
        ctxLibro: ctxLibroPago,
      };
      const nombre = nombreProvMapObj[provId] || `Proveedor #${provId}`;
      // Determinar moneda del proveedor desde moneda_default. Fallback: monedaLocal.
      const provObj = proveedoresArr.find((p) => p.id === provId);
      const provMoneda = provObj?.moneda_default || monedaLocal;
      const esProvUSD = provMoneda === "USD";
      const tdcProv = esProvUSD ? tdcUSD : tdcLocal;
      // Guardamos la moneda en que quedan expresados los saldos de este proveedor para que
      // el consumidor en proyeccionesFuturas re-exprese al display con la moneda correcta.
      nuevoProvMonedaMap.set(provId, provMoneda);

      const { entries, saldosPorFactura } = construirLedgerProveedor({ ...ledgerArgs, toDisplayCurrency: tdcProv });
      if (entries.length === 0) continue;
      const saldo = entries[0].saldo;
      if (saldo > 0.01) {
        saldosProvs.push({ entidadId: provId, nombre, saldo, moneda: provMoneda });
      }
      // Acumular saldosPorFactura del ledger (saldoDisplay en moneda del proveedor — USD
      // para Mailhos, monedaLocal para el resto). El consumidor en useMemo aplica aDisplay
      // tratándolo como factura.moneda — válido cuando provMoneda === f.moneda (caso común).
      // La key especial "RESIDUO" sale del Map general y va a residuosArr para emitirse
      // como fila "Saldo a cuenta" separada (Fuente 3 del useMemo).
      for (const [key, { saldoDisplay }] of saldosPorFactura) {
        if (key === "RESIDUO") {
          residuosArr.push({ provId, nombre, saldoDisplay, moneda: provMoneda });
        } else {
          nuevoSaldosMap.set(key, saldoDisplay);
        }
      }
    }
    setSaldosPendientesProveedores(saldosProvs);
    setSaldosPorFacturaMap(nuevoSaldosMap);
    setProvMonedaMap(nuevoProvMonedaMap);
    setNombreProvMap(nombreProvMapObj);
    setResiduosProveedoresPendientes(residuosArr);

    // CapEx planificado — DOS fuentes desde el bloque Stock:
    //  1) activos sueltos planificados (modal Inversión por edificio, camino legacy)
    //  2) lotes_compra planificados (compra de stock) → 1 línea por lote, NO N por unidad
    // El guard `lote_id === null` en la rama de activos es red de seguridad: por el
    // invariante del bloque una unidad nunca nace `planificado`, así que hoy no filtra
    // nada (las 12 filas existentes tienen lote_id null), pero evita el doble conteo si
    // alguna vez se escribiera una unidad planificada por error.
    const [capexActivos, capexLotesData, componentesLotesData, fobSaldadoSet, compromisosData] = await Promise.all([
      getActivos(pais, { estado: "planificado" }),
      getLotes(pais, { estado: "planificado" }),
      // Componentes de costo pendientes (transporte/aduana/otro). SIN filtro de fecha → incluye
      // vencidos (10.155); se muestran en rojo en su fecha comprometida, igual que la línea FOB.
      // Auditoría 3b/3c/4, Commit 1 (2026-09-25, 10.273): `null` en el catch (nunca `[]`) — mismo
      // motivo que `getCuotasActivosFuturas`/`getActivosConPlan` más arriba (M1): un array vacío por
      // fallo es indistinguible de "cargó bien, cero componentes pendientes" y `componentesFiltrado`
      // dejaría de proyectar TODOS los componentes de costo sin avisar (10.177). `setComponentesLotes`
      // más abajo deja el estado en `null` tal cual; el useMemo de `proyeccionesFuturas` no proyecta
      // componentes mientras sea `null` (nunca los oculta disfrazados de "cero pendientes").
      getCostosLotesFuturos(pais, { saldosCapex }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[FlujoCajaV2] getCostosLotesFuturos:", err);
        toast.error(
          "No se pudieron cargar los componentes de costo CapEx (landed cost) — no se proyectan hasta recargar."
        );
        return null;
      }),
      // Lotes con FOB ya saldado → se oculta su línea FOB (CapEx-IVA Fase 5). Mismo criterio `null`
      // en el catch que arriba: un Map vacío por fallo es indistinguible de "cargó bien, ningún FOB
      // saldado" y `capexLotesFiltrado` mostraría la línea FOB de un lote YA saldado (monto fantasma).
      getLotesFobSaldado(pais, { saldosCapex }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("[FlujoCajaV2] getLotesFobSaldado:", err);
        toast.error("No se pudo cargar el saldo de FOB de CapEx — la línea de lote no se proyecta hasta recargar.");
        return null;
      }),
      // Compromisos de pago (todos los no-eliminados del país). El driver de la proyección se filtra
      // abajo a planificado + sin factura (los 'facturado' ya no proyectan).
      getCompromisos(pais).catch(() => []),
    ]);
    setCapexEntries((capexActivos || []).filter((a) => a.lote_id === null || a.lote_id === undefined));
    // Solo proyectan los compromisos planificado Y sin factura asociada (factura_sii_recibida_id null).
    setCompromisosEntries(
      (compromisosData || []).filter((c) => c.estado === "planificado" && c.factura_sii_recibida_id == null)
    );
    setCapexLotes(capexLotesData || []);
    // Commit 1: SIN `|| []` / `|| new Map()` — `null` (carga fallida) se deja tal cual, el useMemo de
    // proyeccionesFuturas lo trata explícito (no proyecta, no lo disfraza de "cero pendientes").
    setComponentesLotes(componentesLotesData);
    setLotesFobSaldado(fobSaldadoSet);
    const plazosMap = await getPlazosCobroParaSII(emitidas);
    // Cobertura de cobro REAL para "Por cobrar" (fuente de verdad: banco + factoring,
    // NO el campo manual facturafechacobro). Se arma desde datos ya cargados:
    //  - coberturaCobroMap: Σ monto de movimiento_bancario_facturas (sii_emitida) por factura.
    //    Reutiliza mbfRes (ya fetcheado) en vez de una query extra.
    //  - factoringLiquidadaIds: emitidas cobradas vía factor (op estado='liquidada'), tomando
    //    tanto la FK escalar como la tabla puente 1:N. operacionesFactoringActivas trae todas
    //    las ops (sin filtro de estado), así que no requiere query adicional.
    const coberturaCobroMap = new Map();
    (mbfRes.data || [])
      .filter((row) => row.factura_tipo === "sii_emitida")
      .forEach((row) => {
        coberturaCobroMap.set(row.factura_id, (coberturaCobroMap.get(row.factura_id) || 0) + Number(row.monto || 0));
      });
    const factoringLiquidadaIds = new Set();
    (operacionesFactoringActivas || [])
      .filter((op) => op.estado === "liquidada")
      .forEach((op) => {
        if (op.factura_sii_emitida_id) factoringLiquidadaIds.add(op.factura_sii_emitida_id);
        (op.facturas_vinculadas || []).forEach((fv) => {
          if (fv.factura_sii_emitida_id) factoringLiquidadaIds.add(fv.factura_sii_emitida_id);
        });
      });
    // Default de fecha de cobro esperada: fecha_emision + 45 días cuando no hay plazo definido.
    // Garantiza que toda emitida tenga fecha (se pinta en rojo si ya venció).
    const plazoDefault = (fechaEmision) => {
      if (!fechaEmision) return null;
      const d = new Date(`${fechaEmision}T00:00:00`);
      d.setDate(d.getDate() + 45);
      return d.toISOString().split("T")[0];
    };
    // R2.2: saldo por cobrar y "cobrada" desde el libro único (resolverSaldo) — reemplaza el
    // cálculo propio (Σ MBF coberturaCobroMap + flag factoring liquidada). coberturaMBF/
    // esFactoringLiquidada se siguen adjuntando (los usa el parche optimista y quedan de referencia),
    // pero el saldo/filtro de "Por cobrar" ya se derivan de saldoResolver/saldadaResolver.
    const ctxLibroCobro = buildContextoLibro(libroEmitidasRes.data || []);
    const emitidasConPlazos = emitidas.map((f) => {
      const key = `${f.venta_tipo}-${f.venta_ref_id}-${f.venta_ref_index ?? ""}`;
      const plazos = plazosMap.get(key) || { plazocobro: null, facturafechacobro: null };
      const rs = resolverSaldo(
        { tipo: "sii_emitida", id: f.id, monto_total: Number(f.monto_total), moneda: f.moneda },
        ctxLibroCobro
      );
      return {
        ...f,
        saldoResolver: Math.max(Number(rs.saldo || 0), 0),
        saldadaResolver: rs.saldada,
        // SII-first (mismo criterio que facturafechacobro abajo): el plazocobro de la factura SII
        // (lo que escribe PlanCuotasModal → "Ajustar fecha") manda sobre el de la venta (plazos,
        // denormalizado). Antes venta-first → el override de fecha se ignoraba en "Por cobrar".
        plazocobro: f.plazocobro ?? plazos.plazocobro ?? plazoDefault(f.fecha_emision),
        // Hubo override explícito de fecha (no es el default +45) → enciende la pill verde.
        _plazoOverride: (f.plazocobro ?? plazos.plazocobro) != null,
        // SII-first (consistente con VentasFacturas/useVFDataLoading): el facturafechacobro de la
        // factura SII (lo que escribe el cobro reconciliado) manda sobre el de la venta (plazos,
        // que sale de sales.facturafechacobro, denormalizado/manual). Antes estaba invertido.
        facturafechacobro: f.facturafechacobro ?? plazos.facturafechacobro,
        moneda_dolar_origen: plazos.moneda_dolar_origen ?? false,
        coberturaMBF: coberturaCobroMap.get(f.id) || 0,
        esFactoringLiquidada: factoringLiquidadaIds.has(f.id),
      };
    });
    setPendientesCobro(emitidasConPlazos);
    // Último setState de la corrida ya emitido → el estado del panel de futuros quedó completo y
    // consistente. Sube el gate para que el render pinte los datos (fin del "Cargando…").
    setFuturosListos(true);
  }, [pais, monedaLocal, ratesLoaded]);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    try {
      await cargarDatosHistoricos();
      await cargarDatosFuturos();
    } catch {
      toast.error("Error cargando datos");
    } finally {
      setLoading(false);
    }
  }, [cargarDatosHistoricos, cargarDatosFuturos]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  // Set de facturas con asociación CapEx activa (para el label del kebab). Fuente única compartida con
  // ComprasTab (getFacturasConAsociacionCapex, 10.1). Refetch en cada país y tras asociar/desasociar
  // (lo llama el onSuccess del modal); fallo → Set vacío (degradación a "Asociar", nunca rompe el kebab).
  const cargarAsociacionesCapex = useCallback(
    () =>
      getFacturasConAsociacionCapex(pais)
        .then(setAsociacionesCapex)
        .catch(() => setAsociacionesCapex(new Set())),
    [pais]
  );

  useEffect(() => {
    let vivo = true;
    getFacturasConAsociacionCapex(pais)
      .then((s) => vivo && setAsociacionesCapex(s))
      .catch(() => vivo && setAsociacionesCapex(new Set()));
    return () => {
      vivo = false;
    };
  }, [pais]);

  // Actualización optimista al confirmar vinculación: el modal devuelve el objeto del
  // movimiento bancario actualizado (con campos cambiados + embed de movimiento_caja)
  // y acá reemplazamos solo esa fila en el state local. Sin recargar nada más.
  // Empleado pasa _empleado=true + _pagosEmpleado (filas insertadas): el badge E depende de
  // pagosEmpleadosRegistrados (state separado), así que inyectamos esas filas a ese state
  // sin reload. Fallback a cargarDatos solo si no vinieron las filas.
  // Flujos de factoring: el payload trae _factoringOp y _factoringTipo (vincular) o
  // _desvinculaFactoring (desvincular). Mantienen sincronizados mapaFactoring y el Set
  // movimientosBancarioFactoring para que el badge F aparezca/desaparezca sin reload.
  // PASO 1 del split de D.2 "Compra de activo": marcar un CapEx planificado como ya pagado.
  // Solo cambia el estado del activo a 'ejecutado_sin_factura' → sale de la proyección (el loader
  // pide estado='planificado'). NO toca banco, NO crea caja, NO setea fecha_compra (no deprecia).
  // El cierre + factura es la Fase 3. Revert: editar el activo en GestionActivos y volver a 'planificado'.
  const handleMarcarCapexPagado = useCallback(
    async (entry) => {
      if (!entry?.activo_id) return;
      try {
        await actualizarActivo(entry.activo_id, { estado: "ejecutado_sin_factura" });
        await cargarDatosFuturos();
        toast.success("Marcado como pagado · pendiente de factura");
        setCapexPagadoModal(null);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error marcando CapEx como pagado:", err);
        toast.error("Error al marcar como pagado");
      }
    },
    [cargarDatosFuturos]
  );

  // Contexto para el despachador `reagendarEntryFuturo` (click en la celda de fecha del panel
  // Futuros, Fase 1 de "Futuros: reagendar") — mismo `pais`/`userId` que ya usa el resto del panel.
  // `planesCargados` (Fase 1c, Commit 2a; ampliado Fase 3b/M1): true SOLO si `activosConPlan`,
  // `compromisosConPlan` Y el libro CapEx (`saldosCapex`) cargaron con éxito (ninguno quedó en `null`
  // por una carga fallida) — `esFechaReagendable` lo usa para bloquear capex/compromiso mientras no se
  // sepa con certeza si la fila tiene plan o si su evento vigente en `pagos_capex` es confiable. Suma
  // `saldosCapex != null`: sin el libro, `cuota_tiene_evento_vigente` de CUALQUIER cuota CapEx no es
  // confiable (mismo motivo que activosConPlan/compromisosConPlan) — cierra TODO el tipo "capex"
  // (activo simple/lote/cuota), no solo la cuota puntual.
  const ctxReagendarFuturo = {
    pais,
    userId,
    planesCargados: activosConPlan != null && compromisosConPlan != null && saldosCapex != null,
  };

  // Patch puntual (10.234) tras un reagendado por click-en-celda: aplica EXACTAMENTE el mismo
  // setState que ya usa el modal viejo de cada tipo (ver los onSuccess/onSaved de cada
  // Modificar*/Editar*Modal más abajo) — nunca un estado paralelo, nunca un refetch completo. El
  // `_patchType` lo arma `reagendarEntryFuturo` (src/utils/reagendarEntryFuturo.js); cualquier tipo
  // no cubierto acá es un bug a corregir (10.177), nunca un no-op silencioso.
  //
  // Fase 1c, Commit 4b: el CÁLCULO de "cuál es el estado nuevo" vive en `patchReagendado` (util puro,
  // testeado aparte) — acá solo se arma el bundle de estado ACTUAL, se le pasa, y se decide qué `setX`
  // llamar comparando por REFERENCIA cada campo del resultado contra el bundle de entrada (si no
  // cambió de referencia, `patchReagendado` no lo tocó → no hace falta re-setear ese estado).
  // Trade-off documentado: antes este `useCallback` dependía SOLO de `[pais]` (usaba `setX(prev => …)`
  // funcional puro adentro); ahora necesita el VALOR actual de cada slice para pasárselo a la función
  // pura, así que depende de todos esos slices — se recrea más seguido. Es corrección, no una
  // optimización de referencia estable: `patchReagendado` necesita el estado real, no una closure
  // stale, y ningún hijo memoiza por la identidad de esta prop hoy.
  // Fase 1d (cierra la fisura ALTA de la auditoría de Fase 1c): `patchReagendado` ya no recibe un
  // snapshot de estado — devuelve `updaters` por slice (`(prev) => nuevo`) que acá se pasan DIRECTO a
  // cada `setX`. React aplica cada updater sobre el estado MÁS RECIENTE en el momento real del
  // setState (nunca sobre una copia vieja capturada en el closure del click), así que dos reagendados
  // concurrentes — o un reagendado + un refetch de `cargarDatosFuturos` completando en el medio — ya
  // no pueden pisarse entre sí. Por eso las dependencias del useCallback vuelven a ser mínimas: ya no
  // hace falta ningún slice de estado en el arreglo de deps (los `setX` de useState son estables), solo
  // `pais` (para el refetch dirigido del caso "refetch_sueldo").
  //
  // Devuelve una PROMESA (Fase 1d, Commit 2): para el camino de `updaters`, "aplicado" significa que
  // ya se LLAMÓ a cada `setX` correspondiente — no hay forma de esperar el re-render en sí sin un
  // truco que complica de más, y llamar al setter ya es "se aplicó el patch" en el sentido que le
  // importa al caller (CeldaFechaReagendable espera esto antes de mostrar el toast de éxito). Para
  // "refetch_sueldo" la promesa espera el refetch real; para "no_reconocido" rechaza (bug, 10.177).
  const aplicarPatchReagendado = useCallback(
    (resultado) => {
      const patch = patchReagendado(resultado);

      if (patch.efecto === "refetch_sueldo") {
        // Espejo de EditarFechaSueldoModal onSaved: refetch DIRIGIDO (2 queries chicas, 10.234) de
        // los overrides de fecha de sueldo — no hay id escalar simple para un patch por-fila acá (la
        // fila de sueldo depende de fechasPagoPorEmpMes, un índice derivado). La escritura en DB
        // (setFechaPagoSueldo, dentro de reagendarEntryFuturo) ya se confirmó — esto es SOLO el
        // refresh de pantalla. Si falla, la fila queda desactualizada hasta recargar: nunca
        // silencioso (10.177), avisar con un toast explícito Y rechazar (para que el caller en
        // CeldaFechaReagendable sepa que el refresco falló y no muestre el toast de éxito).
        return getFechasPagoPais(pais)
          .then((r) => setFechasPagoRRHH(r || []))
          .catch((err) => {
            toast.error("Se guardó la fecha pero no se pudo refrescar la fila; recargá el panel");
            const rechazo = new Error("refetch_sueldo falló tras guardar");
            rechazo.cause = err;
            // Marca "ya avisé": evita que CeldaFechaReagendable muestre un SEGUNDO toast genérico
            // para el mismo click — un único toast.error, nunca dos (Fase 1d, Commit 2).
            rechazo.toastYaMostrado = true;
            throw rechazo;
          });
      }
      if (patch.efecto === "refetch_cuotas_activo") {
        // Fase 3b (auditoría independiente, fisura B1, 2026-09-25): espejo del refetch dirigido del
        // `onSaved` de `PlanPagosActivoModal` (10.234/10.259, línea ~6098 más abajo) — trae TODAS las
        // cuotas del activo (con su `_posicion` recién recalculada por `getCuotasActivosFuturas`, así
        // el ordinal "Cuota N/M" de las hermanas también queda correcto, no solo la fecha de la cuota
        // movida) y reemplaza el subconjunto de `cuotasActivosPendientes` de ese `activo_id` entero.
        //
        // Auditoría 3b/3c/4, Commit 4b (2026-09-25): `saldosCapex` YA NO viaja acá — el `onSaved` de
        // `PlanPagosActivoModal` (referencia de arriba) tampoco lo pasa, deliberadamente: ese estado es
        // el snapshot del libro de la carga INICIAL del panel, potencialmente viejo para cuando este
        // refetch corre (el reagendado bien pudo ocurrir minutos después de cargar la página, o después
        // de que OTRO evento de `pagos_capex` cambiara el saldo del target). `getCuotasActivosFuturas`
        // self-fetchea un `saldosCapex` FRESCO cuando no se le pasa nada (`saldosCapex || (await
        // getSaldosCapexPorTarget(pais))`), así el `_saldoRestante`/`_tiene_evento_vigente` de las
        // cuotas recién traídas reflejan el ledger REAL al momento del refetch, no el de la carga inicial.
        const { activo_id: activoId, fecha_planificada: fechaPlanificada } = patch;
        if (activoId == null) {
          // eslint-disable-next-line no-console
          console.error("aplicarPatchReagendado: refetch_cuotas_activo sin activo_id", patch);
          return Promise.reject(new Error("aplicarPatchReagendado: refetch_cuotas_activo sin activo_id"));
        }
        return getCuotasActivosFuturas(pais, { activoId })
          .then((cuotas) => {
            setCuotasActivosPendientes((prev) => [
              ...(prev || []).filter((c) => c.activo_id !== activoId),
              ...(cuotas || []),
            ]);
            // Segundo eje (mismo criterio que antes de este rediseño): solo si la RPC devolvió una
            // fecha_planificada no nula — un `null` nunca pisa el valor real ya en pantalla (10.177).
            if (fechaPlanificada != null) {
              setCapexEntries((prev) =>
                prev.map((a) => (a.id === activoId ? { ...a, fecha_planificada: fechaPlanificada } : a))
              );
            }
          })
          .catch((err) => {
            // Mismo contrato que "refetch_sueldo": la escritura en DB ya se confirmó (la RPC corrió
            // dentro de reagendarEntryFuturo) — si el refetch falla, solo el REFRESCO visual falló.
            toast.error("Se guardó la fecha pero no se pudo refrescar el plan; recargá el panel");
            const rechazo = new Error("refetch_cuotas_activo falló tras guardar");
            rechazo.cause = err;
            rechazo.toastYaMostrado = true;
            throw rechazo;
          });
      }
      if (patch.efecto === "no_reconocido") {
        // 10.177: un _patchType desconocido es un bug (reagendarEntryFuturo devolvió algo que este
        // handler no sabe aplicar) — nunca se traga en silencio, aunque la escritura en DB ya haya
        // ocurrido con éxito. El toast (genérico, "se guardó pero no se ve") lo muestra
        // CeldaFechaReagendable al capturar el reject — acá solo se deja evidencia para devs.
        // eslint-disable-next-line no-console
        console.error("aplicarPatchReagendado: _patchType no reconocido", patch.resultado);
        return Promise.reject(new Error("patch no reconocido"));
      }

      const setters = {
        pendientesCobro: setPendientesCobro,
        proveedoresPendientes: setProveedoresPendientes,
        facturasExternasPendientes: setFacturasExternasPendientes,
        // Fase 4 de "Futuros: reagendar" (2026-09-25): case "proveedor_boleta" (patchReagendado.js).
        boletasHonorariosPendientes: setBoletasHonorariosPendientes,
        cuotasCobrosPendientes: setCuotasCobrosPendientes,
        cuotasPagosPendientes: setCuotasPagosPendientes,
        cuotasActivosPendientes: setCuotasActivosPendientes,
        capexEntries: setCapexEntries,
        componentesLotes: setComponentesLotes,
        capexLotes: setCapexLotes,
        planPagosEdificios: setPlanPagosEdificios,
        compromisosEntries: setCompromisosEntries,
        proveedoresRecurrentes: setProveedoresRecurrentes,
        pipeline: setPipeline,
      };
      // 10.177 (Fase 2, Commit 0): un slice de `patch.updaters` sin setter correspondiente en el mapa
      // de arriba es un bug — `patchReagendado` y este mapa se desincronizaron (alguien agregó un
      // `_patchType` nuevo con un slice nuevo sin sumarlo acá). Antes esto se ignoraba en silencio
      // (`if (setter) setter(updater)`); ahora rechaza explícito ANTES de aplicar nada, para no dejar
      // el patch a medias sin avisar.
      const faltantes = slicesSinSetter(patch.updaters, Object.keys(setters));
      if (faltantes.length > 0) {
        // eslint-disable-next-line no-console
        console.error("aplicarPatchReagendado: slice sin setter correspondiente", faltantes, patch);
        return Promise.reject(
          new Error(`aplicarPatchReagendado: slice sin setter correspondiente: ${faltantes.join(", ")}`)
        );
      }
      // Fase 2b, Commit 2: `patch.matchEsperado` (casos "cuota_cobro"/"cuota_pago") se evalúa contra el
      // valor REAL y actual del slice — mismo estilo declarativo de precheck-antes-de-aplicar que
      // `faltantes` arriba. Si la cuota que la escritura en DB confirmó no está en el slice local (otro
      // loader/filtro la excluyó, o un refetch la sacó en el medio), rechazar explícito en vez de
      // aplicar un `.map()` que no toca nada y dejar la UI muda sobre un guardado que sí ocurrió
      // (10.177). Por esto `cuotasCobrosPendientes`/`cuotasPagosPendientes` SÍ entran en las deps de
      // este callback (a diferencia del resto de los slices, que no necesitan leerse, solo setearse).
      // "cuota_activo" (Fase 3) YA NO usa `matchEsperado` desde el rediseño de Fase 3b/B1 — pasó a
      // `efecto: "refetch_cuotas_activo"` (manejado arriba, antes de llegar acá), así que
      // `cuotasActivosPendientes` no hace falta en este mapa ni en las deps del callback.
      const valoresSlice = { cuotasCobrosPendientes, cuotasPagosPendientes };
      if (patch.matchEsperado && !matchEsperadoCumplido(patch.matchEsperado, valoresSlice[patch.matchEsperado.slice])) {
        // eslint-disable-next-line no-console
        console.error("aplicarPatchReagendado: cuota_id ausente del slice esperado", patch.matchEsperado, patch);
        return Promise.reject(
          new Error(
            `aplicarPatchReagendado: la cuota ${patch.matchEsperado.cuota_id} no está en ${patch.matchEsperado.slice} — se guardó en la base pero no se puede reflejar en el panel; recargá.`
          )
        );
      }
      Object.entries(patch.updaters || {}).forEach(([nombreSlice, updater]) => setters[nombreSlice](updater));
      return Promise.resolve();
    },
    // Commit 4b (auditoría 3b/3c/4): `saldosCapex` YA NO es dependencia — el efecto
    // "refetch_cuotas_activo" dejó de pasarlo a `getCuotasActivosFuturas` (self-fetch fresco, ver
    // comentario de ese bloque más arriba), así que este callback ya no lee ese state en absoluto.
    [pais, cuotasCobrosPendientes, cuotasPagosPendientes]
  );

  // Guarda el override manual de PPM/IUT del contador (bloque F29). Upsert preservando la otra
  // columna (se puede overridear solo una). Comparación/escritura por ID (RUT + periodo).
  const handleGuardarOverrideF29 = useCallback(
    async (valor) => {
      if (!overrideF29Modal) return;
      const { campo, periodo } = overrideF29Modal;
      const rut = RUT_EMPRESA_POR_PAIS[pais];
      if (!rut) return;
      try {
        const { data: existente } = await client
          .from("impuestos_f29_override")
          .select("ppm, iut")
          .eq("pais", pais)
          .eq("company_rut", rut)
          .eq("periodo", periodo)
          .maybeSingle();
        const fila = {
          pais,
          company_rut: rut,
          periodo,
          ppm: existente?.ppm ?? null,
          iut: existente?.iut ?? null,
          [campo]: valor,
        };
        const { error } = await client
          .from("impuestos_f29_override")
          .upsert(fila, { onConflict: "pais,company_rut,periodo" });
        if (error) throw error;
        setOverrideF29Modal(null);
        toast.success(`${campo.toUpperCase()} ${periodo} cargado`);
        await cargarDatosFuturos();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error guardando override F29:", err);
        toast.error("Error al guardar el valor");
      }
    },
    [overrideF29Modal, pais, cargarDatosFuturos]
  );

  // ── Patch scoped del panel FUTUROS al vincular (evita el "Cargando…" + las ~30 queries) ──────
  // patchFuturosProveedor: recalcula el ledger de UN proveedor con la FUENTE ÚNICA canónica
  // (getSaldosPorFacturaProveedor → construirLedgerProveedor, regla 10.1) y parchea saldosPorFacturaMap
  // + residuos + agregado SOLO para ese proveedor. Cubre el arrastre FIFO (un pago que mueve el saldo de
  // varias facturas del mismo proveedor): getSaldosPorFacturaProveedor devuelve TODO el set del proveedor
  // y se reemplaza en bloque. Devuelve true si parcheó; false si NO puede garantizar identidad
  // dato-por-dato con cargarDatosFuturos → el caller cae a recarga completa (nunca falla en silencio,
  // 10.177). Diverge (→ false) en dos casos, porque getSaldosPorFacturaProveedor NO recibe los args extra
  // que sí usa cargarDatosFuturos: proveedor-edificio (`excluirProvIds`) y proveedor con facturas de
  // comisión de factoring (`facturasComisionFactoring`).
  const patchFuturosProveedor = useCallback(
    async (provId) => {
      if (!provId) return false;
      if (provIdsEdificio.has(provId)) {
        // eslint-disable-next-line no-console
        console.info(`[FlujoCajaV2] patch scoped omitido (proveedor-edificio #${provId}) → recarga completa`);
        return false;
      }
      const tieneComisionFactoring = proveedoresPendientes.some(
        (f) => f.proveedor_id === provId && facturasComisionFactoringSet.has(f.id)
      );
      if (tieneComisionFactoring) {
        // eslint-disable-next-line no-console
        console.info(
          `[FlujoCajaV2] patch scoped omitido (proveedor #${provId} con comisión de factoring) → recarga completa`
        );
        return false;
      }
      let saldos;
      try {
        saldos = await getSaldosPorFacturaProveedor(provId, pais);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(
          `[FlujoCajaV2] getSaldosPorFacturaProveedor(#${provId}) falló → recarga completa:`,
          err?.message || err
        );
        return false;
      }
      // Keys SII/EXT/BHE de ESTE proveedor a reemplazar en el map (el resto del map queda intacto).
      const keysProv = new Set();
      for (const f of proveedoresPendientes) if (f.proveedor_id === provId) keysProv.add(`SII:${f.id}`);
      for (const f of facturasExternasPendientes) if (f.proveedor_id === provId) keysProv.add(`EXT:${f.id}`);
      for (const b of boletasHonorariosPendientes) if (b.proveedor_id === provId) keysProv.add(`BHE:${b.id}`);
      setSaldosPorFacturaMap((prev) => {
        const next = new Map(prev);
        for (const k of keysProv) next.delete(k);
        for (const [k, v] of saldos) if (k !== "RESIDUO") next.set(k, v.saldoDisplay);
        return next;
      });
      // Residuo del proveedor (Fuente 3 del memo): reemplazar por ID (getSaldosPorFacturaProveedor
      // expresa saldoDisplay en la moneda del proveedor, misma convención que cargarDatosFuturos).
      const residuoEntry = saldos.get("RESIDUO");
      const provMoneda = provMonedaMap.get(provId) || monedaLocal;
      const nombre = nombreProvMap[provId] || `Proveedor #${provId}`;
      setResiduosProveedoresPendientes((prev) => {
        const sinProv = prev.filter((r) => r.provId !== provId);
        if (residuoEntry && residuoEntry.saldoDisplay > 0.01)
          sinProv.push({ provId, nombre, saldoDisplay: residuoEntry.saldoDisplay, moneda: provMoneda });
        return sinProv;
      });
      // Agregado por proveedor (= Σ de todo el set del ledger). Hoy no lo consume el panel, pero se
      // mantiene coherente por si un consumidor futuro lo usa (mismo criterio que cargarDatosFuturos).
      const aggregate = [...saldos.values()].reduce((acc, v) => acc + (v.saldoDisplay || 0), 0);
      setSaldosPendientesProveedores((prev) => {
        const sinProv = prev.filter((s) => s.entidadId !== provId);
        if (aggregate > 0.01) sinProv.push({ entidadId: provId, nombre, saldo: aggregate, moneda: provMoneda });
        return sinProv;
      });
      return true;
    },
    [
      provIdsEdificio,
      proveedoresPendientes,
      facturasComisionFactoringSet,
      facturasExternasPendientes,
      boletasHonorariosPendientes,
      provMonedaMap,
      nombreProvMap,
      monedaLocal,
      pais,
    ]
  );

  // patchFuturosEmitidas: lado "Por cobrar". Un cobro SII (sin cuotas tildadas) suma su MBF a la
  // cobertura de cada emitida; si la cobertura llega al total la emitida sale de "Por cobrar"
  // (yaVinculadasEmitidas, mismo criterio que getFacturasYaVinculadas: Σ MBF ≥ total), si es parcial
  // queda con su saldo (coberturaMBF, que mapFacturado descuenta). El caso con cuotas tildadas NO entra
  // acá (se cae a recarga completa desde el caller): las cuotas 'cobrado' salen de cuotasCobrosPendientes
  // y ese estado no lo parcheamos. emitidas: [{ id, monto_total, montoAplicado }].
  const patchFuturosEmitidas = useCallback(
    (emitidas) => {
      const byId = new Map((emitidas || []).map((e) => [e.id, e]));
      setPendientesCobro((prev) =>
        prev.map((f) => {
          const e = byId.get(f.id);
          if (!e) return f;
          // R2.2: el parche optimista descuenta el cobro del saldoResolver (fuente viva del display);
          // si el remanente cae dentro de la tolerancia, la factura queda saldada → sale de "Por
          // cobrar". coberturaMBF se mantiene por consistencia/referencia.
          const nuevoSaldo = Math.max(Number(f.saldoResolver || 0) - Number(e.montoAplicado || 0), 0);
          return {
            ...f,
            coberturaMBF: Number(f.coberturaMBF || 0) + Number(e.montoAplicado || 0),
            saldoResolver: nuevoSaldo,
            saldadaResolver: nuevoSaldo <= TOLERANCIA_REDONDEO_LOCAL,
          };
        })
      );
      setYaVinculadasEmitidas((prev) => {
        const next = new Set(prev);
        for (const f of pendientesCobro) {
          const e = byId.get(f.id);
          if (!e) continue;
          const nuevaCob = Number(f.coberturaMBF || 0) + Number(e.montoAplicado || 0);
          if (nuevaCob >= Number(f.monto_total || 0) - 0.01) next.add(f.id);
        }
        return next;
      });
    },
    [pendientesCobro]
  );

  // patchFuturosFactoring (Fase 2): vincular/desvincular un movimiento a una operación de factoring solo
  // cambia el FK del movimiento en la(s) op(s) (movimiento_bancario_adelanto_id / _remanente_id), sin tocar
  // la emitida (sigue cedida) ni el ledger de proveedor. Las entries giro/excedente del memo salen de
  // `operacionesFactoring`, así que basta parchear ese estado por ID de op → la entry desaparece (vincular)
  // o reaparece (desvincular). El memo lee `!!op.movimiento_bancario_adelanto_id || movimientos_vinculados…`;
  // solo tocamos el FK (idéntico a lo que escribe la DB), movimientos_vinculados queda como en el reload.
  // Devuelve true si parcheó; false si el caller debe caer a recarga completa.
  const patchFuturosFactoring = useCallback((mov) => {
    if (mov._desvinculaFactoring && mov._factoringOpId != null) {
      const campo =
        mov._factoringTipoVinc === "excedente" ? "movimiento_bancario_remanente_id" : "movimiento_bancario_adelanto_id";
      setOperacionesFactoring((prev) =>
        prev.map((op) => (op.id === mov._factoringOpId ? { ...op, [campo]: null } : op))
      );
      return true;
    }
    if (mov._factoringOpIds?.length) {
      const campo =
        mov._factoringTipoVinc === "excedente" ? "movimiento_bancario_remanente_id" : "movimiento_bancario_adelanto_id";
      const ids = new Set(mov._factoringOpIds);
      setOperacionesFactoring((prev) => prev.map((op) => (ids.has(op.id) ? { ...op, [campo]: mov.id } : op)));
      return true;
    }
    return false;
  }, []);

  const handleVincularSuccess = useCallback(
    (movActualizado) => {
      if (!movActualizado?.id) {
        // Payload sin id: pago a EDIFICIO (handleConfirmarEdificio hace onSuccess() sin datos → su saldo
        // sale de getEdificiosSaldoPendiente, otro motor) o caso degenerado. Recarga completa, con rastro
        // (10.177) — mismo criterio que las guardas de patchFuturosProveedor.
        // eslint-disable-next-line no-console
        console.info(
          "[FlujoCajaV2] recarga completa de FUTUROS (edificio / payload sin id) — path no cubierto por patch scoped"
        );
        cargarDatos();
        return;
      }

      if (movActualizado._empleado) {
        if (movActualizado._pagosEmpleado?.length) {
          setPagosEmpleadosRegistrados((prev) => [...prev, ...movActualizado._pagosEmpleado]);
          cargarDatosFuturos(); // el sueldo pagado desaparece del tab RRHH de Futuros
        } else {
          cargarDatos(); // fallback defensivo si no vinieron las filas insertadas
        }
        return;
      }

      // Desvincular pago a empleado: sacar las filas de ese movimiento del state →
      // pagosMovIds recomputa y el badge E desaparece al instante, sin reload.
      if (movActualizado._empleadoDesvinculado) {
        setPagosEmpleadosRegistrados((prev) =>
          prev.filter((p) => p.movimiento_bancario_id !== movActualizado._movimientoBancarioId)
        );
        cargarDatosFuturos(); // el sueldo vuelve a "por pagar" en el tab RRHH de Futuros
        return;
      }

      setMovBancarios((prev) =>
        prev.map((m) => {
          if (m.id !== movActualizado.id) return m;
          const merged = { ...m, ...movActualizado };
          delete merged._reload;
          delete merged._empleado;
          delete merged._factoringOp;
          delete merged._factoringTipo;
          delete merged._desvinculaFactoring;
          delete merged._scopedProveedorId;
          delete merged._scopedEmitidas;
          delete merged._scopedCuotas;
          delete merged._factoringOpIds;
          delete merged._factoringOpId;
          delete merged._factoringTipoVinc;
          return merged;
        })
      );

      // Consistencia Futuros↔Históricos (regla 10.1 / Bloque B): TODA vinculación/desvinculación tiene
      // que reflejarse en el panel de FUTUROS (el documento aparece/desaparece de "Por pagar"/"Por cobrar"
      // sin reload). Antes esto era SIEMPRE cargarDatosFuturos() → "Cargando…" + ~30 queries (~7-8 s) en
      // cada vinculación. Ahora, cuando el modal marca el alcance exacto de lo que cambió, se aplica un
      // PATCH SCOPED (idéntico dato-por-dato al reload, ver patchFuturosProveedor/Emitidas); si el patch no
      // puede garantizar identidad (proveedor-edificio / comisión factoring, o path no marcado como
      // institucional/edificio/factoring/desvinculación) → recarga completa como antes. Nunca en silencio.
      if (movActualizado._scopedProveedorId && !movActualizado._scopedCuotas) {
        // Pago/cobro a proveedor (SII pago, a cuenta pago/cobro, externas): recalcular su ledger.
        patchFuturosProveedor(movActualizado._scopedProveedorId).then((ok) => {
          if (!ok) cargarDatosFuturos();
        });
      } else if (movActualizado._scopedEmitidas && !movActualizado._scopedCuotas) {
        // Cobro SII sin cuotas: parchear cobertura de las emitidas.
        patchFuturosEmitidas(movActualizado._scopedEmitidas);
      } else if (
        movActualizado._factoringOpIds?.length ||
        (movActualizado._desvinculaFactoring && movActualizado._factoringOpId != null)
      ) {
        // Factoring (Fase 2): vincular/desvincular ajusta el FK de la(s) op(s) → entry giro/excedente
        // aparece/desaparece sin reload. Si por algo no parchea (payload incompleto) cae a recarga.
        if (!patchFuturosFactoring(movActualizado)) cargarDatosFuturos();
      } else {
        // Recarga completa (path no cubierto por patch scoped en Fase 1). Con rastro (10.177) — mismo
        // criterio que las guardas de patchFuturosProveedor. Motivo derivado de los marcadores del payload.
        const motivoFallback =
          movActualizado._factoringOp || movActualizado._desvinculaFactoring
            ? "factoring"
            : movActualizado._scopedCuotas
              ? "vinculación con cuotas tildadas"
              : movActualizado._desvinculado || movActualizado._forzadoResuelto
                ? "desvinculación / forzado"
                : "institucional u otro path no scopeado";
        // eslint-disable-next-line no-console
        console.info(
          `[FlujoCajaV2] recarga completa de FUTUROS (${motivoFallback}) — path no cubierto por patch scoped`
        );
        cargarDatosFuturos();
      }

      // Históricos: solo los casos que el merge optimista de arriba NO realínea por sí solo —
      // forzadosMap del ⚠ (desvincular / regularizar / deshacer el forzado), y los folios/razón/monto
      // de las facturas en un COBRO emitido o una COMPRA recibida MÚLTIPLE (FK escalar en null →
      // facturaInfoMap sin detalle → badge sin tooltip). Ver 7.18 / 10.66 / diagnóstico del forzado.
      // B3 Fase B paso 2: el badge de un cobro/compra SII resuelve folio/razón vía facturaInfoMap /
      // facturaRecibidaInfoMap (puente), que reconstruye cargarDatosHistoricos. Antes el SINGLE salía del
      // embed FK directa y se saltaba el reload; sin ese embed, TANTO single como multi lo necesitan →
      // se dispara por la categoría de caja, no por la FK directa (en retiro).
      const categoriaCajaVinc = movActualizado.movimiento_caja?.categoria_pago;
      const esCobroSiiEmitida = categoriaCajaVinc === "cobro_factura";
      const esCompraSiiRecibida = categoriaCajaVinc === "pago_proveedor";
      if (movActualizado._desvinculado || movActualizado._forzadoResuelto || esCobroSiiEmitida || esCompraSiiRecibida) {
        cargarDatosHistoricos();
      }

      // Fase 2b — badge "A" (CapEx a cuenta): al desvincular un pago CapEx caja-pura, sacar SU entrada
      // de capexResumenMap por mov (patch puntual, 10.234), sin recargar el histórico. La reversa (2a)
      // ya marcó reversado_at los eventos → un rebuild también lo excluiría; el patch evita el reload.
      // No-op si el mov no tenía detalle CapEx.
      if (movActualizado._capexDesvinculado) {
        setCapexResumenMap((prev) => {
          if (!prev.has(movActualizado.id)) return prev;
          const next = new Map(prev);
          next.delete(movActualizado.id);
          return next;
        });
      }

      if (movActualizado._factoringOp && movActualizado._factoringTipo) {
        setMapaFactoring((prev) => {
          const next = new Map(prev);
          const existing = next.get(movActualizado.id) || [];
          next.set(movActualizado.id, [
            ...existing,
            { op: movActualizado._factoringOp, tipoVinc: movActualizado._factoringTipo },
          ]);
          return next;
        });
        setMovimientosBancarioFactoring((prev) => {
          const next = new Set(prev);
          next.add(movActualizado.id);
          return next;
        });
      } else if (movActualizado._desvinculaFactoring) {
        setMapaFactoring((prev) => {
          const next = new Map(prev);
          next.delete(movActualizado.id);
          return next;
        });
        setMovimientosBancarioFactoring((prev) => {
          const next = new Set(prev);
          next.delete(movActualizado.id);
          return next;
        });
      }

      // Vincular factura externa (single o multi): agregar IDs al Set de dedup para
      // que la factura desaparezca al instante del panel "Por pagar" sin reload.
      if (movActualizado._externasVinculadas?.length) {
        setYaVinculadasExternas((prev) => {
          const next = new Set(prev);
          movActualizado._externasVinculadas.forEach((id) => next.add(id));
          return next;
        });
      }

      // Desvincular factura externa: inverso. Sacar IDs del Set para que reaparezca
      // en "Por pagar" sin reload.
      if (movActualizado._externasDesvinculadas?.length) {
        setYaVinculadasExternas((prev) => {
          const next = new Set(prev);
          movActualizado._externasDesvinculadas.forEach((id) => next.delete(id));
          return next;
        });
      }

      // Sin esto, el filtro #4 de proyeccionesFuturas (`saldosPorFacturaMap.get('EXT:id')
      // === undefined → continue`) descarta la factura aunque salga del Set: el map se
      // construye en cargarDatosFuturos y al momento del load la factura estaba pagada
      // (saldo 0 → no entró al map). Inyectamos su monto para que reaparezca al instante.
      if (movActualizado._externasDesvinculadasMontos?.length) {
        setSaldosPorFacturaMap((prev) => {
          const next = new Map(prev);
          movActualizado._externasDesvinculadasMontos.forEach(({ id, monto }) => {
            next.set(`EXT:${id}`, monto);
          });
          return next;
        });
      }
    },
    [
      cargarDatos,
      cargarDatosFuturos,
      cargarDatosHistoricos,
      patchFuturosProveedor,
      patchFuturosEmitidas,
      patchFuturosFactoring,
    ]
  );

  const handleSyncBanco = useCallback(async () => {
    setSincronizandoBanco(true);
    try {
      // desde = fecha del último movimiento ya registrado en movimientos_bancarios (estado
      // cargado en cargarDatosHistoricos para el país actual). Si no hay ninguno, FECHA_CORTE.
      const ultimaFecha =
        movBancarios.length > 0
          ? movBancarios.reduce((max, m) => (m.fecha && m.fecha > max ? m.fecha : max), movBancarios[0].fecha)
          : FECHA_CORTE;
      const desde = ultimaFecha;
      // `hasta` = hoy + N días. Diagnóstico (dryRun, commit 243d12b1): Clay aplica un date_to
      // IMPLÍCITO = hoy cuando NO se le pasa date_to, así que omitir `hasta` NO alcanza (corta en
      // hoy igual). La única forma comprobada de traer asientos con FECHA VALOR FUTURA (el banco
      // registra pagos fechados al próximo día hábil) es mandarle a Clay un date_to en el futuro.
      // Se construye en hora LOCAL (getFullYear/Month/Date, NO toISOString) para no reintroducir
      // el desfase UTC. desde queda igual; la capa (b) del dedup descarta los ya existentes.
      const DIAS_VENTANA_FUTURA = 7;
      const hoy = new Date();
      const hastaDate = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate() + DIAS_VENTANA_FUTURA);
      const pad = (n) => String(n).padStart(2, "0");
      const hasta = `${hastaDate.getFullYear()}-${pad(hastaDate.getMonth() + 1)}-${pad(hastaDate.getDate())}`;
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/clay-import?pais=${pais}&desde=${desde}&hasta=${hasta}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      const resultados = data.resultados || [];
      const totalNuevos = resultados.reduce((s, r) => s + (r.nuevos || 0), 0);
      const erroresReales = resultados.filter((r) => r.error && !r.error.includes("Sin movimientos"));

      if (totalNuevos > 0) {
        toast.success(
          `Banco: ${totalNuevos} movimiento${totalNuevos === 1 ? "" : "s"} nuevo${totalNuevos === 1 ? "" : "s"} · actualizado.`
        );
      } else {
        const [, mmU, ddU] = ultimaFecha.split("-");
        toast(`Sin movimientos nuevos desde el último registrado (${ddU}/${mmU}).`, { duration: 7000 });
      }
      if (erroresReales.length > 0) {
        toast.error(
          `${erroresReales.length} cuenta${erroresReales.length === 1 ? "" : "s"} con error de sincronización`
        );
      }
      await cargarDatos();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error sincronizando banco:", err);
      toast.error("Error al actualizar banco");
    } finally {
      setSincronizandoBanco(false);
    }
  }, [pais, cargarDatos, movBancarios]);

  const handleSyncSII = useCallback(async () => {
    setSincronizandoSII(true);
    const fallidos = [];
    // 1. DTEs (sii-sync) — mantiene su parsing de novedades. Su error NO aborta los demás.
    try {
      const hoy = new Date();
      const periodo = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, "0")}`;
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/sii-sync?periodo=${periodo}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          "Content-Type": "application/json",
        },
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      const nuevasEm = data.emitidas?.nuevas || 0;
      const nuevasRe = data.recibidas?.nuevas || 0;
      const totalNuevas = nuevasEm + nuevasRe;
      if (totalNuevas > 0) {
        toast.success(
          `${enteFiscal} (${periodo}): ${nuevasEm} emitida${nuevasEm === 1 ? "" : "s"} + ${nuevasRe} recibida${nuevasRe === 1 ? "" : "s"} nuevas`
        );
      } else {
        toast(
          `${enteFiscal} sin novedades para ${periodo}. ${data.clay_total || 0} DTEs revisados, todos ya estaban.`,
          {
            duration: 6000,
          }
        );
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Error sincronizando SII:", err);
      fallidos.push(enteFiscal);
    }

    // 2. Fuentes del bloque F29 (solo Chile): F29, honorarios y RCV en paralelo. Cada uno maneja
    // su error de forma independiente (allSettled) → si uno falla, los demás siguen y se avisa cuál.
    if (pais === "Chile") {
      const extras = [
        ["f29-sync", "F29"],
        ["honorarios-sync", "Honorarios"],
        ["rcv-sync", "RCV"],
      ];
      const resultados = await Promise.allSettled(extras.map(([fn]) => invocarEdgeSync(fn)));
      resultados.forEach((r, idx) => {
        if (r.status === "rejected") {
          // eslint-disable-next-line no-console
          console.error(`Error en ${extras[idx][0]}:`, r.reason);
          fallidos.push(extras[idx][1]);
        }
      });
    }

    if (fallidos.length > 0) {
      toast.error(`Sync con error: ${fallidos.join(", ")}`);
    }

    try {
      await cargarDatos();
    } finally {
      setSincronizandoSII(false);
    }
  }, [cargarDatos, enteFiscal, pais]);

  // Moneda en la que se muestra todo. Independiente de qué cuentas se incluyen
  // (eso lo controla `consolidado`). `convertirAUSD` solo aplica cuando estás en
  // la pestaña local — en USD ya está en dólares.
  const monedaDisplay = monedaVista === "USD" || convertirAUSD ? "USD" : monedaLocal;

  // Convierte un monto entre USD/UF ↔ local usando la cotización de la fecha provista.
  // Para fechas futuras, getDolarValue/getUFValue caen en fallback a la fecha anterior más
  // cercana (la última cotización conocida), que es exactamente lo que queremos para proyecciones.
  // Estrategia: normalizar el origen a moneda LOCAL (CLP/PEN) por fecha, luego llevar al destino.
  //   · USD → local: n * dólar(fecha)
  //   · UF  → local: n * UF(fecha)   (UF es Chile-only; getUFValue con fallback a última UF)
  //   · local (CLP/PEN): identidad
  // USD↔local queda bit-idéntico a antes; solo se suma el camino UF (regla 10.185).
  const convertir = useCallback(
    (monto, monedaOrigen, monedaDestino, fecha) => {
      const n = Number(monto || 0);
      if (!n || monedaOrigen === monedaDestino) return n;
      const tasa = getDolarValue(fecha, pais) || getDolarFallback(pais) || 1;
      let enLocal;
      if (monedaOrigen === "USD") enLocal = n * tasa;
      else if (monedaOrigen === "UF") enLocal = n * getUFValue(fecha);
      else enLocal = n; // ya en moneda local (CLP/PEN)
      return monedaDestino === "USD" ? Math.round(enLocal / tasa) : Math.round(enLocal);
    },
    [pais]
  );

  // Saldo derivado de una ancla histórica conocida (saldos_iniciales_cuentas) más la
  // suma de todos los movimientos posteriores. No depende de cuentas_bancarias.saldo_actual,
  // que tiene desfase entre el endpoint de saldo y el de movimientos en Clay.
  const calcularSaldoConAncla = useCallback(
    (moneda) => {
      const ancla = saldosIniciales.find((s) => s.moneda === moneda);
      if (!ancla) return 0;
      const ajuste = movBancarios.reduce((sum, m) => {
        if (m.cuenta_bancaria_id !== ancla.cuenta_bancaria_id) return sum;
        if (!m.fecha || m.fecha <= ancla.fecha) return sum;
        const monto = Number(m.monto || 0);
        return sum + (m.tipo === "ingreso" ? monto : -monto);
      }, 0);
      return Number(ancla.monto) + ajuste;
    },
    [saldosIniciales, movBancarios]
  );

  const saldoActualLocal = useMemo(() => calcularSaldoConAncla(monedaLocal), [calcularSaldoConAncla, monedaLocal]);

  const saldoActualUSD = useMemo(() => calcularSaldoConAncla("USD"), [calcularSaldoConAncla]);

  // Saldo expresado en monedaDisplay. Si consolidado, suma ambas cuentas (la otra
  // se convierte con la cotización de hoy → fallback a la última conocida).
  const hoyStr = new Date().toISOString().split("T")[0];
  const saldoActual = useMemo(() => {
    const localEnDisplay = convertir(saldoActualLocal, monedaLocal, monedaDisplay, hoyStr);
    const usdEnDisplay = convertir(saldoActualUSD, "USD", monedaDisplay, hoyStr);
    if (consolidado) return localEnDisplay + usdEnDisplay;
    return monedaVista === "USD" ? usdEnDisplay : localEnDisplay;
  }, [saldoActualLocal, saldoActualUSD, monedaLocal, monedaDisplay, consolidado, monedaVista, convertir, hoyStr]);

  const rangosFecha = useMemo(() => {
    // Todas las cotas se serializan con toLocalYMD (fecha LOCAL), NO toISOString()/UTC. Así "Hoy"
    // matchea la fecha local real (ej. 24/07 a las 20:11 de Chile) y desde/hasta quedan consistentes.
    // No cambia QUÉ cubre cada rango (lunes de semana, 1° de mes, 1-ene de YTD) — solo la zona.
    const hoy = new Date();
    const hoyStr = toLocalYMD(hoy);
    const diaSemana = hoy.getDay() === 0 ? 7 : hoy.getDay();
    const lunes = new Date(hoy);
    lunes.setDate(hoy.getDate() - diaSemana + 1);
    // YTD llega hasta el ÚLTIMO asiento real de la cartola, no hasta hoy: los bancarios con
    // FECHA VALOR FUTURA (el banco los registra fechados al próximo día hábil) ya están en
    // movBancarios y ya suman en saldoActual (calcularSaldoConAncla, sin tope superior). Sin
    // este max, el filtro 1807 los descartaba y la fila tope (hoy) heredaba saldoActual → saldo
    // inflado por el net de las futuras. Con el max se listan arriba y el walk-back del Modo B
    // cuadra peso a peso. Hoy/Semana/Mes siguen capando en hoy (usan Modo A / acumulador).
    const maxFechaMov =
      movBancarios.length > 0
        ? movBancarios.reduce((max, m) => (m.fecha && m.fecha > max ? m.fecha : max), movBancarios[0].fecha)
        : hoyStr;
    const ytdHasta = maxFechaMov > hoyStr ? maxFechaMov : hoyStr;
    return {
      hoy: { desde: hoyStr, hasta: hoyStr },
      esta_semana: { desde: toLocalYMD(lunes), hasta: hoyStr },
      este_mes: { desde: toLocalYMD(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), hasta: hoyStr },
      ytd: { desde: toLocalYMD(new Date(hoy.getFullYear(), 0, 1)), hasta: ytdHasta },
    };
  }, [movBancarios]);

  // Lista de meses para el dropdown: desde el mes más antiguo con movimientos cargados
  // (o enero 2026 como fallback) hasta el mes anterior al actual. El mes en curso tiene
  // su propio botón "Este mes". El límite es dinámico para que escale conforme se cargan
  // datos históricos más antiguos.
  const mesesDropdown = useMemo(() => {
    const opciones = [];
    const hoy = new Date();
    const fechaMasAntigua =
      movBancarios.length > 0
        ? movBancarios.reduce((min, m) => (m.fecha && m.fecha < min ? m.fecha : min), movBancarios[0].fecha)
        : "2026-01-01";
    const [yLim, mLim] = fechaMasAntigua.split("-").map(Number);
    const limite = new Date(yLim, mLim - 1, 1);

    let cursor = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    while (cursor >= limite) {
      const y = cursor.getFullYear();
      const m = cursor.getMonth();
      const mm = String(m + 1).padStart(2, "0");
      const labelMes = cursor.toLocaleDateString("es", { month: "long" }).replace(/^\w/, (c) => c.toUpperCase());
      opciones.push({
        key: `mes_${y}-${mm}`,
        label: `${labelMes} ${y}`,
        desde: `${y}-${mm}-01`,
        hasta: new Date(y, m + 1, 0).toISOString().split("T")[0],
      });
      cursor = new Date(y, m - 1, 1);
    }
    return opciones;
  }, [movBancarios]);

  // Base del histórico: filtros de la barra (cuenta/moneda + tipo + fecha + Pendiente + búsqueda),
  // SIN el toggle de atención (⚠) y SIN el cálculo de saldo. Se comparte entre movimientosFiltrados
  // y el enabled/disabled del botón de atención (que mira si hay forzados en ESTA vista base).
  const movimientosBaseHistorico = useMemo(() => {
    const cuentaIdsValidos = consolidado
      ? new Set(cuentas.map((c) => c.id))
      : new Set(cuentas.filter((c) => c.moneda === monedaVista).map((c) => c.id));
    let lista = movBancarios.filter((m) => cuentaIdsValidos.has(m.cuenta_bancaria_id));
    if (filtroHistorico === "ingresos") lista = lista.filter(esMovIngreso);
    if (filtroHistorico === "egresos") lista = lista.filter(esMovEgreso);
    const rango =
      filtroTiempo === "hoy"
        ? rangosFecha.hoy
        : filtroTiempo === "esta_semana"
          ? rangosFecha.esta_semana
          : filtroTiempo === "este_mes"
            ? rangosFecha.este_mes
            : filtroTiempo === "ytd"
              ? rangosFecha.ytd
              : mesesDropdown.find((m) => m.key === filtroTiempo);
    if (rango) lista = lista.filter((m) => m.fecha >= rango.desde && m.fecha <= rango.hasta);
    // Mismo predicado y mismos sets que el tinte naranja (fila) y el badge → fuente única.
    if (soloPendientes)
      lista = lista.filter((m) => isMovPendiente(m, pagosMovIds, movimientosBancarioFactoring, movsVinculadosLibro));
    if (busquedaHistorico.trim()) {
      const q = busquedaHistorico.trim().toLowerCase();
      lista = lista.filter(
        (m) =>
          (m.descripcion_raw || "").toLowerCase().includes(q) ||
          String(Math.abs(Math.round(Number(m.monto || 0)))).includes(q)
      );
    }
    return lista;
  }, [
    movBancarios,
    cuentas,
    monedaVista,
    consolidado,
    filtroHistorico,
    filtroTiempo,
    rangosFecha,
    mesesDropdown,
    soloPendientes,
    pagosMovIds,
    movimientosBancarioFactoring,
    movsVinculadosLibro,
    busquedaHistorico,
  ]);

  // ¿Hay algún movimiento con forzado pendiente en la vista base (antes del toggle de atención)?
  // Gobierna el enabled/disabled del botón ⚠: solo tiene sentido filtrar si hay algo que mostrar.
  const hayAtencionEnVista = useMemo(
    () => movimientosBaseHistorico.some((m) => forzadosMap.get(m.id)?.estado === "pendiente"),
    [movimientosBaseHistorico, forzadosMap]
  );

  // Auto-clear: si el toggle de atención está activo y la vista base deja de tener forzados
  // pendientes (cambió fecha/tipo/búsqueda), apagarlo para no quedar con la lista vacía.
  useEffect(() => {
    if (soloAtencion && !hayAtencionEnVista) setSoloAtencion(false);
  }, [soloAtencion, hayAtencionEnVista]);

  const movimientosFiltrados = useMemo(() => {
    // Mapa cuentaId → moneda de la cuenta (para el enriquecido).
    const cuentaMoneda = Object.fromEntries(cuentas.map((c) => [c.id, c.moneda]));
    // Toggle de atención (⚠): filtro AND independiente que se apila sobre la vista base.
    const lista = soloAtencion
      ? movimientosBaseHistorico.filter((m) => forzadosMap.get(m.id)?.estado === "pendiente")
      : movimientosBaseHistorico;
    // Enriquecemos primero (monto convertido + moneda origen). El cálculo del saldo
    // depende de si hay algún filtro activo.
    const enriched = lista.map((m) => {
      const monedaOrigen = cuentaMoneda[m.cuenta_bancaria_id] || monedaLocal;
      const montoEnDisplay = convertir(Number(m.monto || 0), monedaOrigen, monedaDisplay, m.fecha);
      return { ...m, monto_display: montoEnDisplay, moneda_origen: monedaOrigen };
    });

    const hayFiltroActivo =
      busquedaHistorico.trim() !== "" ||
      filtroHistorico !== "todo" ||
      filtroTiempo !== "ytd" ||
      soloPendientes ||
      soloAtencion;

    if (hayFiltroActivo) {
      // Con filtro activo el saldo "real" de cada fila sería inconsistente: la lista
      // oculta movs intermedios, así que caminar hacia atrás desde saldoActual da
      // valores que no coinciden con cartola. En su lugar mostramos el acumulado
      // running de la selección, partiendo de 0 en el más antiguo (sirve como
      // "¿cuánto suman los movs visibles hasta acá?"). La lista entra en orden DESC,
      // calculamos en ASC y devolvemos en DESC para no alterar el render.
      const asc = [...enriched].reverse();
      let acc = 0;
      const ascConSaldo = asc.map((m) => {
        acc += m.tipo === "ingreso" ? m.monto_display : -m.monto_display;
        return { ...m, saldo_calculado: acc };
      });
      return ascConSaldo.reverse();
    }

    // Sin filtro activo: saldo "real" — partir del saldo de ahora y deshacer hacia atrás.
    let saldo = saldoActual;
    return enriched.map((m) => {
      const entry = { ...m, saldo_calculado: saldo };
      if (m.tipo === "ingreso") saldo -= m.monto_display;
      else saldo += m.monto_display;
      return entry;
    });
  }, [
    movimientosBaseHistorico,
    cuentas,
    monedaLocal,
    monedaDisplay,
    saldoActual,
    convertir,
    soloAtencion,
    forzadosMap,
    busquedaHistorico,
    filtroHistorico,
    filtroTiempo,
    soloPendientes,
  ]);

  // Predicado único de inclusión a nivel FACTURA para "Por pagar" (Fuente 1, SII recibidas).
  // Fuente de verdad compartida entre el render de filas (proyeccionesFuturas) y el badge
  // (countPorPagarPendientes): mientras ambos lo usen, no pueden divergir ante un nuevo gate
  // (regla 10.1). NO gatea por estado_vinculacion — una factura "vinculada" pagada solo
  // parcialmente conserva saldo FIFO y debe contar/mostrarse; la exclusión de pagadas la hace
  // el FIFO. Excluye: históricas pre-corte, NC (tipo_dte=61, regla 10.42), caja chica (10.39),
  // comisiones factoring/recepción neteadas, mora saldada por neteo, y facturas saldadas al 100%
  // por el ledger FIFO.
  // Predicado extraído a util compartido (Fase 4, regla 10.1): mismo criterio que consume el
  // contador de EstadoCuenta. El useCallback envuelve la llamada para preservar deps/memoización.
  // Facturas anuladas por NC 'anula_error' (regla 10.222): fuera de "Por pagar". Se arma sobre
  // proveedoresPendientes (trae las NC, tipo_dte=61, y sus documento_referenciado_id vía select("*")).
  const facturasAnuladasAnulaError = useMemo(
    () => construirExclusionNcAnulaError(proveedoresPendientes).facturasAnuladas,
    [proveedoresPendientes]
  );
  const facturaSiiPorPagar = useCallback(
    (f) =>
      facturaSiiPorPagarUtil(f, {
        cajaChicaFacturaIds,
        facturasComisionFactoringSet,
        facturasMoraNeteadaSet,
        saldosPorFacturaMap,
        FECHA_CORTE,
        facturasAnuladasAnulaError,
      }),
    [
      cajaChicaFacturaIds,
      facturasComisionFactoringSet,
      facturasMoraNeteadaSet,
      saldosPorFacturaMap,
      facturasAnuladasAnulaError,
    ]
  );

  const proyeccionesFuturas = useMemo(() => {
    const hoy = new Date().toISOString().split("T")[0];
    // Techo por defecto cuando NO hay filtro temporal activo: 31/12 del año siguiente.
    // Acota las fuentes que proyectan sin límite (créditos, cartelería) a la ventana visible.
    const FECHA_TECHO_DEFAULT = `${new Date().getFullYear() + 1}-12-31`;
    // Filtro temporal: si está activo, calcular la fecha de corte como string YYYY-MM-DD
    // para comparar lex contra entry.fecha_display (también YYYY-MM-DD).
    const fechaCorte = filtroTemporalKey
      ? calcularFiltrosTemporales().find((f) => f.key === filtroTemporalKey)?.fechaCorte
      : null;
    const fechaCorteStr = fechaCorte
      ? `${fechaCorte.getFullYear()}-${String(fechaCorte.getMonth() + 1).padStart(2, "0")}-${String(fechaCorte.getDate()).padStart(2, "0")}`
      : null;
    // Rango personalizado (desde/hasta): piso + techo sobre el MISMO campo fecha_display que
    // usan los cortes fijos. Es excluyente con filtroTemporalKey (elegir uno limpia el otro),
    // pero si ambos estuvieran seteados, el rango tiene precedencia.
    const rangoDesde = rangoPersonalizado?.desde || null;
    const rangoHasta = rangoPersonalizado?.hasta || null;
    // Con corte temporal (o rango) activo: filtro estricto, las entries sin fecha (fecha_display
    // null) quedan fuera — no podemos ubicarlas antes ni después del corte con certeza.
    // Sin corte activo: techo por defecto (31/12 año siguiente) y las entries sin fecha
    // SÍ pasan (son obligaciones sin fecha definida que deben verse en la vista completa).
    const dentroDeCorte = (e) => {
      if (rangoDesde || rangoHasta) {
        return !!e.fecha_display && e.fecha_display >= rangoDesde && e.fecha_display <= rangoHasta;
      }
      return fechaCorteStr
        ? !!e.fecha_display && e.fecha_display <= fechaCorteStr
        : !e.fecha_display || e.fecha_display <= FECHA_TECHO_DEFAULT;
    };
    const filtrarPorCorte = (arr) => arr.filter(dentroDeCorte);
    const mostrarTodo = filtrosActivos.includes("todo");
    const mostrarFacturado = mostrarTodo || filtrosActivos.includes("cobros");
    const mostrarBacklog = mostrarTodo || filtrosActivos.includes("backlog");
    const mostrarProveedoresRecurrentes = mostrarTodo || filtrosActivos.includes("proveedores_recurrentes");
    const mostrarProveedoresFact = mostrarTodo || filtrosActivos.includes("proveedores_facturados");
    const mostrarRRHH = mostrarTodo || filtrosActivos.includes("rrhh");
    const mostrarImpuestos = mostrarTodo || filtrosActivos.includes("impuestos");
    const esUSD = monedaVista === "USD";
    // Cuándo incluir items USD vs local. Si consolidado, ambos. Si no, solo los de la moneda activa.
    const incluirUSD = consolidado || esUSD;
    const incluirLocal = consolidado || !esUSD;

    // Convierte un importe en moneda origen a la moneda de display, usando la fecha del item.
    const aDisplay = (monto, monedaOrig, fecha) => convertir(monto, monedaOrig, monedaDisplay, fecha);

    const mapFacturado = (f) => {
      // monto_total siempre está en moneda local (SII reporta en CLP/PEN, también
      // para facturas con origen USD). moneda_origen es la moneda "natural" de la
      // operación desde la perspectiva del usuario y se usa para badge/filtro.
      const monedaOrig = f.moneda_dolar_origen ? "USD" : monedaLocal;
      // R2.2: el saldo por cobrar sale DIRECTO del libro único (resolverSaldo.saldo, ya adjuntado como
      // saldoResolver). resolverSaldo ya suma MBF + factoring y aplica su tolerancia (una factura
      // saldada no llega acá — la excluye el filtro por saldadaResolver). Reemplaza el cómputo propio
      // Σ MBF (monto_total − coberturaMBF) + el clamp de miga. monto_total real intacto; saldoResolver
      // está en la moneda del documento (local para SII).
      const saldoPorCobrar = Number(f.saldoResolver || 0);
      const esParcial = saldoPorCobrar > 0.01 && saldoPorCobrar < Number(f.monto_total || 0) - 0.01;
      const importeEnMoneda = aDisplay(saldoPorCobrar, monedaLocal, f.fecha_emision);
      return {
        ...f,
        fecha_display: f.plazocobro || null,
        importeEnMoneda,
        moneda_origen: monedaOrig,
        tipo: "facturado",
        folio: f.folio,
        _parcial: esParcial,
        // Pill verde: la fecha de cobro fue overrideada a mano (no es el default +45 días).
        tiene_override: f._plazoOverride,
        vencida: esVencida(f.plazocobro, hoy),
      };
    };
    const mapPipeline = (p) => {
      // pipeline trae importe (USD si moneda_dolar=true) y importeCLP (local).
      const monedaOrig = p.moneda_dolar ? "USD" : monedaLocal;
      const importeOrig = p.moneda_dolar ? p.importe : (p.importeCLP ?? p.importe);
      return {
        ...p,
        fecha_display: p.fecha_estimada,
        importeEnMoneda: aDisplay(Number(importeOrig || 0), monedaOrig, p.fecha_estimada),
        moneda_origen: monedaOrig,
        razon_social_receptor: p.cliente,
        tipo: "pipeline",
        // Vencida: fecha proyectada ya pasada (mismo criterio que mapFactura, regla 10.68).
        // El Backlog ya no descarta vencidas; se muestran con la fecha en rojo en el render.
        vencida: esVencida(p.fecha_estimada, hoy),
      };
    };
    // Fuente de verdad de "cobrada": respaldo bancario (MBF) o factoring liquidada — NO el
    // campo manual facturafechacobro (se setea desde múltiples vías sin respaldo, p.ej.
    // marcarFacturaCobrada de FlujoCaja V1). Las vencidas no cobradas SÍ son por cobrar.
    const facturadoFiltrado = pendientesCobro
      .filter((f) => {
        // R2.2: cobrada = saldada según el libro único (resolverSaldo). Unifica lo que antes eran dos
        // criterios propios: esFactoringLiquidada (factoring) + yaVinculadasEmitidas (Σ MBF ≥ total /
        // cuotas / FK). Las parciales (saldadaResolver=false, saldo>0) siguen acá con su saldo.
        if (f.saldadaResolver) return false;
        return true; // pendiente total o parcial (incluye vencidas)
      })
      // Regla de CAJA, no de saldo (10.2/10.5, 10.263). El factoring es CON RECURSO: en el EJE DE SALDO
      // la cedida sigue pendiente hasta liquidar (resolverSaldo no la da cobrada — correcto). PERO en el
      // EJE DE CAJA el adelanto del factor YA entró al banco como movimiento real; proyectar además la
      // factura en "Por cobrar" la contaría DOS VECES en el flujo. Por eso "Por cobrar" (proyección de
      // caja) excluye la cedida sin liquidar. NO es info que se pueda mostrar como pendiente: es doble
      // conteo. (Revert de Fase 3.2a, 2026-09-19 — mezclé los dos ejes.)
      .filter((f) => f.estado_factoring !== "cedida")
      .filter((f) => (f.moneda_dolar_origen === true ? incluirUSD : incluirLocal));
    // Para cada factura emitida, verificar si tiene cuotas de cobro
    // Fase 1b (10.244): SIEMPRE se computa completo, sin gate por categoría — el gate
    // (`mostrarFacturado`) se aplica recién al armar `todos` más abajo. Así el pool para sticky
    // (más abajo) es una derivación pura de la fuente viva, nunca una copia paralela que pueda
    // quedar stale.
    const facturadoEntries = [];
    for (const f of facturadoFiltrado) {
      // monto_cuota está en la misma moneda que monto_total (CLP/PEN). monedaOrig
      // refleja la moneda "natural" de la operación para badge/filtro.
      const monedaOrig = f.moneda_dolar_origen ? "USD" : monedaLocal;
      const cuotasFactura = cuotasCobrosPendientes.filter((c) => c.factura_sii_emitida_id === f.id);
      if (cuotasFactura.length > 0) {
        cuotasFactura.forEach((c) => {
          facturadoEntries.push({
            ...f,
            fecha_display: c.fecha_vencimiento,
            importeEnMoneda: aDisplay(Number(c.monto_cuota), monedaLocal, c.fecha_vencimiento),
            moneda_origen: monedaOrig,
            razon_social_receptor: f.razon_social_receptor,
            tipo: "facturado",
            folio: f.folio,
            cuota_actual: c.numero_cuota,
            // Identidad de la cuota (Fase 2, Commit 1 de "Futuros: reagendar" — reagendar POR
            // cuota, no por factura): `c.id`/`c.estado`/`c.movimiento_bancario_id` ya vienen en el
            // select("*") de getCuotasCobrosFuturas, sin cambios de query. `esFechaReagendable`
            // (reagendarEntryFuturo.js) los usa para decidir si esta fila puntual se puede mover;
            // `guardarFechaCuotaFactura` los relee de DB antes de escribir (nunca confía en esto).
            cuota_id: c.id,
            cuota_estado: c.estado,
            cuota_movimiento_bancario_id: c.movimiento_bancario_id,
            cuota_tabla: "plan_cobros_facturas",
            // Fase 2d (2026-09-24, cierre de fisuras de la auditoría de 2b+2c): `cuota_tiene_eventos`
            // = `c._tiene_eventos`, YA calculado por `getCuotasCobrosFuturas` (EXISTS puro sobre el
            // MISMO array de eventos del libro — ¿esta cuota_id tiene AL MENOS una fila?, sin
            // importar el monto; reemplaza el `cuota_aplicado`/Σ-con-tolerancia de la Fase 2b, único
            // consumidor, sin otros lectores); `cuota_documento_tipo` fijo (esta rama es siempre
            // cobro de SII emitida). Los consume `esCuotaReagendable` (cuotaReagendable.js) vía
            // `esFechaReagendable`/reagendarEntryFuturo.js — fuente única con el writer y con la RPC.
            cuota_tiene_eventos: c._tiene_eventos,
            cuota_documento_tipo: "sii_emitida",
            // M = total del plan (todas las cuotas), NO solo las pendientes — viene enriquecido
            // desde getCuotasCobrosFuturas. Fallback a cuotasFactura.length por robustez.
            total_cuotas: c.total_cuotas > 0 ? c.total_cuotas : cuotasFactura.length,
            // Fase 3c (2026-09-25, regla de negocio definida por Ignacio): `tiene_override` YA NO es
            // `true` fijo — se deriva de `fecha_vencimiento_original` (columna poblada por el
            // trigger de la migración 20260925b, aplicada) vs la fecha ACTUAL, vía el predicado
            // único `esFechaModificada` (fechasFuturosPanel.js, reusado por los 5 mappers de cuota).
            // `fecha_original_plan` viaja para el hover de la pill azul (`tituloOverride`, render).
            tiene_override: esFechaModificada(c.fecha_vencimiento_original, c.fecha_vencimiento),
            fecha_original_plan: c.fecha_vencimiento_original || null,
            // Cuota comprometida ya vencida → texto de fecha en rojo (reusa el flag `vencida`
            // del render, mismo criterio string que la rama sin-cuotas en línea ~1697).
            vencida: esVencida(c.fecha_vencimiento, hoy),
          });
        });
      } else {
        const entry = mapFacturado(f);
        // La tolerancia de redondeo puede dejar importe 0 (miga cobrada) → no proyectar la fila.
        if (entry.importeEnMoneda > 0) facturadoEntries.push(entry);
      }
    }

    // Facturas cedidas al factoring — dos entries por operación: giro a recibir
    // (anticipo neto de costos en fecha_anticipo) y excedente (en fecha_cobro_final).
    // Cada entry desaparece cuando el movimiento bancario correspondiente queda vinculado
    // (movimiento_bancario_adelanto_id / movimiento_bancario_remanente_id no nulos).
    const factoringEntries = [];
    // Fase 1b (10.244): siempre completo, sin gate (ver comentario espejo en facturadoEntries).
    // Las proyecciones futuras solo se generan para ops activas. Liquidadas / anuladas
    // viven en el state para que el panel histórico pueda marcar sus movs con badge F.
    for (const op of operacionesFactoring.filter((o) => o.estado === "activa")) {
      const proveedorFactoring = getNombreProveedor(op.proveedor) || "Factoring";
      const cliente = op.factura?.razon_social_receptor || "";
      const tieneAdelantoVinc =
        !!op.movimiento_bancario_adelanto_id || (op.movimientos_vinculados || []).some((mv) => mv.tipo === "adelanto");
      const tieneRemanenteVinc =
        !!op.movimiento_bancario_remanente_id ||
        (op.movimientos_vinculados || []).some((mv) => mv.tipo === "remanente");
      if (op.giro_a_recibir > 0 && !tieneAdelantoVinc) {
        const fechaGiro = op.fecha_anticipo || op.fecha_operacion;
        factoringEntries.push({
          id: `factoring-giro-${op.id}`,
          factura_sii_emitida_id: op.factura_sii_emitida_id,
          fecha_display: fechaGiro,
          importeEnMoneda: aDisplay(Number(op.giro_a_recibir), monedaLocal, fechaGiro),
          moneda_origen: monedaLocal,
          moneda_dolar_origen: false,
          razon_social_receptor: `Fact. ${proveedorFactoring} | ${cliente}`,
          tipo: "facturado",
          folio: op.factura?.folio || null,
          es_factoring: true,
        });
      }
      const remanente = op.monto_remanente ?? Number(op.monto_factura || 0) - Number(op.capital_adelantado || 0);
      if (remanente > 0 && !tieneRemanenteVinc) {
        factoringEntries.push({
          id: `factoring-excedente-${op.id}`,
          factura_sii_emitida_id: op.factura_sii_emitida_id,
          fecha_display: op.fecha_cobro_final,
          importeEnMoneda: aDisplay(remanente, monedaLocal, op.fecha_cobro_final),
          moneda_origen: monedaLocal,
          moneda_dolar_origen: false,
          razon_social_receptor: `Fact. ${proveedorFactoring} | Excedente | ${cliente}`,
          tipo: "facturado",
          folio: op.factura?.folio || null,
          es_factoring: true,
        });
      }
    }

    // Fase 1b (10.244): siempre completo (solo filtro de moneda); `mostrarBacklog` se aplica al
    // armar `todos` más abajo, no acá.
    const pipe = pipeline
      .map(mapPipeline)
      .filter((p) => (p.moneda_dolar ? incluirUSD : incluirLocal) && p.importeEnMoneda > 0);

    // "Por pagar" — itera las 2 fuentes individuales (SII recibidas + externas)
    // y genera una fila por folio (sin plan de cuotas) o N filas por folio (con cuotas).
    // El saldo consolidado por proveedor sigue calculándose en cargarDatosFuturos
    // (saldosPendientesProveedores) pero no se usa acá: el agregado vive en ResumenSaldo.
    // Las filas son el detalle operativo. Comparación por ID (regla 10.7).
    const provsEntries = [];

    // Fuente 1 — SII recibidas: descarta ya vinculadas a movs bancarios y las que el
    // usuario marcó como "vinculada" sin movimiento (estado_vinculacion). SII siempre en
    // moneda local (CLP/PEN) — no hay branch USD. Excluye notas de crédito (tipo_dte=61):
    // reducen deuda, no son pagos pendientes — su efecto neto ya está reflejado en el
    // ledger consolidado (ResumenSaldo).
    if (incluirLocal) {
      for (const f of proveedoresPendientes) {
        // Gate de inclusión a nivel factura — fuente única compartida con el badge
        // countPorPagarPendientes (regla 10.1). Encapsula corte de fecha, NC (10.42),
        // caja chica (10.39), comisiones factoring/recepción neteadas y saldada-FIFO.
        if (!facturaSiiPorPagar(f)) continue;

        // Saldo FIFO restante — se consulta ANTES de la bifurcación cuotas/sin-cuotas para que
        // AMBAS ramas respeten lo ya pagado. El predicado ya garantizó que existe (con
        // proveedor_id) o que no hay FIFO (sin proveedor_id → fallback a monto_total).
        const saldoFIFO = f.proveedor_id ? saldosPorFacturaMap.get(`SII:${f.id}`) : Number(f.monto_total || 0);

        const cuotasFactura = cuotasPagosPendientes.filter((c) => c.factura_sii_recibida_id === f.id);
        if (cuotasFactura.length > 0) {
          // Mostrar las cuotas pero CAPADAS al saldo FIFO disponible: una factura con plan de
          // pagos pero ya pagada (parcial/total) por caja no debe sobre-mostrarse (caso Blackim
          // 958/989: el plan tenía el bruto, los pagos reales vivían en movimientos_caja).
          let saldoDisponible = saldoFIFO;
          for (const c of cuotasFactura) {
            if (saldoDisponible <= 0.01) break;
            const montoCuota = Math.min(Number(c.monto_cuota), saldoDisponible);
            saldoDisponible -= montoCuota;
            provsEntries.push({
              ...f,
              fecha_display: c.fecha_vencimiento,
              importeEnMoneda: -aDisplay(montoCuota, monedaLocal, c.fecha_vencimiento),
              moneda_origen: monedaLocal,
              razon_social_receptor: nombreProvMap[f.proveedor_id] || f.razon_social_emisor,
              tipo: "proveedor",
              folio: f.folio,
              cuota_actual: c.numero_cuota,
              // Identidad de la cuota (Fase 2, Commit 1) — ver comentario espejo en la rama "facturado"
              // de arriba. Cubre SII recibida acá; externa y boleta más abajo (mismas 3 líneas c/u).
              cuota_id: c.id,
              cuota_estado: c.estado,
              cuota_movimiento_bancario_id: c.movimiento_bancario_id,
              cuota_tabla: "plan_pagos_facturas",
              // Fase 2d: ver comentario espejo en la rama "facturado" (`cuota_tiene_eventos`). Esta
              // fuente (SII recibida) → cuota_documento_tipo fijo.
              cuota_tiene_eventos: c._tiene_eventos,
              cuota_documento_tipo: "sii_recibida",
              // M = total del plan (todas las cuotas), NO solo las pendientes — viene enriquecido
              // desde getCuotasPagosFuturas. Fallback a cuotasFactura.length por robustez.
              total_cuotas: c.total_cuotas > 0 ? c.total_cuotas : cuotasFactura.length,
              // Fase 3c (2026-09-25): ver comentario espejo en la rama "facturado" de arriba —
              // `tiene_override` derivado de `fecha_vencimiento_original` vs la fecha actual
              // (`esFechaModificada`); `fecha_original_plan` para el hover de la pill azul.
              tiene_override: esFechaModificada(c.fecha_vencimiento_original, c.fecha_vencimiento),
              fecha_original_plan: c.fecha_vencimiento_original || null,
              // Cuota comprometida ya vencida → texto de fecha en rojo (reusa el flag `vencida`
              // del render, mismo criterio string que la rama sin-cuotas en línea ~1697).
              vencida: esVencida(c.fecha_vencimiento, hoy),
            });
          }
        } else {
          // Fecha de pago: `plazopago` si fue planificado (PlanCuotasModal graba ahí), con
          // fallback a `fecha_emision`. Mismo patrón que las externas (Fuente 2, línea ~1713).
          // `tiene_override` enciende la pill verde; `vencida` usa la fecha EFECTIVA (no la de
          // emisión) para no marcar en rojo una factura con pago planificado a futuro — mismo
          // criterio que la rama "Por cobrar" (plazocobro, línea ~1502).
          const fechaEfectiva = f.plazopago || f.fecha_emision || null;
          provsEntries.push({
            ...f,
            fecha_display: fechaEfectiva,
            importeEnMoneda: -aDisplay(saldoFIFO, monedaLocal, f.fecha_emision),
            moneda_origen: monedaLocal,
            razon_social_receptor: nombreProvMap[f.proveedor_id] || f.razon_social_emisor,
            tipo: "proveedor",
            folio: f.folio,
            tiene_override: !!f.plazopago,
            vencida: esVencida(fechaEfectiva, hoy),
            _parcial: saldoFIFO < Number(f.monto_total || 0),
            _montoOriginal: Number(f.monto_total || 0),
          });
        }
      }
    }

    // Fuente 2 — facturas_externas (asesorías, gastos varios sin DTE SII). Tienen moneda
    // explícita (CLP/PEN/USD). Fecha de pago: plazopago si fue planificado; si no, fallback
    // al día 30 del mes de fecha_emision (mismo criterio de día que las recurrentes, ver
    // proveedoresRecurrentes.js). Excluye las ya vinculadas a un movimiento bancario.
    // Día 30 del mes de una fecha YYYY-MM-DD; si el mes no llega al 30, último día del mes.
    const dia30DelMes = (fechaStr) => {
      if (!fechaStr) return null;
      const [yy, mm] = fechaStr.split("-").map(Number);
      if (!yy || !mm) return null;
      const d = new Date(yy, mm - 1, 30);
      if (d.getMonth() !== mm - 1) d.setDate(0);
      return d.toISOString().split("T")[0];
    };
    for (const f of facturasExternasPendientes) {
      if (f.estado === "pagada") continue;
      if (yaVinculadasExternas.has(f.id)) continue;
      // Excluir filas sin nombre identificable — el display usa f.proveedor_nombre y
      // cuando es falsy queda "—", basura visual sin valor operativo.
      if (!f.proveedor_nombre) continue;
      const esUSD = f.moneda === "USD";
      if (esUSD ? !incluirUSD : !incluirLocal) continue;
      let importe;
      // Moneda real en que está expresado `importe`. Cuando viene del ledger (saldosPorFacturaMap),
      // está en la moneda del proveedor (provMoneda = moneda_default), que puede diferir de f.moneda
      // si el dato es inconsistente. Pasar f.moneda acá deformaría el monto (provMoneda CLP + label USD).
      let monedaImporte;
      if (f.proveedor_id) {
        // Ausente del map = el ledger la dio por pagada → ocultar (mismo criterio que SII).
        // El caso desvincular post-load se cubre inyectando su monto al map en handleMovUpdated.
        const saldoRest = saldosPorFacturaMap.get(`EXT:${f.id}`);
        if (saldoRest === undefined) continue;
        importe = saldoRest;
        monedaImporte = provMonedaMap.get(f.proveedor_id) || f.moneda || monedaLocal;
      } else {
        importe = Number(f.monto_total || 0);
        monedaImporte = f.moneda || monedaLocal;
      }
      // Fase 5a-bis: si la externa tiene plan de cuotas (matcheado por documento_id vía
      // factura_externa_id que trae getCuotasPagosFuturas), se PARTE en "folio (N/M)" igual que la
      // Fuente 1 (SII recibidas), capando al saldo disponible (`importe`). Si no, línea única.
      const cuotasExterna = cuotasPagosPendientes.filter((c) => c.factura_externa_id === f.id);
      if (cuotasExterna.length > 0) {
        let saldoDisponible = importe;
        for (const c of cuotasExterna) {
          if (saldoDisponible <= 0.01) break;
          const montoCuota = Math.min(Number(c.monto_cuota), saldoDisponible);
          saldoDisponible -= montoCuota;
          provsEntries.push({
            ...f,
            fecha_display: c.fecha_vencimiento,
            importeEnMoneda: -aDisplay(montoCuota, monedaImporte, c.fecha_vencimiento),
            moneda_origen: monedaImporte,
            razon_social_receptor: f.concepto || f.proveedor_nombre || "—",
            tipo: "proveedor",
            folio: f.numero_documento || null,
            es_externa: true,
            cuota_actual: c.numero_cuota,
            // Identidad de la cuota (Fase 2, Commit 1) — ver comentario espejo en la rama SII recibida.
            cuota_id: c.id,
            cuota_estado: c.estado,
            cuota_movimiento_bancario_id: c.movimiento_bancario_id,
            cuota_tabla: "plan_pagos_facturas",
            // Fase 2d: ver comentario espejo en la rama "facturado" (`cuota_tiene_eventos`). Esta
            // fuente (externa) → cuota_documento_tipo fijo; es justo la que `esCuotaReagendable`
            // excluye siempre (documento_tipo==="factura_externa") — ver cuotaReagendable.js.
            cuota_tiene_eventos: c._tiene_eventos,
            cuota_documento_tipo: "factura_externa",
            // M = total del plan (todas las cuotas), enriquecido por getCuotasPagosFuturas.
            total_cuotas: c.total_cuotas > 0 ? c.total_cuotas : cuotasExterna.length,
            // Fase 3c (2026-09-25): ver comentario espejo en la rama SII recibida.
            tiene_override: esFechaModificada(c.fecha_vencimiento_original, c.fecha_vencimiento),
            fecha_original_plan: c.fecha_vencimiento_original || null,
            vencida: esVencida(c.fecha_vencimiento, hoy),
          });
        }
      } else {
        provsEntries.push({
          ...f,
          fecha_display: f.plazopago || dia30DelMes(f.fecha_emision) || null,
          importeEnMoneda: -aDisplay(importe, monedaImporte, f.fecha_emision),
          moneda_origen: monedaImporte,
          // Concepto completo (mismo campo `f.concepto` que muestra EstadoCuenta en el ledger, ver
          // ledgerProveedor.js débitos EXT) → no divergen. Ej. OnSign: "OnSign 2026/07 · 187 licencias".
          // Fallback a proveedor_nombre si una externa vieja no tuviera concepto. NO aplica a las
          // proyecciones recurrentes (otra fuente, con su propio formato "(N licencias)").
          razon_social_receptor: f.concepto || f.proveedor_nombre || "—",
          tipo: "proveedor",
          folio: f.numero_documento || null,
          es_externa: true,
          // Pill de override (patrón Backlog, regla 10.89): la fecha vino de plazopago planificado.
          tiene_override: !!f.plazopago,
        });
      }
    }

    // Fuente 3 — residuos por proveedor: deuda del ledger global no atribuible a factura
    // específica (cobros a cuenta, saldo inicial). Hace que la suma del panel coincida
    // con el saldo del estado de cuenta. saldoDisplay viene en provMoneda; respetar el
    // filtro USD/local con la moneda del residuo. fecha_display=hoy: si fuera null,
    // dentroDeCorte() lo filtra cuando hay corte temporal activo (default "Año").
    for (const r of residuosProveedoresPendientes) {
      const esUSD = r.moneda === "USD";
      if (esUSD ? !incluirUSD : !incluirLocal) continue;
      provsEntries.push({
        id: `residuo-${r.provId}`,
        fecha_display: hoy,
        importeEnMoneda: -aDisplay(r.saldoDisplay, r.moneda || monedaLocal, hoy),
        moneda_origen: r.moneda || monedaLocal,
        razon_social_receptor: r.nombre,
        tipo: "proveedor",
        folio: null,
        es_residuo: true,
      });
    }

    // Fuente 4 — boletas de honorarios (Bloque A): espejo de la Fuente 1 (SII recibidas). El saldo
    // vive en el ledger (saldosPorFacturaMap, clave "BHE:<id>"): ausente = saldada (por MBF o FIFO)
    // → se oculta; presente = impaga con su saldo. La BHE es local (CLP/PEN). Desde Fase 4 de "Futuros:
    // reagendar" (2026-09-25, migración 20260925c aplicada) SÍ tiene `plazopago`: fecha_display =
    // plazopago si fue planificado, fallback a fecha_emision (una sin planificar/vieja queda "vencida"
    // en rojo por fecha_emision, igual que antes). Solo aplica al lado local (una boleta chilena no es
    // USD). `es_boleta` bloquea el lápiz de PlanCuotasModal.
    if (incluirLocal) {
      for (const b of boletasHonorariosPendientes) {
        if (!b.computa) continue;
        const saldo = saldosPorFacturaMap.get(`BHE:${b.id}`);
        if (saldo === undefined) continue;
        const monedaBoleta = provMonedaMap.get(b.proveedor_id) || monedaLocal;
        // Campos que el modal necesita (Fase 5b): id numérico real (b.id — el id del entry sigue siendo
        // string "boleta-<id>" para el key del render), proveedor_id y monto_bruto (el modal lee el
        // monto de ahí, no de monto_total). Se agregan a AMBAS ramas (split y línea única).
        const camposModalBoleta = { boleta_id: b.id, proveedor_id: b.proveedor_id, monto_bruto: b.monto_bruto };
        // Fase 5b: si la boleta tiene plan de cuotas (matcheado por documento_id vía factura_boleta_id
        // que trae getCuotasPagosFuturas), se PARTE en "folio (N/M)" igual que facturas y externas.
        const cuotasBoleta = cuotasPagosPendientes.filter((c) => c.factura_boleta_id === b.id);
        if (cuotasBoleta.length > 0) {
          let saldoDisponible = saldo;
          for (const c of cuotasBoleta) {
            if (saldoDisponible <= 0.01) break;
            const montoCuota = Math.min(Number(c.monto_cuota), saldoDisponible);
            saldoDisponible -= montoCuota;
            provsEntries.push({
              id: `boleta-${b.id}`,
              ...camposModalBoleta,
              fecha_display: c.fecha_vencimiento,
              importeEnMoneda: -aDisplay(montoCuota, monedaBoleta, c.fecha_vencimiento),
              moneda_origen: monedaBoleta,
              razon_social_receptor: b.emisor_nombre || "—",
              tipo: "proveedor",
              folio: `Boleta N°${b.folio}`,
              es_boleta: true,
              cuota_actual: c.numero_cuota,
              // Identidad de la cuota (Fase 2, Commit 1) — ver comentario espejo en la rama SII recibida.
              cuota_id: c.id,
              cuota_estado: c.estado,
              cuota_movimiento_bancario_id: c.movimiento_bancario_id,
              cuota_tabla: "plan_pagos_facturas",
              // Fase 2d: ver comentario espejo en la rama "facturado" (`cuota_tiene_eventos`). Esta
              // fuente (boleta) → cuota_documento_tipo fijo.
              cuota_tiene_eventos: c._tiene_eventos,
              cuota_documento_tipo: "boleta_honorario",
              total_cuotas: c.total_cuotas > 0 ? c.total_cuotas : cuotasBoleta.length,
              // Fase 3c (2026-09-25): ver comentario espejo en la rama SII recibida.
              tiene_override: esFechaModificada(c.fecha_vencimiento_original, c.fecha_vencimiento),
              fecha_original_plan: c.fecha_vencimiento_original || null,
              vencida: esVencida(c.fecha_vencimiento, hoy),
            });
          }
        } else {
          // Fase 4 de "Futuros: reagendar" (2026-09-25): fecha efectiva = plazopago si fue
          // planificado (columna nueva, migración 20260925c, aplicada), fallback a fecha_emision
          // (dato de Clay, inmutable) — mismo criterio EXACTO que la rama SII recibida sin cuotas de
          // arriba (`fechaEfectiva = f.plazopago || f.fecha_emision`, 10.1). Antes de esta fase
          // SIEMPRE era fecha_emision (boletas_honorarios no tenía columna de fecha de pago).
          const fechaEfectivaBoleta = b.plazopago || b.fecha_emision || null;
          // Auditoría 3b/3c/4, Commit 4c (2026-09-25): antes `tiene_override = b.plazopago != null` —
          // encendía la pill con CUALQUIER plazopago explícito, aunque coincidiera con fecha_emision (un
          // hover "Fecha original: DD/MM/YYYY" mostrando la MISMA fecha que ya se ve, sin nada que
          // explicar). Pasa a `esFechaModificada` (fechasFuturosPanel.js, fuente única del predicado,
          // 10.278/10.1) — misma fórmula que los 5 mappers de cuota: "no nula Y distinta de la actual".
          // Boleta no tiene una columna `_original` capturada por trigger (a diferencia de las cuotas):
          // el propio `fecha_emision` hace de "original" SOLO cuando plazopago está seteado (si
          // plazopago es null, no hay override que mostrar — pasar fecha_emision como original ahí
          // haría que TODA boleta sin plazopago saliera "modificada", falso positivo).
          const overrideBoleta = esFechaModificada(b.plazopago != null ? b.fecha_emision : null, b.plazopago);
          provsEntries.push({
            id: `boleta-${b.id}`,
            ...camposModalBoleta,
            fecha_display: fechaEfectivaBoleta,
            importeEnMoneda: -aDisplay(saldo, monedaBoleta, b.fecha_emision),
            moneda_origen: monedaBoleta,
            razon_social_receptor: b.emisor_nombre || "—",
            tipo: "proveedor",
            folio: `Boleta N°${b.folio}`,
            es_boleta: true,
            // fecha_original_plan SOLO cuando hay override (hover "Fecha original: DD/MM/YYYY") — sin
            // override no hay nada que mostrar como "original" distinto de lo que ya se ve.
            tiene_override: overrideBoleta,
            fecha_original_plan: overrideBoleta ? b.fecha_emision : null,
            vencida: esVencida(fechaEfectivaBoleta, hoy),
          });
        }
      }
    }

    // Recurrentes: pueden ser USD o moneda local. importe ya está en su moneda.
    const provsRecurrentesFiltrados = proveedoresRecurrentes
      .filter((p) => (p.moneda_recurrente === "USD" ? incluirUSD : incluirLocal))
      .map((p) => ({
        ...p,
        importeEnMoneda: -aDisplay(
          Math.abs(Number(p.importeEnMoneda || 0)),
          p.moneda_recurrente || monedaLocal,
          p.fecha_display
        ),
        moneda_origen: p.moneda_recurrente === "USD" ? "USD" : monedaLocal,
      }));
    // provsEntries y provsRecurrentesFiltrados YA son completos (solo gate de moneda, arriba) — sus
    // gates de categoría (`mostrarProveedoresFact`/`mostrarProveedoresRecurrentes`, INDEPENDIENTES
    // entre sí, cada uno con su propio tab) se aplican al armar `todos` más abajo (Fase 1b, 10.244).

    // RRHH e impuestos siempre se calculan en moneda local. Si no incluyo local, no van.
    const reexpresarLocal = (e) => ({
      ...e,
      importeEnMoneda: -aDisplay(Math.abs(Number(e.importeEnMoneda || 0)), monedaLocal, e.fecha_display),
      moneda_origen: monedaLocal,
    });
    // Fase 1b (10.244): siempre completo (solo moneda); `mostrarRRHH`/`mostrarImpuestos` se aplican
    // al armar `todos` más abajo.
    const rrhh = incluirLocal ? rrhhEntries.map(reexpresarLocal) : [];
    const impuestos = incluirLocal ? impuestosEntries.map(reexpresarLocal) : [];

    const mostrarCapex = mostrarTodo || filtrosActivos.includes("capex");
    const mapCapex = (a) => {
      const monedaOrig = a.moneda === "USD" ? "USD" : monedaLocal;
      return {
        id: `capex-${a.id}`,
        // Id real del activo (el `id` de arriba es la key visual con prefijo) — lo usa el
        // modal "pago ejecutado" (lápiz FaEdit) para actualizarActivo(activo_id, { estado: ejecutado_sin_factura }).
        activo_id: a.id,
        fecha_display: a.fecha_planificada,
        // Planificado vencido → fecha en rojo (mismo criterio que Backlog/cuotas). El color lo
        // aplica la regla "vencida universal" del render; el flag se setea por consistencia.
        vencida: esVencida(a.fecha_planificada, hoy),
        // La caja proyecta el BRUTO (neto + IVA si es afecto y en moneda local); el neto capitalizable
        // no se toca. Grosseo por el helper único brutoCapex (CapEx-IVA Fase 2, regla 10.1).
        importeEnMoneda: -aDisplay(
          brutoCapex(a.costo_unitario, { exento_iva: a.exento_iva, moneda: a.moneda, pais }),
          monedaOrig,
          a.fecha_planificada
        ),
        moneda_origen: monedaOrig,
        razon_social_receptor: construirConceptoCapex(
          a,
          a.destino_id ? edificiosMap[a.destino_id] || `Edificio #${a.destino_id}` : null
        ),
        tipo: "capex",
      };
    };
    // Línea de LOTE de compra (bloque Stock). Mapper propio — NO se toca el mapCapex
    // legacy de arriba (su fix de `cantidad` es Fase 3). Diferencia clave: acá el monto
    // SÍ multiplica por la cantidad, porque un lote es N unidades en un solo egreso.
    const mapCapexLote = (l) => {
      const monedaOrig = l.moneda === "USD" ? "USD" : monedaLocal;
      const objetivo = Number(l.costo_unitario || 0) * Number(l.cantidad_total || 0);
      // Fase 3b-ii: si el FOB tiene un parcial (fila fob pendiente con saldo>0), proyectar el SALDO
      // RESTANTE (en la moneda del target), no el objetivo. Sin fila fob → objetivo completo. FOB
      // importado: bruto = neto (sin IVA local), por eso no se grossea. Agregación multimoneda intacta (10.185).
      const total = lotesFobSaldado.get(l.id)?.saldoRestante ?? objetivo;
      return {
        id: `capex-lote-${l.id}`,
        lote_id: l.id,
        // Marca que esta fila es un lote: la excluye del lápiz "marcar pagado" (que
        // espera un activo_id). Ejecutar un lote es sub-fase 5, desde Activos.
        es_lote: true,
        fecha_display: l.fecha_planificada,
        // Mismo criterio de vencida que los activos planificados (regla 10.159): un
        // CapEx comprometido y vencido no se oculta, se muestra en rojo.
        vencida: esVencida(l.fecha_planificada, hoy),
        importeEnMoneda: -aDisplay(total, monedaOrig, l.fecha_planificada),
        moneda_origen: monedaOrig,
        // Concepto de la línea de lote: helper único (10.1) reusado por el tooltip del histórico.
        razon_social_receptor: construirConceptoLote(l),
        tipo: "capex",
      };
    };

    // Línea de COMPONENTE de costo (landed cost) de un lote (9.36). SUMA a la línea FOB del lote
    // (mapCapexLote), no la reemplaza: el FOB es la base y estos son extras (transporte/aduana/otro)
    // que capitalizan aparte, cada uno con su propia moneda y fecha_pago → no hay doble conteo ni
    // bifurcación tipo cuotas. Misma razon_social que su lote para que se agrupen visualmente; el tipo
    // del componente va en FOLIO para distinguirla de la FOB. `es_lote: true` la deja fuera del kebab
    // (igual que la línea FOB); la conciliación es el commit 4.
    const mapCapexComponente = (c) => {
      const monedaOrig = c.moneda === "USD" ? "USD" : monedaLocal;
      return {
        id: `capex-componente-${c.id}`,
        lote_id: c.lote_id,
        // Id real del componente en lote_costos — lo usa el editor "Modificar fecha de pago" de la
        // línea de lote (actualizarCostoLote). El FOB no lo lleva: se distingue por la ausencia de costo_id.
        costo_id: c.id,
        es_lote: true,
        componente_tipo: c.tipo,
        fecha_display: c.fecha_pago,
        vencida: esVencida(c.fecha_pago, hoy),
        // CUTOVER Fase 2b: monto proyectado = saldo RESTANTE del ledger (objetivo − aplicado, moneda del
        // target), no el objetivo entero. En todo-o-nada `_saldoRestante === c.monto` (idéntico). Fallback
        // a c.monto si el loader no lo trae (self-fetch/legacy). Sin IVA local (importado, bruto=neto).
        importeEnMoneda: -aDisplay(Number(c._saldoRestante ?? c.monto ?? 0), monedaOrig, c.fecha_pago),
        moneda_origen: monedaOrig,
        // Concepto de la línea de lote: helper único (10.1) reusado por el tooltip del histórico.
        razon_social_receptor: construirConceptoLote(c.lote),
        folio: TIPO_COMPONENTE_LABEL[c.tipo] || c.tipo,
        tipo: "capex",
      };
    };

    // Índice de cuotas pendientes por activo. Un activo CON plan proyecta una línea por cuota
    // (fecha_vencimiento/monto_cuota) en vez de la línea única de mapCapex — nunca las dos fuentes a
    // la vez (única fuente de la verdad, §7). Caja pura: no toca estado/fecha_compra/depreciación.
    // `cuotasActivosPendientes` puede ser `null` (Fase 3b/M1: la carga del libro/las cuotas falló) — el
    // fallback `|| []` es SOLO para no reventar este render (mismo criterio que `activosConPlanSeguro`
    // más abajo): con el índice vacío, NINGÚN activo con plan emite cuota alguna (nunca una paga
    // reaparece como pendiente); el bloqueo REAL del reagendado en ese escenario lo hace
    // `ctx.planesCargados` en `esFechaReagendable` (`saldosCapex != null`, ver `ctxReagendarFuturo`).
    const cuotasActivoPorId = {};
    for (const c of cuotasActivosPendientes || []) {
      if (!cuotasActivoPorId[c.activo_id]) cuotasActivoPorId[c.activo_id] = [];
      cuotasActivoPorId[c.activo_id].push(c);
    }
    const mapCuotaActivo = (a, c) => {
      const monedaOrig = a.moneda === "USD" ? "USD" : monedaLocal;
      return {
        id: `capex-cuota-${c.id}`,
        activo_id: a.id,
        tiene_plan: true, // marca la fila como "cuota de plan" para el kebab por tipo de línea
        // Fase 3 de "Futuros: reagendar" (2026-09-25): `cuota_id` PLANO (ADEMÁS del `id` compuesto
        // string de arriba, que sigue siendo la key de React/folio) — lo usa `esFechaReagendable`
        // (`typeof f.cuota_id === "number"`, mismo guard que `esCuotaFacturaReagendable`) y el
        // despachador `reagendarEntryFuturo` para llamar `guardarFechaCuotaActivo({ cuota_id, ... })`.
        // `cuota_tiene_evento_vigente` = `c._tiene_evento_vigente`, ya calculado por
        // `getCuotasActivosFuturas` (EXISTS puro sobre el MISMO Map de `pagos_capex` que ya usa para
        // `_saldoRestante` — sin query adicional, mismo patrón que `cuota_tiene_eventos` de las cuotas
        // de factura).
        cuota_id: c.id,
        cuota_tiene_evento_vigente: c._tiene_evento_vigente,
        fecha_display: c.fecha_vencimiento,
        vencida: esVencida(c.fecha_vencimiento, hoy),
        // Bruto de la cuota: afecto/exento del activo PADRE (a.exento_iva/a.moneda). CUTOVER Fase 2b: el
        // neto que se grossea es el saldo RESTANTE del ledger (objetivo − aplicado, moneda del target),
        // no el objetivo entero; en todo-o-nada `_saldoRestante === c.monto_cuota` (idéntico). Fallback a
        // c.monto_cuota si el loader no lo trae. Mismo helper único brutoCapex (10.1).
        importeEnMoneda: -aDisplay(
          brutoCapex(c._saldoRestante ?? c.monto_cuota, { exento_iva: a.exento_iva, moneda: a.moneda, pais }),
          monedaOrig,
          c.fecha_vencimiento
        ),
        moneda_origen: monedaOrig,
        razon_social_receptor: construirConceptoCapex(
          a,
          a.destino_id ? edificiosMap[a.destino_id] || `Edificio #${a.destino_id}` : null
        ),
        // "Cuota X/N" en la columna FOLIO (mismo patrón que facturas/créditos en cuotas), no en el
        // concepto, para no romper el layout.
        folio: "Cuota",
        // Ordinal por POSICIÓN dentro del plan (getCuotasActivosFuturas → `_posicion`), no el
        // numero_cuota crudo append-only (evita el "5/1" tras borrar/regenerar). Nunca crudo.
        cuota_actual: c._posicion || null,
        total_cuotas: c.total_cuotas > 0 ? c.total_cuotas : (cuotasActivoPorId[a.id] || []).length,
        // Fase 3c (2026-09-25, regla de negocio definida por Ignacio): `tiene_override` YA NO es
        // `true` fijo — mismo predicado único `esFechaModificada` que los 4 mappers de cuota de
        // factura (fechasFuturosPanel.js), contra `fecha_vencimiento_original` (columna poblada por el
        // trigger de la migración 20260925b, aplicada — captura una sola vez, nunca se pisa; refetch
        // dirigido `getCuotasActivosFuturas` la trae fresca con `select("*")`, sin wiring adicional).
        // `fecha_original_plan` para el hover de la pill azul. `vencida`/`vencidoReal` (colapso más
        // abajo) siguen mandando primero — precedencia 10.244 intacta, sin cambios.
        tiene_override: esFechaModificada(c.fecha_vencimiento_original, c.fecha_vencimiento),
        fecha_original_plan: c.fecha_vencimiento_original || null,
        tipo: "capex",
      };
    };

    // Unificado con el modal "Compra de activo" (VincularMovimientoModal): SIN filtro de fecha.
    // Un planificado vencido NO se oculta: se muestra en su fecha comprometida, en rojo (regla
    // vencida universal), hasta que se ejecute (vinculando banco) o se elimine — igual que
    // Backlog/cuotas. Se exige fecha_planificada no-null (el alta la blinda; 0 legacy sin fecha).
    const capexFiltrado = capexEntries
      .filter((a) => a.fecha_planificada)
      .filter((a) => (a.moneda === "USD" ? incluirUSD : incluirLocal));
    // Misma regla para los lotes: se exige fecha_planificada (el alta la blinda) y se
    // respeta el filtro de moneda de la vista.
    // Commit 1 (auditoría 3b/3c/4, 10.273): `lotesFobSaldado`/`componentesLotes` pueden ser `null` (la
    // carga del libro CapEx falló — ver catch de `getLotesFobSaldado`/`getCostosLotesFuturos` arriba);
    // el toast.error del load ya avisó explícito. Filtros puros y testeados en `loteCostos.js`
    // (`filtrarLotesCapexProyectables`/`filtrarComponentesCapexProyectables`).
    const capexLotesFiltrado = filtrarLotesCapexProyectables(capexLotes, lotesFobSaldado, {
      incluirUSD,
      incluirLocal,
    });
    const componentesFiltrado = filtrarComponentesCapexProyectables(componentesLotes, { incluirUSD, incluirLocal });
    // Bifurcación por activo: CON plan → una línea por cuota pendiente (mapCuotaActivo); si el plan
    // está completo (sin pendientes) no emite ninguna línea, nunca cae a la única. SIN plan → mapCapex.
    // Lotes: línea FOB (mapCapexLote) + una línea por componente pendiente (mapCapexComponente). El
    // FOB y los componentes son plata distinta → suman, no se reemplazan (corrige §7 del informe).
    // `activosConPlan` puede ser `null` (todavía no cargó, o la carga falló — Fase 1c, Commit 2a): el
    // fallback `|| new Set()` es SOLO para no reventar este render (mismo comportamiento visual que
    // antes, línea única mientras no se sepa mejor); la exclusión REAL del reagendado en ese
    // escenario la hace `ctx.planesCargados` en `esFechaReagendable`, no esto.
    const activosConPlanSeguro = activosConPlan || new Set();
    // Fase 1b (10.244): siempre completo; `mostrarCapex` se aplica al armar `todos` más abajo.
    const capex = [
      ...capexFiltrado.flatMap((a) =>
        activosConPlanSeguro.has(a.id)
          ? (cuotasActivoPorId[a.id] || []).map((c) => mapCuotaActivo(a, c))
          : [mapCapex(a)]
      ),
      ...capexLotesFiltrado.map(mapCapexLote),
      ...componentesFiltrado.map(mapCapexComponente),
    ];

    // COMPROMISOS DE PAGO — egreso futuro a un proveedor SIN factura todavía (ver
    // docs/dominios/compromisos-pago.md). Espeja CapEx: driver = compromisosEntries (planificado +
    // sin factura, ya filtrado en el loader); bifurcación con-plan (una línea por cuota pendiente) /
    // sin-plan (línea única en fecha_estimada). Reusa brutoCapex (grosseo IVA, 10.1) y aDisplay
    // (reexpresión por fecha, 10.185). Egreso negativo. moneda_origen local/USD para badge+filtro;
    // aDisplay recibe la moneda REAL (soporta UF, como los costos asociados de Recurrentes).
    // Los compromisos NO tienen tab propio: se muestran DENTRO de "Por pagar" (mismo gate que las
    // facturas por pagar). Sin factura todavía → sin folio: la columna Folio muestra "—" como el resto
    // de las filas sin folio.
    const mostrarCompromisos = mostrarProveedoresFact;
    const mapCompromiso = (c) => {
      const monedaBadge = c.moneda === "USD" ? "USD" : monedaLocal;
      return {
        id: `compromiso-${c.id}`,
        compromiso_id: c.id,
        // monto/moneda CRUDOS de la columna, para que el modal de edición (kebab) los edite tal cual.
        monto: c.monto,
        moneda: c.moneda,
        fecha_display: c.fecha_estimada,
        // Planificado vencido → fecha en rojo por la regla "vencida universal" del render.
        vencida: esVencida(c.fecha_estimada, hoy),
        importeEnMoneda: -aDisplay(
          brutoCapex(c.monto, { exento_iva: c.exento_iva, moneda: c.moneda, pais }),
          c.moneda,
          c.fecha_estimada
        ),
        moneda_origen: monedaBadge,
        razon_social_receptor: [c.proveedor?.nombre || null, c.concepto].filter(Boolean).join(" | "),
        tipo: "compromiso",
      };
    };
    // Índice de cuotas pendientes por compromiso. Un compromiso CON plan proyecta una línea por cuota
    // (fecha_vencimiento/monto_cuota) en vez de la línea única — nunca las dos a la vez (fuente única).
    const cuotasCompromisoPorId = {};
    for (const cu of cuotasCompromisoPendientes) {
      if (!cuotasCompromisoPorId[cu.compromiso_id]) cuotasCompromisoPorId[cu.compromiso_id] = [];
      cuotasCompromisoPorId[cu.compromiso_id].push(cu);
    }
    const mapCuotaCompromiso = (co, cu) => {
      const monedaBadge = co.moneda === "USD" ? "USD" : monedaLocal;
      return {
        id: `compromiso-cuota-${cu.id}`,
        compromiso_id: co.id,
        tiene_plan: true,
        fecha_display: cu.fecha_vencimiento,
        vencida: esVencida(cu.fecha_vencimiento, hoy),
        // Bruto de la cuota: la condición afecto/exento sale del compromiso PADRE (co.exento_iva/co.moneda).
        importeEnMoneda: -aDisplay(
          brutoCapex(cu.monto_cuota, { exento_iva: co.exento_iva, moneda: co.moneda, pais }),
          co.moneda,
          cu.fecha_vencimiento
        ),
        moneda_origen: monedaBadge,
        razon_social_receptor: [co.proveedor?.nombre || null, co.concepto].filter(Boolean).join(" | "),
        folio: "Cuota",
        cuota_actual: cu.numero_cuota,
        total_cuotas: cu.total_cuotas > 0 ? cu.total_cuotas : (cuotasCompromisoPorId[co.id] || []).length,
        tipo: "compromiso",
      };
    };
    // Driver: compromisos planificado+sin-factura con fecha, respetando el filtro de moneda de la vista.
    const compromisosFiltrado = compromisosEntries
      .filter((c) => c.fecha_estimada)
      .filter((c) => (c.moneda === "USD" ? incluirUSD : incluirLocal));
    // Bifurcación: CON plan → una línea por cuota pendiente (si el plan está completo, ninguna línea,
    // nunca cae a la única); SIN plan → línea única (mapCompromiso). Igual que CapEx. `|| new Set()`:
    // mismo motivo que `activosConPlanSeguro` arriba (`compromisosConPlan` puede ser `null`).
    const compromisosConPlanSeguro = compromisosConPlan || new Set();
    const lineasDeCompromiso = (c) =>
      compromisosConPlanSeguro.has(c.id)
        ? (cuotasCompromisoPorId[c.id] || []).map((cu) => mapCuotaCompromiso(c, cu))
        : [mapCompromiso(c)];
    // Fase 1b (10.244): siempre completo; `mostrarCompromisos` se aplica al armar `todos` más abajo.
    const compromisos = compromisosFiltrado.flatMap(lineasDeCompromiso);

    // Edificios: si hay pagos planificados en plan_pagos_edificios, se genera una entry
    // por cada plan + (opcional) una fila residual con el saldo no planificado en la
    // próxima fecha trimestral. Si no hay planes, una sola entry con el saldo total.
    // Vienen siempre en moneda local.
    const mostrarEdificios = mostrarTodo || filtrosActivos.includes("edificios");
    // Fase 1b (10.244): siempre completo (solo moneda); `mostrarEdificios` se aplica al armar
    // `todos` más abajo.
    const edificiosEntries = [];
    if (incluirLocal) {
      for (const e of edificiosSaldo) {
        const plansEdificio = planPagosEdificios.filter((p) => p.edificio_id === e.edificioId);
        if (plansEdificio.length > 0) {
          for (const plan of plansEdificio) {
            edificiosEntries.push({
              id: `edificio-plan-${plan.id}`,
              fecha_display: plan.fecha_acordada,
              importeEnMoneda: -aDisplay(Number(plan.monto || 0), monedaLocal, plan.fecha_acordada),
              moneda_origen: e.moneda,
              razon_social_receptor: e.edificioNombre,
              tipo: "edificio",
              folio: plan.nota || null,
              edificioData: e,
              planId: plan.id,
            });
          }
          const totalPlanificado = plansEdificio.reduce((sum, p) => sum + Number(p.monto || 0), 0);
          const saldoRestante = Math.round(Number(e.saldoPendiente || 0) - totalPlanificado);
          if (saldoRestante > 0) {
            edificiosEntries.push({
              id: `edificio-residual-${e.edificioId}`,
              fecha_display: e.proximaFecha,
              importeEnMoneda: -aDisplay(saldoRestante, monedaLocal, e.proximaFecha),
              moneda_origen: e.moneda,
              razon_social_receptor: e.edificioNombre,
              tipo: "edificio",
              folio: null,
              edificioData: e,
            });
          }
        } else {
          edificiosEntries.push({
            id: `edificio-${e.edificioId}`,
            fecha_display: e.proximaFecha,
            importeEnMoneda: -aDisplay(e.saldoPendiente, monedaLocal, e.proximaFecha),
            moneda_origen: e.moneda,
            razon_social_receptor: e.edificioNombre,
            tipo: "edificio",
            folio: null,
            edificioData: e,
          });
        }
      }
    }

    // Tab "financiamiento" (label "Créditos"): alimenta dos fuentes — moras de factoring
    // (creditosEntries, mapMora, siempre en moneda local) y cuotas de crédito bancario
    // (cuotasCreditoEntries, de creditos_cuotas, más abajo).
    const mostrarCreditos = mostrarTodo || filtrosActivos.includes("financiamiento");
    const mapMora = (m) => ({
      id: `mora-${m.id}`,
      fecha_display: m.fecha_notificacion,
      importeEnMoneda: -aDisplay(Number(m.monto_mora || 0), monedaLocal, m.fecha_notificacion),
      moneda_origen: monedaLocal,
      razon_social_receptor: [
        "Mora factoring",
        m.operacion?.factura?.folio ? `Folio ${m.operacion.factura.folio}` : null,
        m.operacion?.factura?.razon_social_receptor || null,
      ]
        .filter(Boolean)
        .join(" | "),
      tipo: "credito",
      folio: null,
    });
    // Fase 1b (10.244): las 3 fuentes de créditos siempre completas (solo moneda); `mostrarCreditos`
    // se aplica al armar `todos` más abajo.
    const creditosEntries = incluirLocal ? morasPendientes.map(mapMora) : [];

    // Créditos: cuotas pendientes de creditos_cuotas (JOIN creditos activos). El signo depende
    // de la DIRECCIÓN del crédito (comparado por campo, 10.7), nunca por nombre:
    //   · 'tomado'   (deuda propia)      → EGRESO  (negativo) — idéntico a hoy.
    //   · 'otorgado' (a favor, ej. fianza) → ENTRADA (positivo) en su fecha de recupero.
    // La moneda es la del crédito (CLP/PEN o USD) → reexpresada al display con aDisplay por la
    // fecha de la cuota (10.185; CLP/PEN local = identity).
    const mapCuotaCredito = (c) => {
      const monedaCredito = c.creditos?.moneda || monedaLocal;
      const esOtorgado = c.creditos?.direccion === "otorgado";
      const montoDisplay = aDisplay(Number(c.monto_total || 0), monedaCredito, c.fecha_vencimiento);
      return {
        id: `credito-cuota-${c.id}`,
        fecha_display: c.fecha_vencimiento,
        razon_social_receptor: c.creditos?.nombre || "Crédito",
        folio: `Cuota ${c.numero_cuota}`,
        importeEnMoneda: esOtorgado ? montoDisplay : -montoDisplay,
        moneda_origen: monedaCredito,
        tipo: "credito_cuota",
        es_credito_cuota: true,
        vencida: esVencida(c.fecha_vencimiento, hoy),
      };
    };
    const cuotasCreditoEntries = cuotasCreditos
      .filter((c) => (c.creditos?.moneda === "USD" ? incluirUSD : incluirLocal))
      .map(mapCuotaCredito);

    // Desembolso de la garantía (SOLO créditos 'otorgado'): la plata que SALE al entregar la
    // garantía, en fecha_desembolso, por monto_original — EGRESO (negativo), reexpresado por la
    // fecha del desembolso (10.185). Es la contracara del recupero (ingreso, mapCuotaCredito). La
    // deuda 'tomado' NO genera desembolso acá (su desembolso es un ingreso, fuera de alcance).
    // Dedup por crédito (un otorgado tiene 1 cuota, pero blindamos contra ids repetidos).
    const mapDesembolsoGarantia = (c) => {
      const cr = c.creditos;
      const monedaCredito = cr?.moneda || monedaLocal;
      return {
        id: `credito-desembolso-${cr.id}`,
        fecha_display: cr.fecha_desembolso,
        razon_social_receptor: `Desembolso garantía | ${cr.nombre || "Crédito"}`,
        folio: null,
        importeEnMoneda: -aDisplay(Number(cr.monto_original || 0), monedaCredito, cr.fecha_desembolso),
        moneda_origen: monedaCredito,
        tipo: "credito_cuota",
        es_credito_cuota: true,
        vencida: esVencida(cr.fecha_desembolso, hoy),
      };
    };
    const desembolsoSeen = new Set();
    const desembolsoGarantiaEntries = cuotasCreditos
      .filter((c) => {
        if (c.creditos?.direccion !== "otorgado" || !c.creditos?.fecha_desembolso) return false;
        if (!(c.creditos?.moneda === "USD" ? incluirUSD : incluirLocal)) return false;
        if (desembolsoSeen.has(c.creditos.id)) return false; // dedup por crédito
        desembolsoSeen.add(c.creditos.id);
        return true;
      })
      .map(mapDesembolsoGarantia);

    // Factoring: filtro de moneda propio (no viene de una fuente ya filtrada como las demás).
    const factoringEntriesFiltrado = factoringEntries.filter((e) =>
      e.moneda_dolar_origen ? incluirUSD : incluirLocal
    );

    let todos = [
      ...(mostrarFacturado ? filtrarPorCorte(facturadoEntries) : []),
      ...(mostrarFacturado ? filtrarPorCorte(factoringEntriesFiltrado) : []),
      ...(mostrarBacklog ? filtrarPorCorte(pipe) : []),
      // proveedores_facturados y proveedores_recurrentes son tabs INDEPENDIENTES (10.7/10.244: cada
      // fuente ya viene completa arriba, el gate de categoría se aplica acá, uno por uno — nunca
      // combinados en un solo flag, o un tab mostraría el otro también).
      ...(mostrarProveedoresFact ? filtrarPorCorte(provsEntries) : []),
      ...(mostrarProveedoresRecurrentes ? filtrarPorCorte(provsRecurrentesFiltrados) : []),
      ...(mostrarRRHH ? filtrarPorCorte(rrhh) : []),
      ...(mostrarImpuestos ? filtrarPorCorte(impuestos) : []),
      ...(mostrarCapex ? filtrarPorCorte(capex) : []),
      ...(mostrarCompromisos ? filtrarPorCorte(compromisos) : []),
      ...(mostrarEdificios ? filtrarPorCorte(edificiosEntries) : []),
      ...(mostrarCreditos ? filtrarPorCorte(creditosEntries) : []),
      ...(mostrarCreditos ? filtrarPorCorte(cuotasCreditoEntries) : []),
      ...(mostrarCreditos ? filtrarPorCorte(desembolsoGarantiaEntries) : []),
    ];

    // ── Pool para Sticky (docs/modulos/flujo-caja-v2.md § Sticky en Futuros) ────────────────────
    // Fase 1b (10.244, corrige el cache acumulativo de Fase 1 que nunca podaba): derivación PURA
    // dentro de este mismo useMemo, recalculada de punta a punta en cada corrida — el concat de
    // TODAS las fuentes de arriba (ya completas, sin gate de categoría) ANTES de `filtrarPorCorte`
    // y de la búsqueda, respetando SOLO el filtro de moneda (mismo criterio que cada mapper ya usa:
    // una sticky en USD no aparece en la vista CLP y viceversa). Al ser puro, si una fuente deja de
    // emitir una entry (factura pagada y vinculada, por ejemplo) esta misma corrida del memo ya no
    // la incluye — nunca queda una copia vieja de una corrida anterior.
    const todosPool = [
      ...facturadoEntries,
      ...factoringEntriesFiltrado,
      ...pipe,
      ...provsEntries,
      ...provsRecurrentesFiltrados,
      ...rrhh,
      ...impuestos,
      ...capex,
      ...compromisos,
      ...edificiosEntries,
      ...creditosEntries,
      ...cuotasCreditoEntries,
      ...desembolsoGarantiaEntries,
    ];
    // Clave siempre string: `flujo_caja_sticky.entry_id` es `text` en la base, pero `f.id` es number
    // en varias fuentes (facturado/pipeline spread el id numérico de la fila DB) y string con
    // prefijo en otras (`recurrente-${id}`, etc.) — normalizar acá evita que el lookup de abajo
    // (por `row.entry_id`, siempre string) falle por tipo.
    const poolMapSticky = new Map(todosPool.filter((e) => e?.id != null).map((e) => [String(e.id), e]));

    // ── Colapso de vencidos a HOY (solo frontend; la DB nunca se toca) ──────────────────────────
    // Punto ÚNICO: cualquier fila cuya fecha comprometida (fecha_display) ya pasó se "acumula" en el
    // día de hoy (corrido al día hábil si hoy cae fin de semana). Se preserva la fecha real en
    // `fecha_original` (rojo + hover de Fase 2) y se deja el flag `vencidoReal`. Va DESPUÉS de armar
    // todas las filas y ANTES del sort: así los vencidos entran al bloque de hoy y su importe se
    // acumula en el saldo proyectado en la posición de hoy. Es intencional (no es bug): el total final
    // (netoFuturos y el saldo de la última fila) no cambia porque la suma es conmutativa. `hoyLocalStr`
    // usa el huso REAL del país operativo (`hoyDelPais(pais)`, Fase 1b — reemplaza el huso del
    // NAVEGADOR que tenía antes), NO el `hoyStr` global (UTC) del resto del panel — feature-local.
    // `hoyHabil` parte del MISMO string (`parseLocalYMD(hoyLocalStr)`, Fase 1c) — antes mezclaba
    // `hoyLocalStr` (huso del país) para el corte de vencidos con `new Date()` (huso del NAVEGADOR)
    // para el próximo día hábil, dos nociones de "hoy" distintas en el mismo bloque.
    const hoyLocalStr = hoyDelPais(pais);
    const hoyHabil = proximoDiaHabil(parseLocalYMD(hoyLocalStr));
    // Nombrada (antes arrow inline) para reusarla con las entries sticky resueltas del pool
    // (mismo tratamiento de vencido/original, ver bloque Sticky más abajo) — mismo cuerpo, cero
    // cambio de comportamiento para `todos`.
    const aplicarColapsoVencido = (f) => {
      const fecha_original = f.fecha_display;
      const vencidoReal = fecha_original != null && fecha_original < hoyLocalStr;
      // Las filas con fecha nula no se colapsan: quedan al final del sort (null → "9999").
      return vencidoReal
        ? { ...f, fecha_original, vencidoReal: true, fecha_display: hoyHabil }
        : { ...f, fecha_original, vencidoReal: false };
    };
    todos = todos.map(aplicarColapsoVencido);

    todos.sort((a, b) => {
      // Nivel 1: fecha de pago/cobro proyectada asc (null al final). Entre fechas
      // distintas el orden es puramente cronológico (no cambia).
      const porFecha = (a.fecha_display || "9999").localeCompare(b.fecha_display || "9999");
      if (porFecha !== 0) return porFecha;
      // Nivel 2 (misma fecha): cobros/ingresos antes que egresos/pagos. La dirección se
      // deriva del SIGNO de importeEnMoneda, el mismo criterio que usa el color de fila
      // (importeEnMoneda < 0 → egreso; si no → cobro). Grupo 0 = cobro, grupo 1 = egreso.
      const grupoA = (a.importeEnMoneda ?? 0) < 0 ? 1 : 0;
      const grupoB = (b.importeEnMoneda ?? 0) < 0 ? 1 : 0;
      if (grupoA !== grupoB) return grupoA - grupoB;
      // Nivel 2.5 (SOLO visualización): las filas de RRHH (sueldo/comisión/bono/saldo) se AGRUPAN por
      // empleado dentro de la misma fecha, ordenadas alfabéticamente por su label ("RRHH | Nombre |
      // Sufijo" comparte prefijo → las filas de una misma persona quedan adyacentes). Se tratan como un
      // bloque aparte del resto (rrhh después de no-rrhh) para no reordenar las filas no-RRHH —que
      // siguen por magnitud— y para mantener el comparador transitivo. No toca montos ni cálculo.
      const rrhhA = a.tipo === "rrhh" ? 1 : 0;
      const rrhhB = b.tipo === "rrhh" ? 1 : 0;
      if (rrhhA !== rrhhB) return rrhhA - rrhhB;
      if (rrhhA === 1) return (a.razon_social_receptor || "").localeCompare(b.razon_social_receptor || "");
      // Nivel 3 (mismo grupo): de mayor a menor monto (por magnitud, como se muestra en la UI).
      const magA = Math.abs(a.importeEnMoneda ?? 0);
      const magB = Math.abs(b.importeEnMoneda ?? 0);
      if (magA !== magB) return magB - magA;
      // Desempate final estable entre facturas por fecha de emisión (la más antigua primero).
      if (a.fecha_emision && b.fecha_emision) {
        return String(a.fecha_emision).localeCompare(String(b.fecha_emision));
      }
      return 0;
    });

    // Búsqueda libre: nombre (razon_social_receptor), folio o monto absoluto redondeado.
    // Aplica DESPUÉS de filtrosActivos/corte temporal, antes del cálculo de saldo —
    // ojo que el saldo acumulado refleja sólo lo visible (mismo trade-off que el histórico).
    if (busquedaFuturos.trim()) {
      // Normalizar diacríticos para que "itau" matchee "ITAÚ" (y viceversa). Solo aplica al
      // nombre; folio y monto quedan igual (no llevan acentos).
      const sinAcentos = (s) =>
        (s || "")
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "");
      const q = sinAcentos(busquedaFuturos.trim());
      todos = todos.filter(
        (m) =>
          sinAcentos(m.razon_social_receptor).includes(q) ||
          // "Por pagar" muestra el alias; el buscador igual encuentra por la razón social del DTE
          // (m.razon_social_emisor viene en la entry por el spread ...f). Otros tipos no lo traen → "".
          sinAcentos(m.razon_social_emisor).includes(q) ||
          String(m.folio || "")
            .toLowerCase()
            .includes(q) ||
          String(Math.abs(Math.round(Number(m.importeEnMoneda) || 0))).includes(q)
      );
    }

    // ── Sticky: reordenar arriba, en el orden de fijado ─────────────────────────────────────────
    // Después del sort y de la búsqueda, ANTES del loop que acumula `saldo` — así el saldo
    // proyectado las descuenta primero (punto 3 de la spec). Se resuelven desde `poolMapSticky`
    // (arriba, derivación pura de este mismo render), no desde `todos` ya filtrado — por eso una
    // sticky sigue apareciendo aunque el filtro de categoría/corte/búsqueda la hubiera excluido.
    // `aplicarColapsoVencido` (mismo de arriba) les da el mismo tratamiento de vencido/fecha_original
    // que a cualquier otra fila.
    const stickyEntries = [];
    const idsStickyNoResueltos = [];
    for (const row of stickyRows) {
      const raw = poolMapSticky.get(row.entry_id);
      if (raw) {
        stickyEntries.push({ ...aplicarColapsoVencido(raw), es_sticky: true });
      } else {
        // Ya no existe en la fuente viva (pagada, vinculada, borrada) — al ser el pool una
        // derivación PURA de este mismo render (Fase 1b, 10.244), una ausencia acá es real, no un
        // "todavía no visitada" del cache viejo de Fase 1. Se ignora en pantalla, nunca en silencio
        // (10.177, logueado abajo). El DELETE de la tabla queda para una fase posterior (instrucción
        // explícita de Ignacio en Fase 1b): solo se loguea por ahora.
        idsStickyNoResueltos.push(row.entry_id);
      }
    }
    if (idsStickyNoResueltos.length > 0) {
      // eslint-disable-next-line no-console
      console.info("stickyFuturos.idsNoResueltos", { pais, ids: idsStickyNoResueltos });
    }
    const idsStickySet = new Set(stickyEntries.map((f) => String(f.id)));
    todos = [...stickyEntries, ...todos.filter((f) => !idsStickySet.has(String(f.id)))];

    // Saldo proyectado parte del saldo actual (ya en monedaDisplay). Las sticky van primero en
    // `todos` → el saldo acumulado las descuenta antes que al resto (punto 3 de la spec). El total
    // final no cambia: mismas filas, misma suma, solo cambia el camino (conmutativa).
    let saldo = saldoActual;
    return todos.map((f) => {
      saldo += f.importeEnMoneda;
      return { ...f, saldo_proyectado: saldo };
    });
  }, [
    pendientesCobro,
    pipeline,
    proveedoresPendientes,
    facturaSiiPorPagar,
    proveedoresRecurrentes,
    facturasExternasPendientes,
    saldosPorFacturaMap,
    provMonedaMap,
    nombreProvMap,
    residuosProveedoresPendientes,
    boletasHonorariosPendientes,
    yaVinculadasExternas,
    yaVinculadasEmitidas,
    cuotasPagosPendientes,
    cuotasCobrosPendientes,
    cuotasActivosPendientes,
    activosConPlan,
    compromisosEntries,
    cuotasCompromisoPendientes,
    compromisosConPlan,
    rrhhEntries,
    impuestosEntries,
    capexEntries,
    capexLotes,
    componentesLotes,
    edificiosMap,
    edificiosSaldo,
    planPagosEdificios,
    morasPendientes,
    cuotasCreditos,
    operacionesFactoring,
    filtrosActivos,
    filtroTemporalKey,
    rangoPersonalizado,
    monedaVista,
    monedaLocal,
    monedaDisplay,
    consolidado,
    saldoActual,
    convertir,
    busquedaFuturos,
    stickyRows,
  ]);

  const fmt = (n) =>
    monedaDisplay === "USD"
      ? `US$ ${Number(n).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`
      : `${simboloLocal} ${Number(n).toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;

  // Badge pill con el código corto de la moneda origen. Azul claro para USD, naranja/beige
  // para local. Se usa en la columna "Ref" cuando consolidado está activo.
  const renderBadgeMoneda = (monedaOrigen) => {
    if (!monedaOrigen) return null;
    const esUSD = monedaOrigen === "USD";
    const txt = esUSD ? "US$" : monedaLocal;
    return (
      <span
        style={{
          display: "inline-block",
          padding: "1px 6px",
          fontSize: "0.65rem",
          fontWeight: 700,
          letterSpacing: "0.04em",
          borderRadius: 3,
          background: esUSD ? "#e8f4f8" : "#fef5e7",
          color: esUSD ? "#2980b9" : "#d35400",
          border: `1px solid ${esUSD ? "#aed6f1" : "#fdebd0"}`,
          lineHeight: "14px",
        }}
      >
        {txt}
      </span>
    );
  };

  // Borde lateral izquierdo en filas cuya moneda origen difiere de la de display.
  // Sólo aplica en consolidado (cuando hay mezcla de orígenes posible).
  const bordeMonedaStyle = (monedaOrigen) => {
    if (!consolidado || !monedaOrigen || monedaOrigen === monedaDisplay) return undefined;
    const color = monedaOrigen === "USD" ? "#3498db" : "#e67e22";
    return { boxShadow: `inset 3px 0 0 ${color}` };
  };

  // Neto de las filas visibles del panel (sin el saldo de caja): Σ importeEnMoneda
  // de la proyección ya filtrada por tabs/corte/búsqueda. Cada fila trae el signo
  // baked-in (cobrar +, pagar −), así que el reduce da el flujo neto de lo mostrado.
  // A diferencia del saldo_proyectado de las filas, NO parte del saldo bancario actual.
  const netoVisibleFuturos = useMemo(
    () => proyeccionesFuturas.reduce((acc, f) => acc + (Number(f.importeEnMoneda) || 0), 0),
    [proyeccionesFuturas]
  );

  // Saldo final proyectado del conjunto filtrado = saldo actual + flujo acumulado.
  // NO se recalcula: es el saldo_proyectado de la última fila (proyeccionesFuturas ya
  // viene sembrada con saldoActual), así nunca difiere del último número de la columna
  // SALDO. Fallback a saldoActual cuando no hay filas visibles.
  const saldoFinalProyectado = useMemo(
    () => proyeccionesFuturas.at(-1)?.saldo_proyectado ?? saldoActual,
    [proyeccionesFuturas, saldoActual]
  );

  // Separador "Saldo post sticky" (Fase 2, docs/modulos/flujo-caja-v2.md § Sticky en Futuros):
  // los dos números salen de las filas `es_sticky` YA calculadas por `proyeccionesFuturas` (10.229
  // — mismo predicado/fuente que el detalle, nunca un criterio propio) — no se recalcula nada.
  // `stickyResueltas` son las que efectivamente resolvieron esta corrida (10.1/10.177: una sticky
  // que el pool no pudo resolver ya no aparece ni acá ni en la tabla, mismo criterio en los dos
  // lugares — ver el `idsStickyNoResueltos` del useMemo de arriba).
  const stickyResueltas = useMemo(() => proyeccionesFuturas.filter((f) => f.es_sticky), [proyeccionesFuturas]);
  const sumaSticky = useMemo(
    () => stickyResueltas.reduce((acc, f) => acc + (Number(f.importeEnMoneda) || 0), 0),
    [stickyResueltas]
  );
  // saldo_proyectado de la ÚLTIMA sticky (ya viene con las sticky descontadas primero, Fase 1) —
  // no `saldoActual + sumaSticky` recalculado: se lee directo del campo ya computado.
  const saldoTrasSticky = stickyResueltas.at(-1)?.saldo_proyectado ?? saldoActual;

  // "Soltar todas" del header (solo Ignacio) — mismo patrón optimista + reversión que fijar/soltar
  // individual (10.234/10.177).
  const handleSoltarTodasSticky = async () => {
    if (!puedeGestionarSticky(userId)) return;
    const previo = stickyRows;
    setStickyRows([]);
    try {
      await soltarTodasSticky(pais);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("stickyFuturos.soltarTodas", err);
      setStickyRows(previo);
      toast.error("No se pudieron soltar las filas fijadas");
    }
  };

  // Fijar/soltar una fila sticky (solo Ignacio, gate ya aplicado en el render antes de mostrar el
  // pin — esto es la escritura). Optimista + reversión ante error (10.234/10.177): actualiza el
  // state local antes de la query y lo revierte si la escritura falla, mostrando el error.
  const handleToggleSticky = async (f) => {
    if (!puedeGestionarSticky(userId)) return;
    if (f.es_sticky) {
      const previo = stickyRows;
      setStickyRows((cur) => cur.filter((r) => r.entry_id !== String(f.id)));
      try {
        await soltarEntrySticky(pais, f.id);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("stickyFuturos.soltar", err);
        setStickyRows(previo);
        toast.error("No se pudo soltar la fila fijada");
      }
      return;
    }
    const ordenMax = stickyRows.reduce((max, r) => Math.max(max, r.orden), 0);
    const filaOptimistaId = `optimista-${f.id}`;
    const previo = stickyRows;
    setStickyRows((cur) => [
      ...cur,
      { id: filaOptimistaId, pais, entry_id: String(f.id), orden: ordenMax + 1, creado_por: userId },
    ]);
    try {
      const fila = await fijarEntrySticky(pais, f.id, ordenMax, userId);
      setStickyRows((cur) => cur.map((r) => (r.id === filaOptimistaId ? fila : r)));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("stickyFuturos.fijar", err);
      setStickyRows(previo);
      toast.error("No se pudo fijar la fila");
    }
  };

  // Suma neta de los movimientos históricos visibles (ya filtrados por buscador + tab
  // Ingresos/Egresos + filtro temporal). Mismo criterio de signo que netoVisibleFuturos:
  // ingreso suma +, egreso resta −. Usa monto_display (moneda activa CLP/USD). Reacciona
  // en vivo al filtro porque depende de movimientosFiltrados. Reemplaza al contador de la
  // barra (la cantidad ya vive en el badge del toggle CLP/USD del header).
  const netoVisibleHistorico = useMemo(
    () =>
      movimientosFiltrados.reduce(
        (acc, m) => acc + (m.tipo === "ingreso" ? Number(m.monto_display || 0) : -Number(m.monto_display || 0)),
        0
      ),
    [movimientosFiltrados]
  );

  // El neto de cada barra se muestra SOLO con filtro activo (sirve para cuadrar lo visible);
  // sin filtro queda vacío. Históricos: default filtroHistorico='todo', filtroTiempo='ytd'.
  const hayFiltroHistorico =
    busquedaHistorico.trim() !== "" || filtroHistorico !== "todo" || filtroTiempo !== "ytd" || soloPendientes;
  // Futuros: default filtrosActivos=['todo'] (tab "Todo"), sin corte temporal, sin búsqueda.
  const hayFiltroFuturos =
    busquedaFuturos.trim() !== "" ||
    !filtrosActivos.includes("todo") ||
    filtroTemporalKey !== null ||
    rangoPersonalizado !== null;

  // Tab Backlog en exclusiva: la columna FOLIO pasa a REFERENCIA (identifica la venta/cuota
  // de origen, que aún no tiene folio SII). En cualquier otro tab sigue siendo FOLIO.
  const soloBacklog = filtrosActivos.length === 1 && filtrosActivos[0] === "backlog";
  // "MM-YYYY" → "MM/YY". El mes sale del dato real de la entry (no de hoy).
  const mmYY = (mmYYYY) => {
    if (!mmYYYY || typeof mmYYYY !== "string") return "";
    const [mm, yyyy] = mmYYYY.split("-");
    return mm && yyyy ? `${mm}/${yyyy.slice(-2)}` : "";
  };
  // Referencia de una entry del Backlog según su origen (regla 10.89: la metadata ya viaja).
  const refBacklog = (f) => {
    if (f.origen === "sales") return `#${f.sale_id}`;
    if (f.origen === "contratos") {
      const m = mmYY(f.mes);
      return m ? `C-${f.contrato_id} · ${m}` : `C-${f.contrato_id}`;
    }
    if (f.origen === "carteleria") {
      // fecha_estimada de cartelería = último día del mes de servicio ("YYYY-MM-DD").
      const [yyyy, mm] = (f.fecha_estimada || "").split("-");
      return yyyy && mm ? `${mm}/${yyyy.slice(-2)}` : "—";
    }
    return "—";
  };

  const gridRef = useRef(null);

  // Fuente única del estado "pendiente de vincular": mismo predicado isMovPendiente
  // y mismos sets (pagosMovIds + movimientosBancarioFactoring) que pintan la fila de
  // naranja en la tabla histórica (ver invocación de fila). Así el badge nunca diverge.
  const movPendientes = movBancarios.filter((m) =>
    isMovPendiente(m, pagosMovIds, movimientosBancarioFactoring, movsVinculadosLibro)
  );
  // Conteo de pendientes por DIRECCIÓN para los badges de los subtabs "Ingresos"/"Egresos"
  // (regla 10.1: mismos predicados esMovIngreso/esMovEgreso que filtran la lista en el subtab).
  // Se cuentan TODAS las monedas, NO la seleccionada en el toggle: un pendiente de vincular es un
  // "hueco" que todavía no impactó el ER como ingreso o egreso, hecho independiente de la moneda de
  // visualización — esconder los de la otra moneda ocultaría parte del hueco. Además así
  // pendientesIngreso + pendientesEgreso = el total que suma el tile "Caja" del Home
  // (getCountCajaPendientes → getCountMovimientosPendientesCaja NO filtra por moneda): se mantiene la
  // invariante "el tile muestra exactamente lo que se ve en pantalla".
  const pendientesIngreso = movPendientes.filter(esMovIngreso).length;
  const pendientesEgreso = movPendientes.filter(esMovEgreso).length;

  return (
    <div className={styles.wrapper}>
      <Header
        leftContent={
          <div style={{ display: "flex", alignItems: "center", gap: 32, marginLeft: 150 }}>
            <div
              style={{
                display: "flex",
                gap: 0,
                borderRadius: 6,
                overflow: "hidden",
                border: "1px solid #dee2e6",
              }}
            >
              {/* Selector de moneda puro: el conteo de pendientes ya NO vive acá — pasó a los badges
                  por dirección de los subtabs "Ingresos"/"Egresos" del panel Históricos. */}
              {[monedaLocal, "USD"].map((m) => {
                const activo = monedaVista === m;
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setMonedaVista(m)}
                    style={{
                      width: 108,
                      padding: "6px 0",
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      border: "none",
                      cursor: "pointer",
                      background: activo ? "#2c3e50" : "transparent",
                      color: activo ? "#fff" : "#666",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 4,
                fontSize: "0.75rem",
                color: "#444",
                userSelect: "none",
              }}
            >
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", margin: 0 }}>
                <input
                  type="checkbox"
                  checked={consolidado}
                  onChange={(e) => setConsolidado(e.target.checked)}
                  style={{ cursor: "pointer" }}
                />
                Consolidado
              </label>
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  cursor: monedaVista === "USD" ? "not-allowed" : "pointer",
                  margin: 0,
                  opacity: monedaVista === "USD" ? 0.4 : 1,
                }}
                title={monedaVista === "USD" ? "Ya estás viendo en USD" : undefined}
              >
                <input
                  type="checkbox"
                  checked={monedaVista === "USD" ? false : convertirAUSD}
                  disabled={monedaVista === "USD"}
                  onChange={(e) => setConvertirAUSD(e.target.checked)}
                  style={{ cursor: monedaVista === "USD" ? "not-allowed" : "pointer" }}
                />
                Ver en US$
              </label>
            </div>
          </div>
        }
      >
        <div className={styles.headerStats}>
          <div className={styles.statItem}>
            <div className={styles.statLabel}>Saldo actual</div>
            <div className={styles.statValue}>{fmt(saldoActual)}</div>
          </div>
          <button
            type="button"
            onClick={handleSyncBanco}
            disabled={sincronizandoBanco || sincronizandoSII}
            style={{
              width: 150,
              padding: "6px 0",
              fontSize: "0.78rem",
              fontWeight: 600,
              border: "1px solid #2c3e50",
              borderRadius: 6,
              background: sincronizandoBanco ? "#95a5a6" : "#2c3e50",
              color: "#fff",
              cursor: sincronizandoBanco || sincronizandoSII ? "not-allowed" : "pointer",
              marginLeft: 12,
            }}
          >
            {sincronizandoBanco ? "Actualizando..." : "Actualizar banco"}
          </button>
          <button
            type="button"
            onClick={handleSyncSII}
            disabled={sincronizandoBanco || sincronizandoSII}
            style={{
              width: 150,
              padding: "6px 0",
              fontSize: "0.78rem",
              fontWeight: 600,
              border: "1px solid #6c3483",
              borderRadius: 6,
              background: sincronizandoSII ? "#95a5a6" : "#6c3483",
              color: "#fff",
              cursor: sincronizandoBanco || sincronizandoSII ? "not-allowed" : "pointer",
              marginLeft: 8,
            }}
          >
            {sincronizandoSII ? "Actualizando..." : `Actualizar ${enteFiscal}`}
          </button>
        </div>
      </Header>

      <div className={styles.content}>
        <div
          className={`${styles.grid} ${
            panelMaximizado === "historico"
              ? styles.maxHistorico
              : panelMaximizado === "futuros"
                ? styles.maxFuturos
                : ""
          }`}
          ref={gridRef}
        >
          <div className={styles.connector} />

          {/* Panel izquierdo */}
          <div className={`${styles.panelWrapper} ${styles.panelHistorico}`}>
            <div
              className={styles.panelHeader}
              style={{
                background: "#2c3e50",
                display: "flex",
                alignItems: "center",
                position: "relative",
              }}
            >
              <span>Históricos{ultimaConsulta ? " |" : ""}</span>
              {ultimaConsulta && (
                <span className={styles.ultimaSync}>Última carga: {format(ultimaConsulta, "dd/MM · HH:mm")}</span>
              )}
              <input
                type="text"
                placeholder="Buscar movimiento..."
                value={busquedaHistorico}
                onChange={(e) => setBusquedaHistorico(e.target.value)}
                className={styles.searchInputDark}
              />
              <div className={styles.headerRight}>
                {hayFiltroHistorico && (
                  <span style={{ fontSize: "0.85rem", fontWeight: 700, opacity: 0.95, color: "#fff" }}>
                    {fmt(netoVisibleHistorico)}
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => setPanelMaximizado((p) => (p === "historico" ? null : "historico"))}
                  className={`${styles.pinBtn} ${panelMaximizado === "historico" ? styles.pinBtnActive : ""}`}
                  title={panelMaximizado === "historico" ? "Restaurar vista" : "Maximizar panel"}
                  aria-label={panelMaximizado === "historico" ? "Restaurar vista" : "Maximizar panel"}
                  aria-pressed={panelMaximizado === "historico"}
                >
                  <FaThumbtack />
                </button>
              </div>
            </div>
            <div
              className={styles.filterBar}
              style={{ display: "flex", alignItems: "center", gap: 12, justifyContent: "space-between" }}
            >
              <div style={{ display: "flex", gap: 4 }}>
                {FILTROS_HISTORICO.map((f) => {
                  const btn = (
                    <button
                      type="button"
                      onClick={() => setFiltroHistorico(f.key)}
                      className={`${styles.filterBtn} ${filtroHistorico === f.key ? styles.filterBtnActive : ""}`}
                    >
                      {f.label}
                    </button>
                  );
                  // Badge rojo de pendientes de vincular por dirección — solo "Ingresos"/"Egresos"
                  // (mismo estilo que el badge "Por pagar" de Futuros, para consistencia visual).
                  const pendCount =
                    f.key === "ingresos" ? pendientesIngreso : f.key === "egresos" ? pendientesEgreso : 0;
                  // Gate anti-parpadeo: el conteo depende de movBancarios (loader Históricos) MÁS los
                  // sets de exclusión pagosMovIds + movimientosBancarioFactoring, que llegan de AMBOS
                  // loaders (Históricos y Futuros). `loading` sube al arrancar cargarDatos y baja recién
                  // cuando los dos terminaron (setLoading(false) en el finally), así que hasta entonces
                  // isMovPendiente corre sobre sets vacíos y cuenta de más. Sin gate el badge arrancaría
                  // inflado y bajaría al valor final; con él aparece una sola vez, ya estable — mismo
                  // espíritu que "Por pagar", que queda en 0 hasta que sus inputs terminan de cargar.
                  if (!loading && pendCount > 0) {
                    return (
                      <span key={f.key} style={{ position: "relative", display: "inline-block" }}>
                        {btn}
                        <span
                          style={{
                            position: "absolute",
                            top: -6,
                            right: -6,
                            background: "#c62828",
                            color: "#fff",
                            fontSize: "0.55rem",
                            fontWeight: 700,
                            minWidth: 14,
                            height: 14,
                            borderRadius: 7,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "0 3px",
                            pointerEvents: "none",
                            zIndex: 1,
                          }}
                        >
                          {pendCount}
                        </span>
                      </span>
                    );
                  }
                  return <span key={f.key}>{btn}</span>;
                })}
                {/* Toggle independiente (soloPendientes), NO un valor de filtroHistorico.
                    4º botón del grupo solo por posición; su lógica es combinable. */}
                <button
                  type="button"
                  onClick={() => setSoloPendientes((v) => !v)}
                  className={`${styles.filterBtn} ${soloPendientes ? styles.filterBtnActive : ""}`}
                >
                  Pendiente
                </button>
              </div>
              {/* Toggle de atención (⚠): filtro AND independiente, centrado entre los dos grupos.
                  Enabled solo si hay forzados pendientes en la vista base (hayAtencionEnVista);
                  si no, gris + not-allowed (el auto-clear ya lo apaga si se queda sin ⚠). */}
              <div style={{ display: "flex", alignItems: "center" }}>
                <button
                  type="button"
                  onClick={() => setSoloAtencion((v) => !v)}
                  disabled={!hayAtencionEnVista}
                  title={
                    hayAtencionEnVista
                      ? "Mostrar solo movimientos con forzado pendiente"
                      : "Sin forzados pendientes en la vista actual"
                  }
                  className={`${styles.filterBtn} ${soloAtencion ? styles.filterBtnActive : ""}`}
                  style={{
                    // Solo tiene el ícono: cuadradito compacto. Achica ancho (min-width + padding
                    // horizontal) SIN tocar el padding vertical (5px de filterBtn) → alto intacto.
                    minWidth: 0,
                    paddingLeft: 7,
                    paddingRight: 7,
                    ...(!hayAtencionEnVista ? { cursor: "not-allowed", opacity: 0.45 } : {}),
                  }}
                >
                  <span
                    style={{
                      color: hayAtencionEnVista ? "#e67e22" : "#adb5bd",
                      fontSize: 15,
                      lineHeight: 1,
                      fontWeight: 700,
                      display: "inline-block",
                      verticalAlign: "middle",
                    }}
                  >
                    {"⚠︎"}
                  </span>
                </button>
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                {[
                  { key: "hoy", label: "Hoy" },
                  { key: "esta_semana", label: "Semana" },
                  { key: "este_mes", label: "Mes" },
                  { key: "ytd", label: "YTD" },
                ].map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    onClick={() => setFiltroTiempo(f.key)}
                    className={`${styles.filterBtn} ${filtroTiempo === f.key ? styles.filterBtnActive : ""}`}
                  >
                    {f.label}
                  </button>
                ))}
                <select
                  value={filtroTiempo.startsWith("mes_") ? filtroTiempo : ""}
                  onChange={(e) => e.target.value && setFiltroTiempo(e.target.value)}
                  style={{
                    padding: "5px 8px",
                    fontSize: "0.78rem",
                    border: "1px solid #dee2e6",
                    borderRadius: 4,
                    background: filtroTiempo.startsWith("mes_") ? "#2c3e50" : "#fff",
                    color: filtroTiempo.startsWith("mes_") ? "#fff" : "#333",
                    cursor: "pointer",
                  }}
                >
                  <option value="">Seleccionar…</option>
                  {mesesDropdown.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.panel} style={{ borderRadius: "0 0 6px 6px" }}>
              {loading ? (
                <div className={styles.empty}>Cargando...</div>
              ) : movimientosFiltrados.length === 0 ? (
                <div className={styles.empty}>Sin movimientos</div>
              ) : (
                <table className={`${styles.table} ${styles.tableHistorico}`}>
                  <colgroup>
                    {panelMaximizado === "historico" && <col style={{ width: "5%" }} />}
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "7%" }} />
                    <col style={{ width: panelMaximizado === "historico" ? "39%" : "44%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "7%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      {panelMaximizado === "historico" && <th className={styles.idCol}>ID</th>}
                      <th>Fecha</th>
                      <th colSpan={2}>Movimiento</th>
                      <th className={styles.right}>Monto</th>
                      <th className={styles.right}>Saldo</th>
                      <th className={styles.thCenter}>{consolidado ? "Ref" : "Edit"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimientosFiltrados.map((m) => (
                      <tr
                        key={m.id}
                        className={
                          isMovPendiente(m, pagosMovIds, movimientosBancarioFactoring, movsVinculadosLibro)
                            ? styles.pendiente
                            : ""
                        }
                      >
                        {panelMaximizado === "historico" && <td className={styles.idCol}>{m.id}</td>}
                        <td className={styles.fecha} style={bordeMonedaStyle(m.moneda_origen)}>
                          {formatFecha(m.fecha)}
                        </td>
                        <td className={styles.folio}>
                          {renderBadgeMovimiento(
                            m,
                            pagosMovIds,
                            enteFiscal,
                            movimientosBancarioFactoring,
                            mapaFactoring,
                            facturaInfoMap,
                            facturaRecibidaInfoMap,
                            boletaHonorarioInfoMap,
                            capexResumenMap,
                            pagosEmpleadosPorMov,
                            recurrenteResumenMap,
                            movsCobroSIILibro,
                            movsPagoRecibidaLibro
                          )}
                        </td>
                        <td className={styles.descripcion} title={m.descripcion_raw} style={{ position: "relative" }}>
                          {m.descripcion_raw}
                          {renderMarcaForzado(m, forzadosMap)}
                          {renderCapexDetalleInline(m, capexResumenMap, panelMaximizado)}
                        </td>
                        <td className={`${styles.right} ${m.tipo === "ingreso" ? styles.ingreso : styles.egreso}`}>
                          {fmt(m.monto_display ?? m.monto)}
                        </td>
                        <td className={styles.right}>{fmt(m.saldo_calculado)}</td>
                        <td className={styles.accion}>
                          {consolidado ? (
                            renderBadgeMoneda(m.moneda_origen)
                          ) : (
                            <FaEdit
                              title="Vincular"
                              style={{ cursor: "pointer", color: "#999", fontSize: "1em" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setVincularModal(m);
                              }}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Panel derecho */}
          <div className={`${styles.panelWrapper} ${styles.panelFuturos}`}>
            <div
              className={styles.panelHeader}
              style={{
                background: "#27ae60",
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "nowrap",
              }}
            >
              <span style={{ whiteSpace: "nowrap", flexShrink: 0 }}>Futuros</span>
              <span aria-hidden="true" style={{ color: "#fff", opacity: 0.4 }}>
                |
              </span>
              <div className={styles.periodoControl} ref={periodoMenuRef}>
                {(() => {
                  const periodoActivoLabel = filtroTemporalKey
                    ? calcularFiltrosTemporales().find((f) => f.key === filtroTemporalKey)?.label
                    : null;
                  const rangoLabel = rangoPersonalizado
                    ? `${formatFecha(rangoPersonalizado.desde)}–${formatFecha(rangoPersonalizado.hasta)}`
                    : null;
                  const triggerLabel = rangoLabel || periodoActivoLabel;
                  return (
                    <button
                      type="button"
                      onClick={() => setMenuPeriodoAbierto((v) => !v)}
                      className={`${styles.bannerBtn} ${triggerLabel ? styles.bannerBtnActive : ""}`}
                      title="Filtrar por período"
                      aria-label={triggerLabel ? `Período: ${triggerLabel}` : "Filtrar por período"}
                      aria-haspopup="true"
                      aria-expanded={menuPeriodoAbierto}
                    >
                      {triggerLabel || <FaRegCalendarAlt style={{ fontSize: "0.85rem" }} />}
                    </button>
                  );
                })()}
                {menuPeriodoAbierto && (
                  <div className={styles.periodoMenu}>
                    {calcularFiltrosTemporales().map((f) => (
                      <button
                        key={f.key}
                        type="button"
                        className={`${styles.periodoMenuItem} ${
                          filtroTemporalKey === f.key ? styles.periodoMenuItemActive : ""
                        }`}
                        onClick={() => seleccionarPeriodo(f.key)}
                      >
                        {f.label}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`${styles.periodoMenuItem} ${rangoPersonalizado ? styles.periodoMenuItemActive : ""}`}
                      onClick={abrirRangoForm}
                    >
                      Rango de fechas
                    </button>
                    {mostrarRangoForm && (
                      <div className={styles.periodoRangoForm}>
                        <label>
                          Desde
                          <input
                            type="date"
                            value={rangoDesdeInput}
                            onChange={(e) => setRangoDesdeInput(e.target.value)}
                          />
                        </label>
                        <label>
                          Hasta
                          <input
                            type="date"
                            value={rangoHastaInput}
                            onChange={(e) => setRangoHastaInput(e.target.value)}
                          />
                        </label>
                        <button
                          type="button"
                          className={styles.periodoRangoAplicar}
                          onClick={aplicarRangoPersonalizado}
                          disabled={!rangoDesdeInput || !rangoHastaInput}
                        >
                          Aplicar
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setMostrarCompromisoAltaModal(true)}
                className={styles.pinBtn}
                title="Nuevo compromiso de pago"
                aria-label="Nuevo compromiso de pago"
              >
                <FaPlus />
              </button>
              <input
                type="text"
                placeholder="Buscar movimiento..."
                value={busquedaFuturos}
                onChange={(e) => setBusquedaFuturos(e.target.value)}
                className={styles.searchInputLight}
              />
              {/* Contador + "Soltar todas" (Fase 2, docs/modulos/flujo-caja-v2.md § Sticky en
                  Futuros): a la IZQUIERDA de .headerRight (que tiene su propio margin-left:auto),
                  nunca adentro — así el grupo saldo neto + pin de maximizar no se desplaza. */}
              {stickyResueltas.length > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", flexShrink: 0 }}>
                  <span className={styles.stickyContador}>{stickyResueltas.length} sticky</span>
                  {puedeGestionarSticky(userId) && (
                    <button
                      type="button"
                      className={styles.soltarTodasBtn}
                      onClick={handleSoltarTodasSticky}
                      title="Soltar todas las filas fijadas"
                      aria-label="Soltar todas las filas fijadas"
                    >
                      <FaThumbtack /> Soltar todas
                    </button>
                  )}
                </div>
              )}
              <div className={styles.headerRight}>
                {hayFiltroFuturos && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    <span style={{ fontSize: "0.85rem", fontWeight: 700, opacity: 0.95, color: "#fff" }}>
                      {fmt(modoHeaderFuturos === "saldo" ? saldoFinalProyectado : netoVisibleFuturos)}
                    </span>
                    <button
                      type="button"
                      className={styles.headerModeBtn}
                      onClick={() => setModoHeaderFuturos((m) => (m === "neto" ? "saldo" : "neto"))}
                      title={
                        modoHeaderFuturos === "saldo"
                          ? "Saldo final proyectado (saldo actual + flujo del período)"
                          : "Neto de los movimientos seleccionados"
                      }
                      aria-label={
                        modoHeaderFuturos === "saldo"
                          ? "Saldo final proyectado (saldo actual + flujo del período)"
                          : "Neto de los movimientos seleccionados"
                      }
                    >
                      {modoHeaderFuturos === "saldo" ? "S" : "N"}
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setPanelMaximizado((p) => (p === "futuros" ? null : "futuros"))}
                  className={`${styles.pinBtn} ${panelMaximizado === "futuros" ? styles.pinBtnActive : ""}`}
                  title={panelMaximizado === "futuros" ? "Restaurar vista" : "Maximizar panel"}
                  aria-label={panelMaximizado === "futuros" ? "Restaurar vista" : "Maximizar panel"}
                  aria-pressed={panelMaximizado === "futuros"}
                >
                  <FaThumbtack />
                </button>
              </div>
            </div>
            <div className={`${styles.filterBar} ${styles.filterBarFull}`}>
              {FILTROS_PROYECCION.map((f) => {
                const btn = (
                  <button
                    type="button"
                    onClick={() => toggleFiltro(f.key)}
                    className={`${styles.filterBtn} ${filtrosActivos.includes(f.key) ? styles.filterBtnActive : ""}`}
                  >
                    {f.label}
                  </button>
                );
                // Ningún subtab de Futuros lleva badge. "Por pagar" tenía un contador de pendientes
                // (countPorPagarPendientes), obsoleto desde que la vinculación de facturas a proveedores
                // se resuelve en Facturación > Compras: el contador ya no aporta (decisión 2026-08-29).
                // El subtab se mantiene para ver el detalle; solo se quitó el badge.
                return <span key={f.key}>{btn}</span>;
              })}
            </div>
            <div className={styles.panel} style={{ borderRadius: "0 0 6px 6px" }}>
              {/* El panel se muestra "listo" recién cuando TODAS las fuentes de proyeccionesFuturas
                  terminaron: los 4 batches de cargarDatosFuturos (futurosListos) MÁS las 3 fuentes de
                  rrhhEntries que cargan por afuera (hook RRHH vía isEmpleadosRRHHLoading, haberes y
                  fechas de pago). Mientras falte cualquiera, "Cargando..." — evita los repintes de
                  RRHH que llegaban tarde (Fase 1.5). */}
              {!(futurosListos && !isEmpleadosRRHHLoading && haberesListos && fechasPagoListos) ? (
                <div className={styles.empty}>Cargando...</div>
              ) : proyeccionesFuturas.length === 0 ? (
                <div className={styles.empty}>Sin proyecciones</div>
              ) : (
                <table className={styles.table}>
                  {/* Anchos fijos por <colgroup> (mismo criterio que la tabla de Históricos arriba,
                      mismo comentario ahí): con table-layout:fixed, una fila con colSpan (el
                      separador "Saldo post sticky") no puede derivar anchos correctos solo de
                      nth-child — el colgroup los fija a nivel tabla, independiente de la forma de
                      cada fila. Mismos % que las reglas nth-child de abajo (10/36/15/16/16/7). */}
                  <colgroup>
                    <col style={{ width: "10%" }} />
                    <col style={{ width: "36%" }} />
                    <col style={{ width: "15%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "16%" }} />
                    <col style={{ width: "7%" }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Concepto</th>
                      <th>{soloBacklog ? "Referencia" : "Folio"}</th>
                      <th>Monto</th>
                      <th>Saldo</th>
                      <th>{consolidado ? "Ref" : "Edit"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Fila fija "Saldo actual": solo en el panel Futuros maximizado.
                        Punto de partida (saldoActual, la MISMA semilla desde la que
                        proyeccionesFuturas acumula el saldo_proyectado de cada fila).
                        Puramente visual: no es un movimiento_caja, no toca DB, no se edita. */}
                    {panelMaximizado === "futuros" && (
                      <tr className={styles.filaSaldoActual}>
                        <td className={styles.fecha}>{formatFecha(hoyStr)}</td>
                        <td className={styles.descripcion}>Saldo actual</td>
                        <td className={styles.folio} />
                        <td className={styles.right} />
                        <td className={`${styles.right} ${saldoActual >= 0 ? styles.ingreso : styles.egreso}`}>
                          {fmt(saldoActual)}
                        </td>
                        <td className={styles.accion} />
                      </tr>
                    )}
                    {proyeccionesFuturas.map((f, i) => (
                      <Fragment key={`${f.id}-${i}`}>
                        <tr
                          className={f.es_sticky ? styles.filaSticky : ""}
                          style={{ cursor: f.tipo === "facturado" ? "pointer" : "default" }}
                          onClick={() => {
                            if (f.tipo === "facturado") {
                              setPlanCuotasModal({ factura: f, tipo: "cobro", accionInicial: "fecha" });
                            }
                          }}
                        >
                          <CeldaFechaReagendable
                            f={f}
                            ctx={ctxReagendarFuturo}
                            onSaved={aplicarPatchReagendado}
                            className={`${styles.fecha} ${!f.fecha_display ? styles.sinFecha : ""}`}
                            // El color de la fecha lo lleva la pill según el ESTADO (vencido/override), no
                            // el `style` del td: acá solo queda el borde por moneda. (El rojo suelto de
                            // Fase 1 se movió a la pill roja de abajo.)
                            style={{ ...(bordeMonedaStyle(f.moneda_origen) || {}) }}
                          >
                            {f.fecha_display ? (
                              // Precedencia estricta: vencido > override > normal.
                              // 1) Vencido (f.vencidoReal): pill ROJO clarito, SIEMPRE gana (aunque tenga
                              //    override); hover con la fecha comprometida real + días vencidos.
                              // 2) Override futuro (f.tiene_override y NO vencido): pill AZUL clarito.
                              // 3) Normal: sin pill, texto normal.
                              f.vencidoReal ? (
                                <span style={PILL_FECHA_VENCIDO} title={tituloVencida(f.fecha_original, pais)}>
                                  {formatFecha(f.fecha_display)}
                                </span>
                              ) : f.tiene_override ? (
                                <span style={PILL_FECHA_OVERRIDE} title={tituloOverride(f.fecha_original_plan)}>
                                  {formatFecha(f.fecha_display)}
                                </span>
                              ) : f.es_sticky ? (
                                // Sticky sin pill (ni vencida ni override): texto atenuado (punto 6 de la
                                // spec). La pill roja/azul de arriba sigue mandando igual — 10.278 intacto.
                                <span className={styles.fechaStickyAtenuada}>{formatFecha(f.fecha_display)}</span>
                              ) : (
                                formatFecha(f.fecha_display)
                              )
                            ) : (
                              "Sin fecha"
                            )}
                          </CeldaFechaReagendable>
                          <td className={styles.descripcion}>
                            {f.razon_social_receptor || "—"}
                            {f.estimado && (
                              <span
                                style={{
                                  marginLeft: 6,
                                  fontSize: "0.68rem",
                                  background: "#f39c12",
                                  color: "#fff",
                                  borderRadius: 3,
                                  padding: "1px 5px",
                                }}
                              >
                                Estimado
                              </span>
                            )}
                            {f._parcial && (
                              <span
                                style={{ marginLeft: 6, color: "#e67e22", fontSize: "0.68rem", fontStyle: "italic" }}
                              >
                                parcial
                              </span>
                            )}
                          </td>
                          <td className={styles.folio}>
                            {soloBacklog ? (
                              refBacklog(f)
                            ) : f.folio ? (
                              f.cuota_actual ? (
                                `${f.folio} (${f.cuota_actual}/${f.total_cuotas})`
                              ) : (
                                `${f.folio}`
                              )
                            ) : f.es_residuo ? (
                              <span style={{ color: "#999" }}>Sin folio</span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td
                            className={`${styles.right} ${
                              f.tipo === "pipeline"
                                ? styles.pipeline
                                : f.importeEnMoneda < 0
                                  ? styles.egreso
                                  : styles.ingreso
                            }`}
                          >
                            {fmt(Math.abs(f.importeEnMoneda))}
                          </td>
                          <td className={`${styles.right} ${f.saldo_proyectado >= 0 ? styles.ingreso : styles.egreso}`}>
                            {fmt(f.saldo_proyectado)}
                          </td>
                          <td className={styles.accion}>
                            {consolidado ? (
                              renderBadgeMoneda(f.moneda_origen)
                            ) : f.es_lote && f.tipo === "capex" ? (
                              // Líneas de LOTE (FOB + componentes): no pasan esEntryEditable (por el guard
                              // !es_lote), así que llevan su propio kebab MÍNIMO de un solo ítem — solo
                              // "Modificar fecha de pago". El resto del menú del activo (plan, marcar pagado)
                              // no aplica: ejecutar un lote se hace desde Activos, no desde acá.
                              <KebabMenu
                                minWidth={210}
                                buttonClassName={styles.kebabAccion}
                                items={[
                                  {
                                    label: "Modificar fecha de pago",
                                    onClick: () => setLoteFechaModal(f),
                                  },
                                ]}
                              />
                            ) : esEntryEditable(f) && f.tipo === "capex" ? (
                              // CapEx: menú de acciones (planificar fecha de proyección + marcar pagado),
                              // en vez del lápiz único. Los de lote/Stock (es_lote) no pasan esEntryEditable.
                              <KebabMenu
                                minWidth={210}
                                buttonClassName={styles.kebabAccion}
                                items={
                                  f.tiene_plan
                                    ? [
                                        // Línea de cuota (activo con plan): solo editar el plan. La
                                        // conciliación por cuota es el commit 4; la fecha de cada cuota
                                        // se edita dentro del plan.
                                        {
                                          label: "Editar plan de pagos",
                                          onClick: () => setCapexPlanModal(f),
                                        },
                                      ]
                                    : [
                                        // Línea de activo sin plan: los 3 ítems.
                                        {
                                          label: "Modificar fecha de pago",
                                          onClick: () => setCapexFechaModal(f),
                                        },
                                        {
                                          label: "Agregar plan de pagos",
                                          onClick: () => setCapexPlanModal(f),
                                        },
                                        {
                                          label: "Confirmar pago ejecutado",
                                          onClick: () => setCapexPagadoModal(f),
                                        },
                                      ]
                                }
                              />
                            ) : esEntryEditable(f) && esFacturaSiiRecibidaPorPagar(f) ? (
                              // Factura de proveedor real (SII recibida, externa o boleta) en "Por pagar":
                              // kebab. Boleta (Fase 5b) → "Plan de cuotas" (abre directo en cuotas; no tiene
                              // "Modificar fecha de pago"). SII/externa → "Modificar fecha de pago" (y de ahí a
                              // cuotas) — vocabulario unificado con la pestaña homónima de PlanCuotasModal
                              // (B.3 Commit 8, 2026-09-27). "Asociar pagos de CapEx" y "Pagada por caja chica"
                              // son SII-recibida-only (no externa ni boleta).
                              <KebabMenu
                                minWidth={210}
                                buttonClassName={styles.kebabAccion}
                                items={[
                                  f.es_boleta
                                    ? {
                                        label: "Plan de cuotas",
                                        onClick: () =>
                                          setPlanCuotasModal({ factura: f, tipo: "pago", accionInicial: "cuotas" }),
                                      }
                                    : {
                                        label: "Modificar fecha de pago",
                                        onClick: () =>
                                          setPlanCuotasModal({ factura: f, tipo: "pago", accionInicial: "fecha" }),
                                      },
                                  // CapEx cuelga de sii_recibida → solo SII recibidas (no externa, no boleta).
                                  // Con asociación ACTIVA → "Desasociar" (modo reversa del modal); sin ella →
                                  // "Asociar". Misma decisión por ID y misma fuente que ComprasTab (10.1/10.7);
                                  // el modal se abre igual (se autocorrige por getAsociacionActiva al montar).
                                  ...(f.es_externa || f.es_boleta
                                    ? []
                                    : [
                                        {
                                          label: asociacionesCapex.has(f.id)
                                            ? "Desasociar pagos de CapEx"
                                            : "Asociar pagos de CapEx",
                                          onClick: () =>
                                            setAsociarCapexModal({ facturaId: f.id, proveedorId: f.proveedor_id }),
                                        },
                                        // Cuotas y caja chica no coexisten (antes lo lockeaba destinoComprometido
                                        // en PlanCuotasModal). Una factura con plan se proyecta como líneas de
                                        // cuota, y solo esas llevan `cuota_id` (fila de plan_pagos_facturas) →
                                        // disabled. El util repite el chequeo contra la base al guardar.
                                        {
                                          label: "Pagada por caja chica",
                                          onClick: () => setCajaChicaModal(f),
                                          disabled: f.cuota_id != null,
                                          tooltip:
                                            f.cuota_id != null
                                              ? "Tiene plan de cuotas: no puede pagarse por caja chica"
                                              : undefined,
                                        },
                                      ]),
                                ]}
                              />
                            ) : f.tipo === "compromiso" && !f.tiene_plan ? (
                              // Compromiso planificado SIN plan: kebab con "Modificar fecha estimada"
                              // (edita compromisos_pago.fecha_estimada; no toca pago/ledger/factura). Los
                              // que tienen plan editan la fecha de cada cuota desde el plan (ficha del proveedor).
                              <KebabMenu
                                minWidth={210}
                                buttonClassName={styles.kebabAccion}
                                items={[
                                  {
                                    label: "Editar",
                                    onClick: () => setCompromisoFechaModal(f),
                                  },
                                  {
                                    label: "Eliminar compromiso",
                                    danger: true,
                                    // Borrado LÓGICO (eliminado=true); getCompromisos filtra eliminado=false,
                                    // así que desaparece de "Por pagar" tras el refetch. Confirmación simple.
                                    onClick: async () => {
                                      if (
                                        !window.confirm(
                                          "¿Eliminar este compromiso de pago? Dejará de proyectarse en Por pagar."
                                        )
                                      )
                                        return;
                                      try {
                                        await eliminarCompromiso(f.compromiso_id);
                                        toast.success("Compromiso eliminado");
                                        // Patch puntual (Fase 2C): sacar el compromiso del estado por id (10.7),
                                        // sin refetch. Un borrado lógico lo excluye del loader; replicamos eso.
                                        // Se limpian ambas fuentes de proyección (línea única + cuotas si tuviera).
                                        setCompromisosEntries((prev) => prev.filter((c) => c.id !== f.compromiso_id));
                                        setCuotasCompromisoPendientes((prev) =>
                                          prev.filter((c) => c.compromiso_id !== f.compromiso_id)
                                        );
                                      } catch (e) {
                                        toast.error(`Error al eliminar: ${e.message || e}`);
                                      }
                                    },
                                  },
                                ]}
                              />
                            ) : esEntryEditable(f) ? (
                              <FaEdit
                                title="Editar"
                                style={{ cursor: "pointer", color: "#999", fontSize: "1em" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (f.tipo === "pipeline") {
                                    setEntryEditarFecha(f);
                                  } else if (f.tipo === "facturado") {
                                    setPlanCuotasModal({ factura: f, tipo: "cobro", accionInicial: "cuotas" });
                                  } else if (f.tipo === "proveedor") {
                                    // Externas y SII recibidas reales → planificar fecha de pago.
                                    // Recurrentes/residuos no llegan acá: no renderizan ícono editable.
                                    setPlanCuotasModal({ factura: f, tipo: "pago", accionInicial: "fecha" });
                                  } else if (f.tipo === "edificio") {
                                    setPagoEdificioModal(f.edificioData);
                                  }
                                }}
                              />
                            ) : esFilaOverrideF29(f) && RUT_EMPRESA_POR_PAIS[pais] ? (
                              <FaEdit
                                title="Cargar valor del contador"
                                style={{ cursor: "pointer", color: "#999", fontSize: "1em" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOverrideF29Modal({
                                    entry: f,
                                    campo: f.id.startsWith("ppm") ? "ppm" : "iut",
                                    periodo: periodoDeImpuesto(f),
                                  });
                                }}
                              />
                            ) : f.es_sueldo_liquido && esGerente(userId) ? (
                              <FaEdit
                                title="Modificar fecha de pago"
                                style={{ cursor: "pointer", color: "#999", fontSize: "1em" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEntrySueldoFecha(f);
                                }}
                              />
                            ) : esRecurrenteEditable(f) ? (
                              // Ocurrencia recurrente (ej. "Oficina" / Troncoso): correr la fecha de
                              // ESE pago puntual. Escribe en pago_recurrente_fecha; no toca el monto.
                              <FaEdit
                                title="Modificar fecha de pago"
                                style={{ cursor: "pointer", color: "#999", fontSize: "1em" }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEntryRecurrenteFecha(f);
                                }}
                              />
                            ) : (
                              <span style={{ color: "#ccc", fontSize: "0.85rem" }}>—</span>
                            )}
                            {/* Pin sticky (docs/modulos/flujo-caja-v2.md § Sticky en Futuros): a la derecha
                              del lápiz/kebab, en TODAS las filas de Futuros (saldos pendientes y residuos
                              incluidos). Solo Ignacio ve el pin (10.219); el resto ve la fila sticky arriba
                              igual, sin el control. stopPropagation: nunca abre el modal de edición. */}
                            {puedeGestionarSticky(userId) && stickyLoaded && (
                              <FaThumbtack
                                role="button"
                                tabIndex={0}
                                title={f.es_sticky ? "Soltar" : "Fijar arriba"}
                                aria-label={f.es_sticky ? "Soltar" : "Fijar arriba"}
                                className={`${styles.pinFila} ${f.es_sticky ? styles.pinFilaActivo : ""}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleSticky(f);
                                }}
                              />
                            )}
                          </td>
                        </tr>
                        {/* Separador "Saldo post sticky" (docs/modulos/flujo-caja-v2.md § Sticky en
                          Futuros): inmediatamente debajo de la ÚLTIMA sticky (las sticky son SIEMPRE
                          un prefijo contiguo de `proyeccionesFuturas`, Fase 1/1b) — se detecta el
                          borde comparando con la fila siguiente, sin asumir posición fija. No es una
                          entry: no entra en proyeccionesFuturas ni en ningún cálculo (10.244); sus dos
                          números salen de `stickyResueltas`/`sumaSticky`/`saldoTrasSticky` ya
                          derivados arriba. Se muestra en las DOS vistas (maximizada y 50/50) porque
                          vive DENTRO del mismo `.map()`, sin gate de `panelMaximizado`. */}
                        {f.es_sticky && !proyeccionesFuturas[i + 1]?.es_sticky && (
                          <tr className={styles.filaSaldoPostSticky}>
                            {/* FECHA+CONCEPTO unificadas en una sola celda (colSpan=2, ajuste pedido por
                                Ignacio 2026-09-27): el texto arranca en la columna FECHA en vez de dejarla
                                vacía. `.descripcion` ya alinea a la izquierda con el mismo padding que
                                `.fecha` (ambas heredan `padding:6px 10px` de `.table td`, sin overrides
                                propios). El <colgroup> de arriba fija los anchos a nivel tabla, así esta
                                fila con colSpan no los desalinea (mismo criterio que la tabla de
                                Históricos, que ya lo necesitaba por su columna "Movimiento" con
                                colSpan=2). CADA celda de abajo lleva su `textAlign` INLINE (gana siempre,
                                sin depender de qué nth-child le toque): el colSpan corre el resto de las
                                celdas un puesto — antes MONTO caía en `td:nth-child(3)` (la regla de
                                alineación de FOLIO, mayor especificidad que la clase `.right`) y salía
                                pegado a la izquierda. Verificado visualmente por Ignacio en las dos vistas. */}
                            <td
                              className={styles.descripcion}
                              colSpan={2}
                              style={{ fontWeight: 700, textAlign: "left" }}
                            >
                              Saldo post sticky
                            </td>
                            <td className={styles.folio} style={{ textAlign: "left" }}>
                              {stickyResueltas.length} filas
                            </td>
                            <td
                              className={`${styles.right} ${sumaSticky < 0 ? styles.egreso : styles.ingreso}`}
                              style={{ textAlign: "right" }}
                            >
                              {fmt(Math.abs(sumaSticky))}
                            </td>
                            <td
                              className={`${styles.right} ${saldoTrasSticky >= 0 ? styles.ingreso : styles.egreso}`}
                              style={{ textAlign: "right" }}
                            >
                              {fmt(saldoTrasSticky)}
                            </td>
                            <td className={styles.accion} style={{ textAlign: "center" }} />
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {cajaChicaModal && (
        <AsignarCajaChicaModal
          isOpen={!!cajaChicaModal}
          factura={cajaChicaModal}
          onClose={() => setCajaChicaModal(null)}
          onSuccess={({ facturaId }) =>
            // Patch puntual (10.234, el mismo que antes disparaba el tab de PlanCuotasModal): la factura ya
            // tiene fila en caja_chica_gastos → se suma al Set y facturaSiiPorPagar la saca de "Por pagar"
            // sin refetch. Por ID (10.7).
            setCajaChicaFacturaIds((prev) => {
              const next = new Set(prev);
              next.add(facturaId);
              return next;
            })
          }
        />
      )}

      {planCuotasModal && (
        <PlanCuotasModal
          isOpen={!!planCuotasModal}
          onClose={() => setPlanCuotasModal(null)}
          tipo={planCuotasModal.tipo}
          factura={planCuotasModal.factura}
          accionInicial={planCuotasModal.accionInicial || "fecha"}
          onSuccess={(res) => {
            const pcm = planCuotasModal;
            const accion = res?.accion;
            // Fase 2A: "Ajustar fecha" se resuelve con patch puntual por ID de factura. "Cuotas" y
            // "Factoring" (insertan filas/ops con ids y derivados nuevos) siguen con refetch. (El tab
            // "Caja Chica" se retiró el 2026-09-25 → AsignarCajaChicaModal, desde el kebab.)
            if (accion === "fecha" && pcm?.factura) {
              const fid = pcm.factura.id;
              const nuevaFecha = res.nuevaFecha;
              if (pcm.tipo === "cobro") {
                setPendientesCobro((prev) =>
                  prev.map((f) => (f.id === fid ? { ...f, plazocobro: nuevaFecha, _plazoOverride: true } : f))
                );
              } else if (pcm.factura.es_externa) {
                setFacturasExternasPendientes((prev) =>
                  prev.map((f) => (f.id === fid ? { ...f, plazopago: nuevaFecha } : f))
                );
                // Fase 5a-bis: pasar una externa a pago único borra su plan (eliminarPlanPagosPorDocumento
                // en el modal) → sacar sus cuotas del estado local (por id de externa) para que vuelva a
                // su línea única.
                setCuotasPagosPendientes((prev) => prev.filter((c) => c.factura_externa_id !== fid));
              } else {
                setProveedoresPendientes((prev) =>
                  prev.map((f) => (f.id === fid ? { ...f, plazopago: nuevaFecha } : f))
                );
                // Pago SII "a pago único" borra el plan de cuotas en backend (eliminarPlanPagos) → sacar
                // esas cuotas del estado local para que la fila vuelva a su línea única (por ID de factura).
                setCuotasPagosPendientes((prev) => prev.filter((c) => c.factura_sii_recibida_id !== fid));
              }
            } else if (accion === "cuotas" && pcm?.factura) {
              // Fase 2C — refetch DIRIGIDO a las cuotas de ESTA factura (2 queries chicas), no el panel.
              // El guardado exige que las cuotas cuadren con el total → siempre queda ≥1 pendiente, así
              // que la factura pasa a proyectar por cuotas. Merge por id de factura (10.7). El saldo FIFO
              // (saldosPorFacturaMap) no cambia con el plan → no hace falta tocarlo.
              const fid = pcm.factura.id;
              if (pcm.tipo === "cobro") {
                getCuotasCobrosFuturas(pais, { facturaId: fid })
                  .then((cuotas) =>
                    setCuotasCobrosPendientes((prev) => [
                      ...prev.filter((c) => c.factura_sii_emitida_id !== fid),
                      ...(cuotas || []),
                    ])
                  )
                  .catch(() => cargarDatosFuturos());
              } else {
                // Fase 5a-bis/5b: externa → factura_externa_id ; boleta → factura_boleta_id ; SII
                // recibida → factura_sii_recibida_id. La boleta tiene id de entry string "boleta-<id>",
                // su id numérico real vive en boleta_id (lo que el loader y el match necesitan).
                const esExt = !!pcm.factura.es_externa;
                const esBol = !!pcm.factura.es_boleta;
                const fidDoc = esBol ? pcm.factura.boleta_id : fid;
                getCuotasPagosFuturas(pais, { facturaId: fidDoc, esExterna: esExt, esBoleta: esBol })
                  .then((cuotas) =>
                    setCuotasPagosPendientes((prev) => [
                      ...prev.filter((c) =>
                        esBol
                          ? c.factura_boleta_id !== fidDoc
                          : esExt
                            ? c.factura_externa_id !== fid
                            : c.factura_sii_recibida_id !== fid
                      ),
                      ...(cuotas || []),
                    ])
                  )
                  .catch(() => cargarDatosFuturos());
              }
            } else {
              // "factoring" / payload ausente (defensivo) → refetch completo, como hoy.
              // FACTORING NO ACOTADO A PROPÓSITO (Fase 2C): crear la op cambia operacionesFactoring (giro
              // + excedente) Y el estado de cobro de la emitida (sale de "Por cobrar"). Ese segundo efecto
              // se deriva en el bloque inline de emitidasConPlazos dentro de cargarDatosFuturos
              // (coberturaMBF / esFactoringLiquidada / plazos); acotarlo exige extraer ese bloque a un util
              // reutilizable primero (refactor aparte). Hasta entonces, refetch completo = correcto (10.191).
              cargarDatosFuturos();
            }
          }}
        />
      )}

      {asociarCapexModal && (
        <AsociarPagosCapexModal
          show
          facturaId={asociarCapexModal.facturaId}
          proveedorId={asociarCapexModal.proveedorId}
          pais={pais}
          onClose={() => setAsociarCapexModal(null)}
          onSuccess={() => {
            cargarDatosHistoricos();
            cargarDatosFuturos();
            cargarAsociacionesCapex(); // refresca el label del kebab (asociar↔desasociar) sin recargar
          }}
        />
      )}

      {vincularModal && (
        <VincularMovimientoModal
          show={!!vincularModal}
          onClose={() => setVincularModal(null)}
          movimiento={vincularModal}
          onSuccess={handleVincularSuccess}
          onRefreshFuturos={cargarDatosFuturos}
          comisionGen={comisionGen}
        />
      )}

      {pagoEdificioModal && (
        <PlanPagoEdificioModal
          isOpen={!!pagoEdificioModal}
          onClose={() => setPagoEdificioModal(null)}
          edificio={pagoEdificioModal}
          pais={pais}
          userId={userId}
          onSuccess={(res) => {
            // Patch puntual (Fase 2B): el modal queda ABIERTO para operaciones sucesivas; el padre solo
            // ajusta planPagosEdificios por ID según la operación. El residual del edificio lo recompone
            // el memo edificiosEntries (planPagosEdificios vs edificiosSaldo, 100% local) sin refetch.
            if (!res?.op) {
              cargarDatosFuturos(); // defensivo: sin payload → comportamiento viejo
              return;
            }
            if (res.op === "crear" && res.row) {
              setPlanPagosEdificios((prev) => [...prev, res.row]);
            } else if (res.op === "editar" && res.row) {
              setPlanPagosEdificios((prev) => prev.map((p) => (p.id === res.row.id ? { ...p, ...res.row } : p)));
            } else if (res.op === "anular") {
              // getPlanPagosEdificios trae solo estado='pendiente' → el anulado sale del array (10.7 por id).
              setPlanPagosEdificios((prev) => prev.filter((p) => p.id !== res.id));
            }
          }}
        />
      )}

      {entryEditarFecha && (
        <EditarFechaCobroModal
          show={!!entryEditarFecha}
          onHide={() => setEntryEditarFecha(null)}
          entry={entryEditarFecha}
          pais={pais}
          onSaved={(patch) => {
            const entryId = entryEditarFecha?.id;
            setEntryEditarFecha(null);
            if (!entryId || !patch) return;
            // Patch puntual del item de `pipeline` por su id compuesto (estable, no cambia con la fecha
            // de cobro). proyeccionesFuturas recalcula solo desde el array actualizado.
            setPipeline((prev) =>
              prev.map((p) => {
                if (p.id !== entryId) return p;
                const next = {
                  ...p,
                  fecha_estimada: patch.fecha_estimada,
                  fecha_override_actual: patch.fecha_override_actual,
                  tiene_override: patch.tiene_override,
                };
                // USD: importeCLP depende del TC de la fecha estimada (espejo exacto de getPipelineCobro).
                if (next.moneda_dolar) {
                  next.importeCLP = Math.round(
                    Number(next.importe || 0) * (getDolarValue(next.fecha_estimada, pais) || 1)
                  );
                }
                return next;
              })
            );
          }}
        />
      )}

      {entrySueldoFecha && (
        <EditarFechaSueldoModal
          show={!!entrySueldoFecha}
          onHide={() => setEntrySueldoFecha(null)}
          entry={entrySueldoFecha}
          pais={pais}
          onSaved={() => {
            setEntrySueldoFecha(null);
            // Refresca el override para que la fila de sueldo se mueva en el acto (rrhhEntries
            // depende de fechasPagoPorEmpMes). El propio EditarFechaSueldoModal ya mostró el toast de
            // éxito de la escritura — este catch es SOLO del refresh; si falla, avisar (10.177), no
            // tragarlo en silencio (misma fila queda desactualizada hasta recargar).
            getFechasPagoPais(pais)
              .then((f) => setFechasPagoRRHH(f || []))
              .catch(() => toast.error("Se guardó la fecha pero no se pudo refrescar la fila; recargá el panel"));
          }}
        />
      )}

      {entryRecurrenteFecha && (
        <EditarFechaRecurrenteModal
          show={!!entryRecurrenteFecha}
          onHide={() => setEntryRecurrenteFecha(null)}
          entry={entryRecurrenteFecha}
          pais={pais}
          userId={userId}
          onSaved={(patch) => {
            const e = entryRecurrenteFecha;
            setEntryRecurrenteFecha(null);
            if (!e || !patch) return;
            // Patch puntual por la clave compuesta de la ocurrencia recurrente (proveedor + período +
            // costo asociado). No hay id escalar → se matchea por esos campos (regla 10.7, no por posición).
            const disc = e.costo_asociado_id ?? "main";
            setProveedoresRecurrentes((prev) =>
              prev.map((r) => {
                const rDisc = r.costo_asociado_id ?? "main";
                if (
                  r.proveedor_id !== e.proveedor_id ||
                  r.periodo_año !== e.periodo_año ||
                  r.periodo_mes !== e.periodo_mes ||
                  rDisc !== disc
                ) {
                  return r;
                }
                return { ...r, fecha_display: patch.nuevaFecha, tiene_override: patch.tieneOverride };
              })
            );
          }}
        />
      )}

      {capexPagadoModal && (
        <ConfirmarCapexPagadoModal
          show={!!capexPagadoModal}
          onHide={() => setCapexPagadoModal(null)}
          entry={capexPagadoModal}
          onConfirm={handleMarcarCapexPagado}
        />
      )}

      {capexFechaModal && (
        <ModificarFechaProyeccionCapexModal
          show={!!capexFechaModal}
          onHide={() => setCapexFechaModal(null)}
          entry={capexFechaModal}
          pais={pais}
          onSaved={(nuevaFecha) => {
            const activoId = capexFechaModal?.activo_id;
            setCapexFechaModal(null);
            if (!activoId || !nuevaFecha) return;
            // Patch puntual de `capexEntries` por activos.id (estable). Solo cambia fecha_planificada.
            setCapexEntries((prev) =>
              prev.map((a) => (a.id === activoId ? { ...a, fecha_planificada: nuevaFecha } : a))
            );
          }}
        />
      )}

      {compromisoFechaModal && (
        <ModificarFechaProyeccionCompromisoModal
          show={!!compromisoFechaModal}
          onHide={() => setCompromisoFechaModal(null)}
          entry={compromisoFechaModal}
          pais={pais}
          onSaved={(cambios) => {
            const compromisoId = compromisoFechaModal?.compromiso_id;
            setCompromisoFechaModal(null);
            if (!compromisoId || !cambios?.fecha) return;
            // Patch puntual de `compromisosEntries` por compromisos_pago.id: fecha_estimada + monto
            // (mapCompromiso recomputa importeEnMoneda desde el monto crudo en el re-render).
            setCompromisosEntries((prev) =>
              prev.map((c) =>
                c.id === compromisoId ? { ...c, fecha_estimada: cambios.fecha, monto: cambios.monto } : c
              )
            );
          }}
        />
      )}

      <CompromisoPagoModal
        show={mostrarCompromisoAltaModal}
        onHide={() => setMostrarCompromisoAltaModal(false)}
        pais={pais}
        userId={userId}
        permitirElegirProveedor
        onSaved={(res) => {
          setMostrarCompromisoAltaModal(false);
          const creado = res?.creado;
          if (!creado?.id) {
            cargarDatosFuturos(); // defensivo (sin payload / edición) → comportamiento viejo
            return;
          }
          // Solo proyecta si nace planificado + sin factura (mismo gate que el loader). Si no, no se muestra.
          if (creado.estado !== "planificado" || creado.factura_sii_recibida_id != null) return;
          // Embed de proveedor que consume la proyección (mapCompromiso/mapCuotaCompromiso usan
          // proveedor?.nombre); crearCompromiso no lo trae, lo reconstruimos con el nombre del modal.
          const conEmbed = {
            ...creado,
            proveedor: res.proveedorNombre ? { id: creado.proveedor_id, nombre: res.proveedorNombre } : null,
          };
          if (res.conPlan) {
            // Alta CON plan → refetch DIRIGIDO de las cuotas de ESTE compromiso (2 queries chicas), no
            // el panel completo. El compromiso entra como driver + al Set de "con plan" → proyecta cuotas.
            getCuotasCompromisoFuturas(pais, { compromisoId: creado.id })
              .then((cuotas) => {
                setCompromisosEntries((prev) => [...prev, conEmbed]);
                setCompromisosConPlan((prev) => new Set(prev).add(creado.id));
                setCuotasCompromisoPendientes((prev) => [
                  ...prev.filter((c) => c.compromiso_id !== creado.id),
                  ...(cuotas || []),
                ]);
              })
              .catch(() => cargarDatosFuturos());
          } else {
            // Alta SIN plan → línea única: agregar el compromiso a compromisosEntries.
            setCompromisosEntries((prev) => [...prev, conEmbed]);
          }
        }}
      />

      {loteFechaModal && (
        <ModificarFechaLoteCapexModal
          show={!!loteFechaModal}
          onHide={() => setLoteFechaModal(null)}
          entry={loteFechaModal}
          onSaved={(nuevaFecha) => {
            const e = loteFechaModal;
            setLoteFechaModal(null);
            if (!e || !nuevaFecha) return;
            // Patch puntual: componente → `componentesLotes` por lote_costos.id (fecha_pago); FOB →
            // `capexLotes` por lotes_compra.id (fecha_planificada). El entry ya discrimina por costo_id.
            if (e.costo_id) {
              setComponentesLotes((prev) =>
                prev.map((c) => (c.id === e.costo_id ? { ...c, fecha_pago: nuevaFecha } : c))
              );
            } else {
              setCapexLotes((prev) =>
                prev.map((l) => (l.id === e.lote_id ? { ...l, fecha_planificada: nuevaFecha } : l))
              );
            }
          }}
        />
      )}

      {capexPlanModal && (
        <PlanPagosActivoModal
          show={!!capexPlanModal}
          onHide={() => setCapexPlanModal(null)}
          entry={capexPlanModal}
          userId={userId}
          onSaved={() => {
            const activoId = capexPlanModal?.activo_id;
            setCapexPlanModal(null);
            if (activoId == null) {
              cargarDatosFuturos();
              return;
            }
            // Refetch DIRIGIDO (Fase 2C): SOLO las cuotas de este activo (2 queries chicas) en vez del
            // panel completo. 2c Fase 2: el modal ahora guarda tres modos (plan / fecha única / cero),
            // y fecha única/cero BORRAN las cuotas. Por eso hay que ramificar según lo que volvió:
            //  · con cuotas → sigue proyectando por cuotas: merge por activo_id (10.7) + activosConPlan.add.
            //  · SIN cuotas (fecha única o cero) → recargar completo: refresca capexEntries.fecha_planificada
            //    (la línea única) y saca el activo de activosConPlan en sync con la DB (si no, quedaría
            //    marcado con plan, se buscaría por el camino de cuotas vacío y desaparecería de Futuros).
            getCuotasActivosFuturas(pais, { activoId })
              .then((cuotas) => {
                if ((cuotas || []).length === 0) {
                  cargarDatosFuturos();
                  return;
                }
                // `prev` puede ser `null` (Fase 3b/M1: una carga previa del libro/cuotas falló) — guard
                // defensivo, mismo criterio que el resto de esta fase (nunca asumir array).
                setCuotasActivosPendientes((prev) => [
                  ...(prev || []).filter((c) => c.activo_id !== activoId),
                  ...cuotas,
                ]);
                setActivosConPlan((prev) => new Set(prev).add(activoId));
              })
              .catch(() => cargarDatosFuturos());
          }}
        />
      )}

      {overrideF29Modal && (
        <OverrideF29Modal
          show={!!overrideF29Modal}
          onHide={() => setOverrideF29Modal(null)}
          entry={overrideF29Modal.entry}
          campo={overrideF29Modal.campo}
          periodo={overrideF29Modal.periodo}
          onSave={handleGuardarOverrideF29}
        />
      )}
    </div>
  );
};

export default FlujoCajaV2;
